import type { AircraftType } from "./constants"

export interface AircraftTypeInfo {
    manufacturer: string
    model: string
    variant: string
    category: AircraftType
}

// Derived from ICAO Doc 8643 (Aircraft Type Designators). Source dataset (community-maintained
// mirror of the FAA/ICAO designator orders, not the paid ICAO Store dataset):
// https://github.com/ColtJD45/icao-aircraft-designator-list
//
// `category` collapses Doc 8643's own category field to our five buckets as: LandPlane/SeaPlane/
// Amphibian -> fixedWing, Gyrocopter/Helicopter -> rotorcraft, Tiltrotor -> other (neither
// fixed-wing nor conventional rotorcraft). A handful of designators (e.g. AS32) list an
// inconsistent manufacturer/model/category across entries in the source data (shared designators,
// relabeled/licensed manufacturers, or data-entry noise) -- resolved by majority vote.
//
// The special generic designators below aren't tied to a manufacturer/model in Doc 8643 (they
// cover a whole aircraft class, not one model), so they're absent from the manufacturer-keyed
// dataset and added by hand from ICAO's published "Special Designators" list: BALL (Balloon),
// SHIP (Airship), GLID (Glider/sailplane), ULAC (Ultra Light Aircraft), UHEL (Ultralight
// Helicopter), GYRO (Gyrocopter, generic), PARA (Paraglider/paramotor).
//
// A curated subset of common designators below has hand-verified manufacturer/model/variant
// (e.g. decomposing "737-800" into model "737" + variant "800") and takes precedence over the
// bulk-derived data for those entries.
export const AIRCRAFT_TYPE_DESIGNATORS: Record<string, AircraftTypeInfo> = {
    A002: { manufacturer: "Irkut", model: "A-002", variant: "", category: "rotorcraft" },
    A1: { manufacturer: "Douglas", model: "AD Skyraider", variant: "", category: "fixedWing" },
    A10: {
        manufacturer: "Fairchild (1)",
        model: "A-10 Thunderbolt 2",
        variant: "",
        category: "fixedWing",
    },
    A109: { manufacturer: "Agusta", model: "A-109", variant: "", category: "rotorcraft" },
    A119: { manufacturer: "Agusta", model: "A-119 Koala", variant: "", category: "rotorcraft" },
    A122: {
        manufacturer: "Aerotec (1)",
        model: "A-122 Uirapuru",
        variant: "",
        category: "fixedWing",
    },
    A124: { manufacturer: "Antonov", model: "An-124 Ruslan", variant: "", category: "fixedWing" },
    A129: { manufacturer: "Agusta", model: "A-129 Mangusta", variant: "", category: "rotorcraft" },
    A139: { manufacturer: "Agusta", model: "AW-139", variant: "", category: "rotorcraft" },
    A140: { manufacturer: "Antonov", model: "An-140", variant: "", category: "fixedWing" },
    A148: { manufacturer: "Antonov", model: "An-148", variant: "", category: "fixedWing" },
    A149: { manufacturer: "Agusta", model: "AW-149", variant: "", category: "rotorcraft" },
    A158: { manufacturer: "Antonov", model: "An-158", variant: "", category: "fixedWing" },
    A169: { manufacturer: "Agustawestland", model: "AW-169", variant: "", category: "rotorcraft" },
    A178: { manufacturer: "Antonov", model: "An-178", variant: "", category: "fixedWing" },
    A189: { manufacturer: "Agustawestland", model: "AW-189", variant: "", category: "rotorcraft" },
    A19: { manufacturer: "Aeropract", model: "A-19", variant: "", category: "fixedWing" },
    A19N: { manufacturer: "Airbus", model: "A-319neo", variant: "", category: "fixedWing" },
    A20: { manufacturer: "Douglas", model: "A-20 Havoc", variant: "", category: "fixedWing" },
    A205: { manufacturer: "Oskbes-Mai", model: "MAI-205", variant: "", category: "rotorcraft" },
    A20J: { manufacturer: "Schleicher", model: "ASW-20J", variant: "", category: "fixedWing" },
    A20N: { manufacturer: "Airbus", model: "A320", variant: "neo", category: "fixedWing" },
    A21: { manufacturer: "Aeropract", model: "A-21 Solo", variant: "", category: "fixedWing" },
    A210: { manufacturer: "Aquila", model: "A-210", variant: "", category: "fixedWing" },
    A211: { manufacturer: "Alfa-M", model: "A-211", variant: "", category: "fixedWing" },
    A21N: { manufacturer: "Airbus", model: "A321", variant: "neo", category: "fixedWing" },
    A22: { manufacturer: "Sadler", model: "A-22 Piranha", variant: "", category: "fixedWing" },
    A223: {
        manufacturer: "Oskbes-Mai",
        model: "MAI-223 Kityonok",
        variant: "",
        category: "fixedWing",
    },
    A23: { manufacturer: "Aeropract", model: "A-23 Dragon", variant: "", category: "fixedWing" },
    A25: { manufacturer: "Aeropract", model: "A-25 Breeze", variant: "", category: "fixedWing" },
    A251: {
        manufacturer: "Aviatik-Alyans",
        model: "Aleks-251",
        variant: "",
        category: "fixedWing",
    },
    A27: { manufacturer: "Aeropract", model: "A-27", variant: "", category: "fixedWing" },
    A270: { manufacturer: "Aero (2)", model: "Ae-270 Propjet", variant: "", category: "fixedWing" },
    A29: { manufacturer: "Avantage", model: "A-29", variant: "", category: "fixedWing" },
    A2RT: { manufacturer: "Kazan", model: "Ansat 2RT", variant: "", category: "rotorcraft" },
    A3: { manufacturer: "Douglas", model: "A-3 Skywarrior", variant: "", category: "fixedWing" },
    A306: { manufacturer: "Airbus", model: "A-300B4-600", variant: "", category: "fixedWing" },
    A30B: { manufacturer: "Airbus", model: "A-300B2-200", variant: "", category: "fixedWing" },
    A31: { manufacturer: "Avantage", model: "A-31 Spectrum", variant: "", category: "fixedWing" },
    A310: { manufacturer: "Airbus", model: "T-22", variant: "", category: "fixedWing" },
    A318: { manufacturer: "Airbus", model: "A318", variant: "", category: "fixedWing" },
    A319: { manufacturer: "Airbus", model: "A319", variant: "", category: "fixedWing" },
    A320: { manufacturer: "Airbus", model: "A320", variant: "", category: "fixedWing" },
    A321: { manufacturer: "Airbus", model: "A321", variant: "", category: "fixedWing" },
    A32E: { manufacturer: "Schleicher", model: "ASG-32EL", variant: "", category: "fixedWing" },
    A32P: { manufacturer: "Schleicher", model: "ASG-32Mi", variant: "", category: "fixedWing" },
    A33: { manufacturer: "Aeropract", model: "A-33", variant: "", category: "fixedWing" },
    A332: { manufacturer: "Airbus", model: "A330", variant: "200", category: "fixedWing" },
    A333: { manufacturer: "Airbus", model: "A330", variant: "300", category: "fixedWing" },
    A337: {
        manufacturer: "Airbus",
        model: "A-330-700 Beluga XL",
        variant: "",
        category: "fixedWing",
    },
    A338: { manufacturer: "Airbus", model: "A-330-800", variant: "", category: "fixedWing" },
    A339: { manufacturer: "Airbus", model: "A-330-900", variant: "", category: "fixedWing" },
    A33E: { manufacturer: "Schleicher", model: "AS-33Me", variant: "", category: "fixedWing" },
    A33P: { manufacturer: "Schleicher", model: "AS-33Es", variant: "", category: "fixedWing" },
    A342: {
        manufacturer: "Airbus",
        model: "A-340-200 Prestige",
        variant: "",
        category: "fixedWing",
    },
    A343: {
        manufacturer: "Airbus",
        model: "A-340-300 Prestige",
        variant: "",
        category: "fixedWing",
    },
    A345: {
        manufacturer: "Airbus",
        model: "A-340-500 Prestige",
        variant: "",
        category: "fixedWing",
    },
    A346: {
        manufacturer: "Airbus",
        model: "A-340-600 Prestige",
        variant: "",
        category: "fixedWing",
    },
    A34E: { manufacturer: "Schleicher", model: "AS-34Me", variant: "", category: "fixedWing" },
    A35: { manufacturer: "Avantage", model: "A-35 Scanner", variant: "", category: "fixedWing" },
    A359: { manufacturer: "Airbus", model: "A350", variant: "900", category: "fixedWing" },
    A35K: { manufacturer: "Airbus", model: "A-350-1000 XWB", variant: "", category: "fixedWing" },
    A37: { manufacturer: "Cessna", model: "318D Dragonfly", variant: "", category: "fixedWing" },
    A388: { manufacturer: "Airbus", model: "A380", variant: "800", category: "fixedWing" },
    A3ST: { manufacturer: "Airbus", model: "A-300ST Beluga", variant: "", category: "fixedWing" },
    A4: { manufacturer: "Douglas", model: "A-4 Skyhawk", variant: "", category: "fixedWing" },
    A400: { manufacturer: "Airbus", model: "A-400M Atlas", variant: "", category: "fixedWing" },
    A411: { manufacturer: "Oskbes-Mai", model: "MAI-411", variant: "", category: "fixedWing" },
    A5: { manufacturer: "Icon", model: "A-5", variant: "", category: "fixedWing" },
    A50: { manufacturer: "Beriev", model: "A-50", variant: "", category: "fixedWing" },
    A500: { manufacturer: "Adam (2)", model: "A-500", variant: "", category: "fixedWing" },
    A504: { manufacturer: "Avro", model: "504", variant: "", category: "fixedWing" },
    A6: { manufacturer: "Grumman", model: "A-6 Intruder", variant: "", category: "fixedWing" },
    A600: { manufacturer: "Rotorway", model: "A-600 Talon", variant: "", category: "rotorcraft" },
    A660: {
        manufacturer: "Ayres",
        model: "S-2R-T660 Turbo Thrush",
        variant: "",
        category: "fixedWing",
    },
    A700: { manufacturer: "Adam (2)", model: "A-700 AdamJet", variant: "", category: "fixedWing" },
    A743: { manufacturer: "Antonov", model: "An-74-300", variant: "", category: "fixedWing" },
    A748: { manufacturer: "Ail", model: "748", variant: "", category: "fixedWing" },
    A890: { manufacturer: "Aviatika", model: "MAI-890", variant: "", category: "fixedWing" },
    A9: { manufacturer: "Aamsa", model: "A-9 Quail", variant: "", category: "fixedWing" },
    A900: {
        manufacturer: "Aviatika",
        model: "MAI-900 Acrobat",
        variant: "",
        category: "fixedWing",
    },
    A910: { manufacturer: "Aviatika", model: "MAI-910", variant: "", category: "fixedWing" },
    AA1: { manufacturer: "American", model: "AA-1 Tr2", variant: "", category: "fixedWing" },
    AA37: { manufacturer: "Aeropract", model: "A-37", variant: "", category: "fixedWing" },
    AA5: { manufacturer: "American", model: "AA-5 Traveler", variant: "", category: "fixedWing" },
    AAT3: { manufacturer: "Aero (3)", model: "AT-3", variant: "", category: "fixedWing" },
    AAT4: { manufacturer: "Aero (3)", model: "AT-4", variant: "", category: "fixedWing" },
    AB11: { manufacturer: "Aero Boero", model: "AB-115", variant: "", category: "fixedWing" },
    AB15: { manufacturer: "Aero Boero", model: "AB-150", variant: "", category: "fixedWing" },
    AB18: { manufacturer: "Aero Boero", model: "AB-180", variant: "", category: "fixedWing" },
    AB95: { manufacturer: "Aero Boero", model: "AB-95", variant: "", category: "fixedWing" },
    AC10: {
        manufacturer: "Fd-Composites",
        model: "ArrowCopter",
        variant: "",
        category: "rotorcraft",
    },
    AC11: { manufacturer: "Commander", model: "Commander 114", variant: "", category: "fixedWing" },
    AC31: { manufacturer: "Avicopter", model: "AC-311", variant: "", category: "rotorcraft" },
    AC33: { manufacturer: "Avicopter", model: "AC-313", variant: "", category: "rotorcraft" },
    AC4: { manufacturer: "Light Wing", model: "AC-4", variant: "", category: "fixedWing" },
    AC50: { manufacturer: "Aero (1)", model: "Commander 500", variant: "", category: "fixedWing" },
    AC52: { manufacturer: "Aero (1)", model: "Commander 520", variant: "", category: "fixedWing" },
    AC56: { manufacturer: "Aero (1)", model: "Commander 560", variant: "", category: "fixedWing" },
    AC5A: { manufacturer: "Nanjing", model: "AC-500 Aircar", variant: "", category: "fixedWing" },
    AC5M: { manufacturer: "Aviastroitel", model: "AC-5M", variant: "", category: "fixedWing" },
    AC68: {
        manufacturer: "Aero (1)",
        model: "Commander 680 Super",
        variant: "",
        category: "fixedWing",
    },
    AC6L: {
        manufacturer: "Aero Commander",
        model: "680FL Grand Commander",
        variant: "",
        category: "fixedWing",
    },
    AC72: {
        manufacturer: "Aero (1)",
        model: "Commander 720 Alti Cruiser",
        variant: "",
        category: "fixedWing",
    },
    AC80: {
        manufacturer: "Aero Commander",
        model: "680T Turbo Commander",
        variant: "",
        category: "fixedWing",
    },
    AC90: {
        manufacturer: "Gulfstream Aerospace",
        model: "690 Jetprop Commander 840",
        variant: "",
        category: "fixedWing",
    },
    AC95: {
        manufacturer: "Gulfstream Aerospace",
        model: "695 Jetprop Commander 980",
        variant: "",
        category: "fixedWing",
    },
    ACAM: { manufacturer: "Leza", model: "Air Cam", variant: "", category: "fixedWing" },
    ACAR: { manufacturer: "Auster", model: "J-5B Autocar", variant: "", category: "fixedWing" },
    ACED: { manufacturer: "Powell", model: "P-70 Acey Deucy", variant: "", category: "fixedWing" },
    ACR2: { manufacturer: "Acro Sport", model: "Acro-Sport 2", variant: "", category: "fixedWing" },
    ACRD: { manufacturer: "Avia (3)", model: "Accord", variant: "", category: "fixedWing" },
    ACRO: { manufacturer: "Acro Sport", model: "Acro-Sport 1", variant: "", category: "fixedWing" },
    ACSR: { manufacturer: "Victa", model: "Aircruiser", variant: "", category: "fixedWing" },
    AD20: { manufacturer: "Beijing Keyuan", model: "AD-200", variant: "", category: "fixedWing" },
    ADEL: { manufacturer: "Ustinov", model: "Adel", variant: "", category: "rotorcraft" },
    ADVE: { manufacturer: "Auster", model: "J-5 Adventurer", variant: "", category: "fixedWing" },
    ADVN: {
        manufacturer: "Adventure Air",
        model: "Adventurer",
        variant: "",
        category: "fixedWing",
    },
    AE45: { manufacturer: "Aero (2)", model: "45", variant: "", category: "fixedWing" },
    AEA1: { manufacturer: "American Eagle", model: "A-1", variant: "", category: "fixedWing" },
    AERK: { manufacturer: "Aeronca", model: "K Scout", variant: "", category: "fixedWing" },
    AEST: { manufacturer: "Aerostar (1)", model: "600", variant: "", category: "fixedWing" },
    AFOX: { manufacturer: "Halley", model: "Apollo Fox", variant: "", category: "fixedWing" },
    AG02: { manufacturer: "Gatard", model: "AG-02 Poussin", variant: "", category: "fixedWing" },
    AG04: { manufacturer: "Gatard", model: "AG-04 Pigeon", variant: "", category: "fixedWing" },
    AG1: { manufacturer: "Halley", model: "Apollo AG-1 Gyro", variant: "", category: "rotorcraft" },
    AG10: { manufacturer: "Caiga", model: "AG-100", variant: "", category: "fixedWing" },
    AG14: { manufacturer: "Anderson-Greenwood", model: "14", variant: "", category: "fixedWing" },
    AG60: { manufacturer: "Caiga", model: "AG-600 Kunlong", variant: "", category: "fixedWing" },
    AGSH: { manufacturer: "Aero Gare", model: "Sea Hawker", variant: "", category: "fixedWing" },
    AI10: { manufacturer: "Ikar", model: "Ai-10 Ikar", variant: "", category: "fixedWing" },
    AIGT: {
        manufacturer: "Auster",
        model: "J-5F Aiglet Trainer",
        variant: "",
        category: "fixedWing",
    },
    AIRD: { manufacturer: "Beagle", model: "A-109 Airedale", variant: "", category: "fixedWing" },
    AIRL: { manufacturer: "Aeronix", model: "Airelle", variant: "", category: "fixedWing" },
    AJ27: { manufacturer: "Comac", model: "CBJ-909", variant: "", category: "fixedWing" },
    AJET: { manufacturer: "Aoi", model: "Alpha Jet", variant: "", category: "fixedWing" },
    AKNC: { manufacturer: "Baykar", model: "Bayraktar Akıncı", variant: "", category: "fixedWing" },
    AKOY: { manufacturer: "Lisa", model: "Akoya", variant: "", category: "fixedWing" },
    AKRO: { manufacturer: "Stephens", model: "Akro", variant: "", category: "fixedWing" },
    ALBU: {
        manufacturer: "Aviation Development",
        model: "Alaskan Bushmaster",
        variant: "",
        category: "fixedWing",
    },
    ALC1: { manufacturer: "Altair Coelho", model: "AC-11", variant: "", category: "fixedWing" },
    ALGR: { manufacturer: "Fantasy Air", model: "Allegro", variant: "", category: "fixedWing" },
    ALH: { manufacturer: "Hindustan", model: "ALH Dhruv", variant: "", category: "rotorcraft" },
    ALIG: { manufacturer: "Arion", model: "LS-1 Lightning", variant: "", category: "fixedWing" },
    ALIZ: { manufacturer: "Breguet", model: "1050 Alizé", variant: "", category: "fixedWing" },
    ALO2: {
        manufacturer: "Aerospatiale",
        model: "SA-318 Alouette 2",
        variant: "",
        category: "rotorcraft",
    },
    ALO3: {
        manufacturer: "Aerospatiale",
        model: "SA-316 Alouette 3",
        variant: "",
        category: "rotorcraft",
    },
    ALPI: { manufacturer: "Auster", model: "J-5Q Alpine", variant: "", category: "fixedWing" },
    ALSL: { manufacturer: "Airlony", model: "Skylane", variant: "", category: "fixedWing" },
    ALTO: { manufacturer: "Direct Fly", model: "Alto", variant: "", category: "fixedWing" },
    AM3: { manufacturer: "Aerfer-Aermacchi", model: "AM-3", variant: "", category: "fixedWing" },
    AMX: { manufacturer: "Aeritalia", model: "AMX Ghibli", variant: "", category: "fixedWing" },
    AN12: { manufacturer: "Antonov", model: "An-12", variant: "", category: "fixedWing" },
    AN2: { manufacturer: "Aicsa", model: "An-2", variant: "", category: "fixedWing" },
    AN22: { manufacturer: "Antonov", model: "An-22 Antheus", variant: "", category: "fixedWing" },
    AN24: { manufacturer: "Antonov", model: "An-24", variant: "", category: "fixedWing" },
    AN26: { manufacturer: "Antonov", model: "An-26", variant: "", category: "fixedWing" },
    AN28: { manufacturer: "Antonov", model: "An-28", variant: "", category: "fixedWing" },
    AN3: { manufacturer: "Antonov", model: "An-3", variant: "", category: "fixedWing" },
    AN30: { manufacturer: "Antonov", model: "An-30", variant: "", category: "fixedWing" },
    AN32: { manufacturer: "Antonov", model: "An-32", variant: "", category: "fixedWing" },
    AN38: { manufacturer: "Antonov", model: "An-38", variant: "", category: "fixedWing" },
    AN70: { manufacturer: "Antonov", model: "An-70", variant: "", category: "fixedWing" },
    AN72: { manufacturer: "Antonov", model: "An-72", variant: "", category: "fixedWing" },
    AN8: { manufacturer: "Antonov", model: "An-8", variant: "", category: "fixedWing" },
    ANDR: { manufacturer: "Millioud", model: "Andromède", variant: "", category: "fixedWing" },
    ANGL: { manufacturer: "Angel", model: "44 Angel", variant: "", category: "fixedWing" },
    ANKA: { manufacturer: "Tai", model: "Anka", variant: "", category: "fixedWing" },
    ANSN: { manufacturer: "Avro", model: "652 Anson", variant: "", category: "fixedWing" },
    ANST: { manufacturer: "Kazan", model: "Ansat", variant: "", category: "rotorcraft" },
    AP20: { manufacturer: "Aeroprakt", model: "A-20", variant: "", category: "fixedWing" },
    AP22: { manufacturer: "Aeroprakt", model: "A-22", variant: "", category: "fixedWing" },
    AP24: { manufacturer: "Aeroprakt", model: "A-24 Viking", variant: "", category: "fixedWing" },
    AP26: {
        manufacturer: "Aeroprakt",
        model: "A-26 Twin Vista",
        variant: "",
        category: "fixedWing",
    },
    AP28: { manufacturer: "Aeroprakt", model: "A-28 Victor", variant: "", category: "fixedWing" },
    AP32: { manufacturer: "Aeroprakt", model: "A-32 Vixxen", variant: "", category: "fixedWing" },
    AP36: { manufacturer: "Aeroprakt", model: "A-36 Vulcan", variant: "", category: "fixedWing" },
    APM2: { manufacturer: "Issoire", model: "APM-20 Lionceau", variant: "", category: "fixedWing" },
    APM3: { manufacturer: "Issoire", model: "APM-30 Lion", variant: "", category: "fixedWing" },
    APM4: { manufacturer: "Issoire", model: "APM-41 Simba", variant: "", category: "fixedWing" },
    APUP: { manufacturer: "Aeropup", model: "Aeropup", variant: "", category: "fixedWing" },
    AR1: {
        manufacturer: "Silverlight",
        model: "AR-1 American Ranger 1",
        variant: "",
        category: "rotorcraft",
    },
    AR11: { manufacturer: "Aeronca", model: "11 Chief", variant: "", category: "fixedWing" },
    AR15: { manufacturer: "Aeronca", model: "15 Sedan", variant: "", category: "fixedWing" },
    AR50: { manufacturer: "Aeronca", model: "50 Chief", variant: "", category: "fixedWing" },
    AR5T: { manufacturer: "Aeronca", model: "50 Tandem", variant: "", category: "fixedWing" },
    AR65: { manufacturer: "Aeronca", model: "65 Super Chief", variant: "", category: "fixedWing" },
    AR6T: { manufacturer: "Aeronca", model: "60 Tandem", variant: "", category: "fixedWing" },
    ARCE: { manufacturer: "Schempp-Hirth", model: "Arcus E", variant: "", category: "fixedWing" },
    ARCP: { manufacturer: "Schempp-Hirth", model: "Arcus T", variant: "", category: "fixedWing" },
    ARES: { manufacturer: "Rutan", model: "151 Ares", variant: "", category: "fixedWing" },
    ARV1: { manufacturer: "Arv", model: "ARV-1 Super 2", variant: "", category: "fixedWing" },
    ARVA: { manufacturer: "Iai", model: "101 Arava", variant: "", category: "fixedWing" },
    ARWF: { manufacturer: "Arrow (1)", model: "F Sport", variant: "", category: "fixedWing" },
    AS02: { manufacturer: "Ffa", model: "AS-202-15 Bravo", variant: "", category: "fixedWing" },
    AS14: { manufacturer: "Schleicher", model: "ASK-14", variant: "", category: "fixedWing" },
    AS16: { manufacturer: "Schleicher", model: "ASK-16", variant: "", category: "fixedWing" },
    AS20: { manufacturer: "Schleicher", model: "ASW-20TOP", variant: "", category: "fixedWing" },
    AS21: { manufacturer: "Schleicher", model: "ASK-21Mi", variant: "", category: "fixedWing" },
    AS22: { manufacturer: "Schleicher", model: "ASW-22BE", variant: "", category: "fixedWing" },
    AS24: { manufacturer: "Schleicher", model: "ASW-24E", variant: "", category: "fixedWing" },
    AS25: { manufacturer: "Binder (2)", model: "ASH-25EB", variant: "", category: "fixedWing" },
    AS26: { manufacturer: "Schleicher", model: "ASH-26E", variant: "", category: "fixedWing" },
    AS28: { manufacturer: "Schleicher", model: "ASW-28E", variant: "", category: "fixedWing" },
    AS29: { manufacturer: "Schleicher", model: "ASG-29Es", variant: "", category: "fixedWing" },
    AS2T: {
        manufacturer: "Ffa",
        model: "AS-202-32TP Turbine Bravo",
        variant: "",
        category: "fixedWing",
    },
    AS30: { manufacturer: "Schleicher", model: "ASH-30Mi", variant: "", category: "fixedWing" },
    AS31: { manufacturer: "Schleicher", model: "ASH-31Mi", variant: "", category: "fixedWing" },
    AS32: { manufacturer: "Aerospatiale", model: "HD-21", variant: "", category: "rotorcraft" },
    AS3B: {
        manufacturer: "Aerospatiale",
        model: "AS-332L2 Super Puma Mk2",
        variant: "",
        category: "rotorcraft",
    },
    AS50: {
        manufacturer: "Airbus Helicopters",
        model: "AS350",
        variant: "",
        category: "rotorcraft",
    },
    AS55: {
        manufacturer: "Aerospatiale",
        model: "AS-355 Ecureuil 2",
        variant: "",
        category: "rotorcraft",
    },
    AS65: {
        manufacturer: "Aerospatiale",
        model: "AS-365 Dauphin 2",
        variant: "",
        category: "rotorcraft",
    },
    AS80: { manufacturer: "Starck", model: "AS-80 Holiday", variant: "", category: "fixedWing" },
    ASDR: { manufacturer: "Wild", model: "Ascender", variant: "", category: "fixedWing" },
    ASO4: {
        manufacturer: "Asso Aerei",
        model: "Asso 4 Whisky",
        variant: "",
        category: "fixedWing",
    },
    ASO5: {
        manufacturer: "Asso Aerei",
        model: "Asso 5 Champion",
        variant: "",
        category: "fixedWing",
    },
    ASOX: {
        manufacturer: "Asso Aerei",
        model: "Asso 10 Jewel",
        variant: "",
        category: "fixedWing",
    },
    ASTO: { manufacturer: "Tecnam", model: "Astore", variant: "", category: "fixedWing" },
    ASTR: {
        manufacturer: "Gulfstream Aerospace",
        model: "Gulfstream G100",
        variant: "",
        category: "fixedWing",
    },
    AT1: { manufacturer: "Antoniewski", model: "AT-1", variant: "", category: "fixedWing" },
    AT2P: { manufacturer: "Air Tractor", model: "AT-250", variant: "", category: "fixedWing" },
    AT3: { manufacturer: "Aidc", model: "AT-3 Tzu-Chung", variant: "", category: "fixedWing" },
    AT3P: { manufacturer: "Air Tractor", model: "AT-300", variant: "", category: "fixedWing" },
    AT3T: { manufacturer: "Air Tractor", model: "AT-302", variant: "", category: "fixedWing" },
    AT43: { manufacturer: "Atr", model: "ATR-42-300", variant: "", category: "fixedWing" },
    AT44: { manufacturer: "Atr", model: "ATR-42-400", variant: "", category: "fixedWing" },
    AT45: { manufacturer: "Atr", model: "ATR-42-500", variant: "", category: "fixedWing" },
    AT46: { manufacturer: "Atr", model: "ATR-42-600", variant: "", category: "fixedWing" },
    AT5P: { manufacturer: "Air Tractor", model: "AT-501", variant: "", category: "fixedWing" },
    AT5T: { manufacturer: "Air Tractor", model: "AT-502", variant: "", category: "fixedWing" },
    AT6T: { manufacturer: "Air Tractor", model: "AT-602", variant: "", category: "fixedWing" },
    AT72: { manufacturer: "Atr", model: "ATR-72-201", variant: "", category: "fixedWing" },
    AT73: { manufacturer: "Atr", model: "ATR-72-211", variant: "", category: "fixedWing" },
    AT75: { manufacturer: "Atr", model: "ATR-72-500", variant: "", category: "fixedWing" },
    AT76: { manufacturer: "Atr", model: "ATR-72-600", variant: "", category: "fixedWing" },
    AT8T: { manufacturer: "Air Tractor", model: "AT-802", variant: "", category: "fixedWing" },
    ATAC: {
        manufacturer: "Aircraft Technologies",
        model: "Acro 1",
        variant: "",
        category: "fixedWing",
    },
    ATIS: {
        manufacturer: "Aircraft Technologies",
        model: "Atlantis",
        variant: "",
        category: "fixedWing",
    },
    ATL: { manufacturer: "Robin", model: "ATL", variant: "", category: "fixedWing" },
    ATLA: { manufacturer: "Breguet", model: "1150 Atlantic", variant: "", category: "fixedWing" },
    ATP: { manufacturer: "British Aerospace", model: "ATP", variant: "", category: "fixedWing" },
    ATTL: { manufacturer: "Euronef", model: "ATTL-1", variant: "", category: "fixedWing" },
    AU11: {
        manufacturer: "Beagle-Auster",
        model: "E-3 Auster AOP11",
        variant: "",
        category: "fixedWing",
    },
    AUJ2: { manufacturer: "Auster", model: "J-2 Arrow", variant: "", category: "fixedWing" },
    AUJ4: { manufacturer: "Auster", model: "J-4 Archer", variant: "", category: "fixedWing" },
    AURA: { manufacturer: "Sunward", model: "SA60 Aurora", variant: "", category: "fixedWing" },
    AUS3: {
        manufacturer: "Taylorcraft (2)",
        model: "Auster 3",
        variant: "",
        category: "fixedWing",
    },
    AUS4: {
        manufacturer: "Taylorcraft (2)",
        model: "Auster 4",
        variant: "",
        category: "fixedWing",
    },
    AUS5: { manufacturer: "Auster", model: "J", variant: "", category: "fixedWing" },
    AUS6: { manufacturer: "Auster", model: "K", variant: "", category: "fixedWing" },
    AUS7: { manufacturer: "Auster", model: "Q", variant: "", category: "fixedWing" },
    AUS9: { manufacturer: "Auster", model: "B5", variant: "", category: "fixedWing" },
    AV68: { manufacturer: "Alpla", model: "AVO-68 Samburo", variant: "", category: "fixedWing" },
    AVAM: { manufacturer: "Avid", model: "Avid Amphibian", variant: "", category: "fixedWing" },
    AVID: { manufacturer: "Airdale", model: "Avid Flyer", variant: "", category: "fixedWing" },
    AVIN: { manufacturer: "Avro", model: "594 Avian", variant: "", category: "fixedWing" },
    AVTR: {
        manufacturer: "Aero Adventure",
        model: "Aventura 2",
        variant: "",
        category: "fixedWing",
    },
    AXE: { manufacturer: "Skyfly", model: "Axe", variant: "", category: "fixedWing" },
    B06: { manufacturer: "Bell", model: "206", variant: "", category: "rotorcraft" },
    B06T: { manufacturer: "Bell", model: "206LT TwinRanger", variant: "", category: "rotorcraft" },
    B1: { manufacturer: "Rockwell", model: "B-1 Lancer", variant: "", category: "fixedWing" },
    B103: { manufacturer: "Beriev", model: "Be-103 Bekas", variant: "", category: "fixedWing" },
    B105: { manufacturer: "Bolkow", model: "BO-105", variant: "", category: "rotorcraft" },
    B13: { manufacturer: "Akaflieg Berlin", model: "B-13", variant: "", category: "fixedWing" },
    B14A: { manufacturer: "Bellanca", model: "14 Cruisair", variant: "", category: "fixedWing" },
    B14B: { manufacturer: "Downer", model: "14 Bellanca 260", variant: "", category: "fixedWing" },
    B14C: {
        manufacturer: "Bellanca",
        model: "14 Bellanca 260A",
        variant: "",
        category: "fixedWing",
    },
    B150: { manufacturer: "Winner", model: "B-150", variant: "", category: "rotorcraft" },
    B17: {
        manufacturer: "Boeing",
        model: "B-17 Flying Fortress",
        variant: "",
        category: "fixedWing",
    },
    B18T: { manufacturer: "Beech", model: "18 (turbine)", variant: "", category: "fixedWing" },
    B190: { manufacturer: "Beech", model: "1900", variant: "", category: "fixedWing" },
    B2: { manufacturer: "Northrop", model: "B-2 Spirit", variant: "", category: "fixedWing" },
    B209: { manufacturer: "Bolkow", model: "BO-209 Monsun", variant: "", category: "fixedWing" },
    B21: {
        manufacturer: "Northrop Grumman",
        model: "B-21 Raider",
        variant: "",
        category: "fixedWing",
    },
    B212: { manufacturer: "Agusta", model: "AB-212", variant: "", category: "rotorcraft" },
    B214: { manufacturer: "Bell", model: "214A Isfahan", variant: "", category: "rotorcraft" },
    B222: { manufacturer: "Bell", model: "222", variant: "", category: "rotorcraft" },
    B23: { manufacturer: "Douglas", model: "B-23 Dragon", variant: "", category: "fixedWing" },
    B230: { manufacturer: "Bell", model: "230", variant: "", category: "rotorcraft" },
    B23E: {
        manufacturer: "Brm Aero",
        model: "Bristell B23 Energic",
        variant: "",
        category: "fixedWing",
    },
    B24: {
        manufacturer: "Consolidated",
        model: "B-24 Liberator",
        variant: "",
        category: "fixedWing",
    },
    B25: {
        manufacturer: "North American",
        model: "B-25 Mitchell",
        variant: "",
        category: "fixedWing",
    },
    B26: { manufacturer: "Douglas", model: "A-26 Invader", variant: "", category: "fixedWing" },
    B26M: { manufacturer: "Martin", model: "179 Marauder", variant: "", category: "fixedWing" },
    B29: {
        manufacturer: "Boeing",
        model: "B-29 Superfortress",
        variant: "",
        category: "fixedWing",
    },
    B305: { manufacturer: "Brantly", model: "305", variant: "", category: "rotorcraft" },
    B350: {
        manufacturer: "Beech",
        model: "300 (B300) Super King Air 350",
        variant: "",
        category: "fixedWing",
    },
    B360: {
        manufacturer: "Yakovlev Aircraft",
        model: "Bear 360",
        variant: "",
        category: "fixedWing",
    },
    B36T: {
        manufacturer: "Allison",
        model: "36 Turbine Bonanza",
        variant: "",
        category: "fixedWing",
    },
    B37M: { manufacturer: "Boeing", model: "737 MAX 7", variant: "", category: "fixedWing" },
    B38M: { manufacturer: "Boeing", model: "737", variant: "MAX 8", category: "fixedWing" },
    B39M: { manufacturer: "Boeing", model: "737", variant: "MAX 9", category: "fixedWing" },
    B3XM: { manufacturer: "Boeing", model: "737-10", variant: "", category: "fixedWing" },
    B407: { manufacturer: "Bell", model: "407", variant: "", category: "rotorcraft" },
    B412: { manufacturer: "Bell", model: "412", variant: "", category: "rotorcraft" },
    B427: { manufacturer: "Bell", model: "427", variant: "", category: "rotorcraft" },
    B429: { manufacturer: "Bell", model: "429 GlobalRanger", variant: "", category: "rotorcraft" },
    B430: { manufacturer: "Bell", model: "430", variant: "", category: "rotorcraft" },
    B461: {
        manufacturer: "British Aerospace",
        model: "BAe-146-100",
        variant: "",
        category: "fixedWing",
    },
    B462: {
        manufacturer: "British Aerospace",
        model: "BAe-146-200",
        variant: "",
        category: "fixedWing",
    },
    B463: {
        manufacturer: "British Aerospace",
        model: "BAe-146-300",
        variant: "",
        category: "fixedWing",
    },
    B47G: { manufacturer: "Agusta", model: "AB-47G", variant: "", category: "rotorcraft" },
    B47J: { manufacturer: "Agusta", model: "AB-47J", variant: "", category: "rotorcraft" },
    B47T: { manufacturer: "Soloy", model: "Bell 47", variant: "", category: "rotorcraft" },
    B505: { manufacturer: "Bell", model: "505 Jet Ranger X", variant: "", category: "rotorcraft" },
    B52: {
        manufacturer: "Boeing",
        model: "B-52 Stratofortress",
        variant: "",
        category: "fixedWing",
    },
    B525: { manufacturer: "Bell", model: "525 Relentless", variant: "", category: "rotorcraft" },
    B58T: {
        manufacturer: "Beech",
        model: "58P Pressurized Baron",
        variant: "",
        category: "fixedWing",
    },
    B60: { manufacturer: "Boisavia", model: "B-60 Mercurey", variant: "", category: "fixedWing" },
    B609: { manufacturer: "Agustawestland", model: "AW-609", variant: "", category: "other" },
    B60T: {
        manufacturer: "Beech",
        model: "60 Royal Turbine Duke",
        variant: "",
        category: "fixedWing",
    },
    B701: { manufacturer: "Boeing", model: "707-100", variant: "", category: "fixedWing" },
    B703: { manufacturer: "Boeing", model: "707-300", variant: "", category: "fixedWing" },
    B712: { manufacturer: "Boeing", model: "717-200", variant: "", category: "fixedWing" },
    B720: { manufacturer: "Boeing", model: "720", variant: "", category: "fixedWing" },
    B721: { manufacturer: "Boeing", model: "727-100", variant: "", category: "fixedWing" },
    B722: { manufacturer: "Boeing", model: "727-200", variant: "", category: "fixedWing" },
    B732: { manufacturer: "Boeing", model: "737-200", variant: "", category: "fixedWing" },
    B733: { manufacturer: "Boeing", model: "737-300", variant: "", category: "fixedWing" },
    B734: { manufacturer: "Boeing", model: "737-400", variant: "", category: "fixedWing" },
    B735: { manufacturer: "Boeing", model: "737-500", variant: "", category: "fixedWing" },
    B736: { manufacturer: "Boeing", model: "737-600", variant: "", category: "fixedWing" },
    B737: { manufacturer: "Boeing", model: "737", variant: "700", category: "fixedWing" },
    B738: { manufacturer: "Boeing", model: "737", variant: "800", category: "fixedWing" },
    B739: { manufacturer: "Boeing", model: "737", variant: "900", category: "fixedWing" },
    B741: { manufacturer: "Boeing", model: "747-100", variant: "", category: "fixedWing" },
    B742: { manufacturer: "Boeing", model: "747-200", variant: "", category: "fixedWing" },
    B743: { manufacturer: "Boeing", model: "747-300", variant: "", category: "fixedWing" },
    B744: { manufacturer: "Boeing", model: "747", variant: "400", category: "fixedWing" },
    B748: { manufacturer: "Boeing", model: "747-8", variant: "", category: "fixedWing" },
    B74R: { manufacturer: "Boeing", model: "747SR", variant: "", category: "fixedWing" },
    B74S: { manufacturer: "Boeing", model: "747SP", variant: "", category: "fixedWing" },
    B752: { manufacturer: "Boeing", model: "757", variant: "200", category: "fixedWing" },
    B753: { manufacturer: "Boeing", model: "757-300", variant: "", category: "fixedWing" },
    B762: { manufacturer: "Boeing", model: "767", variant: "200", category: "fixedWing" },
    B763: { manufacturer: "Boeing", model: "767", variant: "300", category: "fixedWing" },
    B764: { manufacturer: "Boeing", model: "767-400", variant: "", category: "fixedWing" },
    B772: { manufacturer: "Boeing", model: "777", variant: "200", category: "fixedWing" },
    B773: { manufacturer: "Boeing", model: "777-300", variant: "", category: "fixedWing" },
    B778: { manufacturer: "Boeing", model: "777-8", variant: "", category: "fixedWing" },
    B779: { manufacturer: "Boeing", model: "777-9", variant: "", category: "fixedWing" },
    B77L: { manufacturer: "Boeing", model: "777-200LR", variant: "", category: "fixedWing" },
    B77W: { manufacturer: "Boeing", model: "777", variant: "300ER", category: "fixedWing" },
    B788: { manufacturer: "Boeing", model: "787", variant: "8", category: "fixedWing" },
    B789: { manufacturer: "Boeing", model: "787", variant: "9", category: "fixedWing" },
    B78X: { manufacturer: "Boeing", model: "787", variant: "10", category: "fixedWing" },
    BA11: { manufacturer: "Bac", model: "111 One-Eleven", variant: "", category: "fixedWing" },
    BABY: {
        manufacturer: "Canadian Home Rotors",
        model: "Baby Belle",
        variant: "",
        category: "rotorcraft",
    },
    BALL: { manufacturer: "Generic", model: "Balloon", variant: "", category: "lta" },
    BARC: { manufacturer: "Buethe", model: "Barracuda", variant: "", category: "fixedWing" },
    BASS: { manufacturer: "Beagle", model: "B-206", variant: "", category: "fixedWing" },
    BBAT: { manufacturer: "Bradley", model: "BA-100 Aerobat", variant: "", category: "fixedWing" },
    BBIR: { manufacturer: "Hovey", model: "Beta Bird", variant: "", category: "fixedWing" },
    BCA3: {
        manufacturer: "Buhl",
        model: "CA-3 Sport Airsedan",
        variant: "",
        category: "fixedWing",
    },
    BCAT: { manufacturer: "Grumman", model: "F8F Bearcat", variant: "", category: "fixedWing" },
    BCMP: {
        manufacturer: "Avipro",
        model: "Bearhawk Companion",
        variant: "",
        category: "fixedWing",
    },
    BCS1: { manufacturer: "Airbus", model: "A-220-100", variant: "", category: "fixedWing" },
    BCS3: { manufacturer: "Airbus", model: "A-220-300", variant: "", category: "fixedWing" },
    BD10: { manufacturer: "Bede", model: "BD-10", variant: "", category: "fixedWing" },
    BD17: { manufacturer: "Bede", model: "BD-17 Nuggett", variant: "", category: "fixedWing" },
    BD4: { manufacturer: "Bede", model: "BD-4", variant: "", category: "fixedWing" },
    BD5: { manufacturer: "Alturair", model: "BD-5B Micro", variant: "", category: "fixedWing" },
    BD5J: { manufacturer: "Alturair", model: "BD-5J Micro", variant: "", category: "fixedWing" },
    BD5T: { manufacturer: "Bd-Micro", model: "BD-5T Micro", variant: "", category: "fixedWing" },
    BDOG: { manufacturer: "Beagle", model: "B-125 Bulldog", variant: "", category: "fixedWing" },
    BE10: { manufacturer: "Beech", model: "100 King Air", variant: "", category: "fixedWing" },
    BE12: { manufacturer: "Beriev", model: "Be-12 Tchaika", variant: "", category: "fixedWing" },
    BE17: { manufacturer: "Beech", model: "17 Staggerwing", variant: "", category: "fixedWing" },
    BE18: { manufacturer: "Beech", model: "18 (piston)", variant: "", category: "fixedWing" },
    BE19: {
        manufacturer: "Beech",
        model: "19 Musketeer Sport",
        variant: "",
        category: "fixedWing",
    },
    BE20: { manufacturer: "Beechcraft", model: "King Air", variant: "200", category: "fixedWing" },
    BE22: { manufacturer: "Beechcraft", model: "220 Denali", variant: "", category: "fixedWing" },
    BE23: { manufacturer: "Beech", model: "23 Musketeer", variant: "", category: "fixedWing" },
    BE24: {
        manufacturer: "Beech",
        model: "24 Musketeer Super",
        variant: "",
        category: "fixedWing",
    },
    BE30: {
        manufacturer: "Beech",
        model: "300 Super King Air",
        variant: "",
        category: "fixedWing",
    },
    BE32: { manufacturer: "Beriev", model: "Be-30", variant: "", category: "fixedWing" },
    BE33: { manufacturer: "Beech", model: "33 Bonanza", variant: "", category: "fixedWing" },
    BE35: { manufacturer: "Beech", model: "35 Bonanza", variant: "", category: "fixedWing" },
    BE36: { manufacturer: "Beech", model: "A36 Bonanza 36", variant: "", category: "fixedWing" },
    BE40: { manufacturer: "Beech", model: "400 Beechjet", variant: "", category: "fixedWing" },
    BE4W: { manufacturer: "Beech", model: "400XT Beechjet", variant: "", category: "fixedWing" },
    BE50: { manufacturer: "Beech", model: "50 Twin Bonanza", variant: "", category: "fixedWing" },
    BE55: { manufacturer: "Beech", model: "55 Baron", variant: "", category: "fixedWing" },
    BE56: { manufacturer: "Beech", model: "56 Turbo Baron", variant: "", category: "fixedWing" },
    BE58: { manufacturer: "Beech", model: "58 Baron", variant: "", category: "fixedWing" },
    BE60: { manufacturer: "Beech", model: "60 Duke", variant: "", category: "fixedWing" },
    BE65: { manufacturer: "Beech", model: "65 Queen Air", variant: "", category: "fixedWing" },
    BE70: { manufacturer: "Beech", model: "70 Queen Air", variant: "", category: "fixedWing" },
    BE76: { manufacturer: "Beech", model: "76 Duchess", variant: "", category: "fixedWing" },
    BE77: { manufacturer: "Beech", model: "77 Skipper", variant: "", category: "fixedWing" },
    BE80: { manufacturer: "Beech", model: "80 Queen Air", variant: "", category: "fixedWing" },
    BE88: { manufacturer: "Beech", model: "88 Queen Air", variant: "", category: "fixedWing" },
    BE95: { manufacturer: "Beech", model: "95 Travel Air", variant: "", category: "fixedWing" },
    BE99: { manufacturer: "Beech", model: "99 Airliner", variant: "", category: "fixedWing" },
    BE9L: { manufacturer: "Beech", model: "90 (A90) King Air", variant: "", category: "fixedWing" },
    BE9T: { manufacturer: "Beech", model: "90 (F90) King Air", variant: "", category: "fixedWing" },
    BEA5: { manufacturer: "Avipro", model: "Bearhawk 5", variant: "", category: "fixedWing" },
    BEAR: { manufacturer: "Avipro", model: "Bearhawk", variant: "", category: "fixedWing" },
    BELF: { manufacturer: "Short", model: "SC-5 Belfast", variant: "", category: "fixedWing" },
    BER2: { manufacturer: "Beriev", model: "Be-200 Altair", variant: "", category: "fixedWing" },
    BER4: { manufacturer: "Beriev", model: "A-40 Albatross", variant: "", category: "fixedWing" },
    BETA: { manufacturer: "Rollason", model: "Beta", variant: "", category: "fixedWing" },
    BEVR: { manufacturer: "Asap", model: "Beaver", variant: "", category: "fixedWing" },
    BF19: { manufacturer: "Podesva", model: "Bf-109G-2", variant: "", category: "fixedWing" },
    BFIT: { manufacturer: "Bristol", model: "F-2B Fighter", variant: "", category: "fixedWing" },
    BILO: { manufacturer: "Pena", model: "Bilouis", variant: "", category: "fixedWing" },
    BIPL: { manufacturer: "Eaa", model: "Biplane", variant: "", category: "fixedWing" },
    BIRD: { manufacturer: "Taylor (3)", model: "TA-2 Bird", variant: "", category: "fixedWing" },
    BISC: { manufacturer: "Bilsam", model: "Sky Cruiser", variant: "", category: "fixedWing" },
    BK17: { manufacturer: "Casa", model: "BK-117", variant: "", category: "rotorcraft" },
    BKUT: { manufacturer: "Berkut", model: "Berkut", variant: "", category: "fixedWing" },
    BL11: { manufacturer: "Bleriot", model: "11", variant: "", category: "fixedWing" },
    BL17: {
        manufacturer: "Alexandria",
        model: "17 Super Viking",
        variant: "",
        category: "fixedWing",
    },
    BL19: {
        manufacturer: "Aviabellanca",
        model: "19 Skyrocket",
        variant: "",
        category: "fixedWing",
    },
    BL8: {
        manufacturer: "American Champion",
        model: "8 Scout",
        variant: "",
        category: "fixedWing",
    },
    BLBU: { manufacturer: "Ameur", model: "Altania", variant: "", category: "fixedWing" },
    BLCF: {
        manufacturer: "Boeing",
        model: "747-400LCF Dreamlifter",
        variant: "",
        category: "fixedWing",
    },
    BLEN: { manufacturer: "Bristol", model: "149 Blenheim", variant: "", category: "fixedWing" },
    BLKS: {
        manufacturer: "Aircraft Spruce",
        model: "Baby Lakes",
        variant: "",
        category: "fixedWing",
    },
    BLSA: { manufacturer: "Avipro", model: "Bearhawk LSA", variant: "", category: "fixedWing" },
    BM6: { manufacturer: "Maranda", model: "BM-6 Lark", variant: "", category: "fixedWing" },
    BMAN: { manufacturer: "Aak", model: "Bushman", variant: "", category: "fixedWing" },
    BN2P: {
        manufacturer: "Avions Fairey",
        model: "BN-2A Defender",
        variant: "",
        category: "fixedWing",
    },
    BN2T: {
        manufacturer: "Britten-Norman",
        model: "BN-2T Defender 4000",
        variant: "",
        category: "fixedWing",
    },
    BO40: { manufacturer: "Boeing", model: "40", variant: "", category: "fixedWing" },
    BOLT: { manufacturer: "Steen", model: "Skybolt", variant: "", category: "fixedWing" },
    BOOM: { manufacturer: "Rutan", model: "202 Boomerang", variant: "", category: "fixedWing" },
    BPAT: { manufacturer: "Avipro", model: "Bearhawk Patrol", variant: "", category: "fixedWing" },
    BPUM: { manufacturer: "Bdc Aero", model: "Puma", variant: "", category: "fixedWing" },
    BR14: { manufacturer: "Breguet", model: "14 Replica", variant: "", category: "fixedWing" },
    BR23: { manufacturer: "Brm Aero", model: "Bristell B23", variant: "", category: "fixedWing" },
    BR54: { manufacturer: "Barnett", model: "BRC-540", variant: "", category: "rotorcraft" },
    BR60: { manufacturer: "Brumby", model: "Brumby", variant: "", category: "fixedWing" },
    BR61: { manufacturer: "Brumby", model: "610 Evolution", variant: "", category: "fixedWing" },
    BR8: { manufacturer: "Brm Aero", model: "Bristell B8", variant: "", category: "fixedWing" },
    BRAV: { manufacturer: "Tecnam", model: "P-2004 Bravo", variant: "", category: "fixedWing" },
    BRB2: { manufacturer: "Brantly", model: "B-2", variant: "", category: "rotorcraft" },
    BREZ: { manufacturer: "Aerostyle", model: "Breezer", variant: "", category: "fixedWing" },
    BROU: {
        manufacturer: "Max Holste",
        model: "MH-1521 Broussard",
        variant: "",
        category: "fixedWing",
    },
    BS60: { manufacturer: "Beech-Sferma", model: "60 Marquis", variant: "", category: "fixedWing" },
    BSTP: {
        manufacturer: "Bell",
        model: "214ST SuperTransport",
        variant: "",
        category: "rotorcraft",
    },
    BSWN: { manufacturer: "Dronamics", model: "Black Swan", variant: "", category: "fixedWing" },
    BT36: { manufacturer: "Beech", model: "A36TC Bonanza", variant: "", category: "fixedWing" },
    BT7: { manufacturer: "Boeing", model: "T-7 Red Hawk", variant: "", category: "fixedWing" },
    BTB2: { manufacturer: "Baykar", model: "Bayraktar TB-2", variant: "", category: "fixedWing" },
    BTB3: { manufacturer: "Baykar", model: "Bayraktar TB-3", variant: "", category: "fixedWing" },
    BTUB: { manufacturer: "Kimbrel", model: "Dormoy Bathtub", variant: "", category: "fixedWing" },
    BU20: {
        manufacturer: "Aircraft Hydro-Forming",
        model: "Bushmaster 2000",
        variant: "",
        category: "fixedWing",
    },
    BU31: { manufacturer: "Aero (2)", model: "C-104", variant: "", category: "fixedWing" },
    BU33: { manufacturer: "Bitz", model: "Bü-133 Jungmeister", variant: "", category: "fixedWing" },
    BU81: { manufacturer: "Bucker", model: "Bü-181 Bestmann", variant: "", category: "fixedWing" },
    BUC: {
        manufacturer: "Hawker Siddeley",
        model: "Buccaneer",
        variant: "",
        category: "fixedWing",
    },
    BUCA: {
        manufacturer: "Advanced Aeromarine",
        model: "Mallard",
        variant: "",
        category: "fixedWing",
    },
    BUSH: {
        manufacturer: "Rainbow Skyreach",
        model: "BushCat",
        variant: "",
        category: "fixedWing",
    },
    BW60: { manufacturer: "Blackwing", model: "BW-600", variant: "", category: "fixedWing" },
    BW6T: { manufacturer: "Blackwing", model: "BW-635", variant: "", category: "fixedWing" },
    BX2: { manufacturer: "Brandli", model: "BX-2 Cherry", variant: "", category: "fixedWing" },
    C02T: { manufacturer: "Aji", model: "Turbo Star 402", variant: "", category: "fixedWing" },
    C04T: { manufacturer: "Cessna", model: "404 (turbine)", variant: "", category: "fixedWing" },
    C06T: { manufacturer: "Cessna", model: "206 (turbine)", variant: "", category: "fixedWing" },
    C07T: { manufacturer: "Cessna", model: "207 (turbine)", variant: "", category: "fixedWing" },
    C1: { manufacturer: "Kawasaki", model: "C-1", variant: "", category: "fixedWing" },
    C101: { manufacturer: "Casa", model: "E-25", variant: "", category: "fixedWing" },
    C10T: {
        manufacturer: "Advanced Aircraft",
        model: "Spirit 750",
        variant: "",
        category: "fixedWing",
    },
    C119: {
        manufacturer: "Fairchild (1)",
        model: "C-119 Flying Boxcar",
        variant: "",
        category: "fixedWing",
    },
    C120: { manufacturer: "Cessna", model: "120", variant: "", category: "fixedWing" },
    C123: {
        manufacturer: "Fairchild (1)",
        model: "C-123 Provider",
        variant: "",
        category: "fixedWing",
    },
    C130: { manufacturer: "Lockheed", model: "C-130 Karnaf", variant: "", category: "fixedWing" },
    C135: { manufacturer: "Boeing", model: "NC-135", variant: "", category: "fixedWing" },
    C140: { manufacturer: "Cessna", model: "140", variant: "", category: "fixedWing" },
    C14T: { manufacturer: "Aji", model: "Turbo Star 414", variant: "", category: "fixedWing" },
    C15: { manufacturer: "Mcdonnell Douglas", model: "YC-15", variant: "", category: "fixedWing" },
    C150: { manufacturer: "Aviones Colombia", model: "150", variant: "", category: "fixedWing" },
    C152: { manufacturer: "Aviones Colombia", model: "152", variant: "", category: "fixedWing" },
    C160: { manufacturer: "Transall", model: "C-160", variant: "", category: "fixedWing" },
    C162: { manufacturer: "Cessna", model: "162 Skycatcher", variant: "", category: "fixedWing" },
    C17: {
        manufacturer: "Boeing",
        model: "C-17 Globemaster 3",
        variant: "",
        category: "fixedWing",
    },
    C170: { manufacturer: "Cessna", model: "170", variant: "", category: "fixedWing" },
    C172: { manufacturer: "Cessna", model: "172", variant: "", category: "fixedWing" },
    C175: { manufacturer: "Cessna", model: "175", variant: "", category: "fixedWing" },
    C177: { manufacturer: "Cessna", model: "177", variant: "", category: "fixedWing" },
    C180: { manufacturer: "Cessna", model: "180", variant: "", category: "fixedWing" },
    C182: { manufacturer: "Cessna", model: "182", variant: "", category: "fixedWing" },
    C185: { manufacturer: "Aviatech", model: "Super Cyclone", variant: "", category: "fixedWing" },
    C188: { manufacturer: "Aviones Colombia", model: "188", variant: "", category: "fixedWing" },
    C190: { manufacturer: "Cessna", model: "190", variant: "", category: "fixedWing" },
    C195: { manufacturer: "Cessna", model: "195", variant: "", category: "fixedWing" },
    C2: { manufacturer: "Grumman", model: "C-2 Greyhound", variant: "", category: "fixedWing" },
    C205: { manufacturer: "Cessna", model: "205", variant: "", category: "fixedWing" },
    C206: { manufacturer: "Aviones Colombia", model: "206", variant: "", category: "fixedWing" },
    C207: { manufacturer: "Cessna", model: "207 Skywagon 207", variant: "", category: "fixedWing" },
    C208: { manufacturer: "Cessna", model: "208", variant: "Caravan", category: "fixedWing" },
    C210: { manufacturer: "Aviones Colombia", model: "210", variant: "", category: "fixedWing" },
    C212: { manufacturer: "Airbus", model: "C-212", variant: "", category: "fixedWing" },
    C21T: {
        manufacturer: "Advanced Aircraft",
        model: "Regent 1500",
        variant: "",
        category: "fixedWing",
    },
    C22J: {
        manufacturer: "Caproni Vizzola",
        model: "C-22J Ventura",
        variant: "",
        category: "fixedWing",
    },
    C240: {
        manufacturer: "Cessna",
        model: "T240 Corvalis TTx",
        variant: "",
        category: "fixedWing",
    },
    C25A: {
        manufacturer: "Cessna",
        model: "525A Citation CJ2",
        variant: "",
        category: "fixedWing",
    },
    C25B: {
        manufacturer: "Cessna",
        model: "525B Citation CJ3",
        variant: "",
        category: "fixedWing",
    },
    C25C: {
        manufacturer: "Cessna",
        model: "525C Citation CJ4",
        variant: "",
        category: "fixedWing",
    },
    C25M: { manufacturer: "Cessna", model: "525 Citation M2", variant: "", category: "fixedWing" },
    C270: { manufacturer: "Caudron", model: "C-270 Luciole", variant: "", category: "fixedWing" },
    C27J: { manufacturer: "Alenia", model: "C-27J Spartan", variant: "", category: "fixedWing" },
    C295: { manufacturer: "Airbus", model: "C-295", variant: "", category: "fixedWing" },
    C303: { manufacturer: "Aviones Colombia", model: "T303", variant: "", category: "fixedWing" },
    C306: {
        manufacturer: "Cea-Ufmg",
        model: "CEA-306 CB-10 Triathlon",
        variant: "",
        category: "fixedWing",
    },
    C309: { manufacturer: "Cea-Ufmg", model: "CEA-309 Mehari", variant: "", category: "fixedWing" },
    C30J: {
        manufacturer: "Lockheed Martin",
        model: "382J Super Hercules",
        variant: "",
        category: "fixedWing",
    },
    C310: { manufacturer: "Aviones Colombia", model: "310", variant: "", category: "fixedWing" },
    C311: {
        manufacturer: "Cea-Ufmg",
        model: "CEA-311 Anequim",
        variant: "",
        category: "fixedWing",
    },
    C320: {
        manufacturer: "Cessna",
        model: "320 Executive Skyknight",
        variant: "",
        category: "fixedWing",
    },
    C335: { manufacturer: "Cessna", model: "335", variant: "", category: "fixedWing" },
    C336: { manufacturer: "Cessna", model: "336 Skymaster", variant: "", category: "fixedWing" },
    C337: { manufacturer: "Aviones Colombia", model: "337", variant: "", category: "fixedWing" },
    C340: { manufacturer: "Aviones Colombia", model: "340", variant: "", category: "fixedWing" },
    C365: { manufacturer: "Ekw", model: "C-3605", variant: "", category: "fixedWing" },
    C402: { manufacturer: "Aviones Colombia", model: "402", variant: "", category: "fixedWing" },
    C404: { manufacturer: "Cessna", model: "404 Titan", variant: "", category: "fixedWing" },
    C408: { manufacturer: "Cessna", model: "408 SkyCourier", variant: "", category: "fixedWing" },
    C411: { manufacturer: "Cessna", model: "411", variant: "", category: "fixedWing" },
    C414: { manufacturer: "Aviones Colombia", model: "414", variant: "", category: "fixedWing" },
    C42: { manufacturer: "Ikarus", model: "C-42", variant: "", category: "fixedWing" },
    C421: { manufacturer: "Cessna", model: "421", variant: "", category: "fixedWing" },
    C425: { manufacturer: "Cessna", model: "425 Conquest 1", variant: "", category: "fixedWing" },
    C441: { manufacturer: "Cessna", model: "441 Conquest", variant: "", category: "fixedWing" },
    C46: { manufacturer: "Curtiss", model: "C-46 Commando", variant: "", category: "fixedWing" },
    C500: { manufacturer: "Cessna", model: "500 Citation", variant: "", category: "fixedWing" },
    C501: { manufacturer: "Cessna", model: "501 Citation 1SP", variant: "", category: "fixedWing" },
    C510: {
        manufacturer: "Cessna",
        model: "510 Citation Mustang",
        variant: "",
        category: "fixedWing",
    },
    C525: { manufacturer: "Cessna", model: "525 Citation CJ1", variant: "", category: "fixedWing" },
    C550: { manufacturer: "Cessna", model: "550 Citation 2", variant: "", category: "fixedWing" },
    C551: { manufacturer: "Cessna", model: "551 Citation 2SP", variant: "", category: "fixedWing" },
    C55B: {
        manufacturer: "Cessna",
        model: "550B Citation Bravo",
        variant: "",
        category: "fixedWing",
    },
    C560: { manufacturer: "Cessna", model: "560 Citation 5", variant: "", category: "fixedWing" },
    C56X: {
        manufacturer: "Cessna",
        model: "560XL Citation Excel",
        variant: "",
        category: "fixedWing",
    },
    C5M: {
        manufacturer: "Lockheed",
        model: "C-5 Super Galaxy",
        variant: "",
        category: "fixedWing",
    },
    C650: { manufacturer: "Cessna", model: "650 Citation 3", variant: "", category: "fixedWing" },
    C680: {
        manufacturer: "Cessna",
        model: "680 Citation Sovereign",
        variant: "",
        category: "fixedWing",
    },
    C68A: {
        manufacturer: "Cessna",
        model: "680A Citation Latitude",
        variant: "",
        category: "fixedWing",
    },
    C700: {
        manufacturer: "Cessna",
        model: "700 Citation Longitude",
        variant: "",
        category: "fixedWing",
    },
    C72R: { manufacturer: "Aviones Colombia", model: "172RG", variant: "", category: "fixedWing" },
    C750: { manufacturer: "Cessna", model: "750 Citation 10", variant: "", category: "fixedWing" },
    C77R: { manufacturer: "Aviones Colombia", model: "177RG", variant: "", category: "fixedWing" },
    C82: {
        manufacturer: "Fairchild (1)",
        model: "C-82 Packet",
        variant: "",
        category: "fixedWing",
    },
    C82R: { manufacturer: "Aviones Colombia", model: "R182", variant: "", category: "fixedWing" },
    C82S: {
        manufacturer: "Cessna",
        model: "T182 Turbo Skylane",
        variant: "",
        category: "fixedWing",
    },
    C82T: {
        manufacturer: "Cessna",
        model: "TR182 Turbo Skylane RG",
        variant: "",
        category: "fixedWing",
    },
    C919: { manufacturer: "Comac", model: "C-919", variant: "", category: "fixedWing" },
    C97: {
        manufacturer: "Boeing",
        model: "C-97 Stratofreighter",
        variant: "",
        category: "fixedWing",
    },
    CA12: {
        manufacturer: "Comp Air",
        model: "CA-12 Comp Air 12",
        variant: "",
        category: "fixedWing",
    },
    CA19: {
        manufacturer: "Commonwealth (1)",
        model: "CA-13 Boomerang",
        variant: "",
        category: "fixedWing",
    },
    CA1P: {
        manufacturer: "Aerocomp",
        model: "CA-10 Comp Air 10",
        variant: "",
        category: "fixedWing",
    },
    CA1T: {
        manufacturer: "Aerocomp",
        model: "CA-10T Comp Air 10T",
        variant: "",
        category: "fixedWing",
    },
    CA25: {
        manufacturer: "Commonwealth (1)",
        model: "CA-25 Winjeel",
        variant: "",
        category: "fixedWing",
    },
    CA3: { manufacturer: "Aerocomp", model: "CA-3 Comp Air 3", variant: "", category: "fixedWing" },
    CA4: { manufacturer: "Aerocomp", model: "CA-4 Comp Air 4", variant: "", category: "fixedWing" },
    CA41: { manufacturer: "Corvus", model: "CA-41 Racer", variant: "", category: "fixedWing" },
    CA6: { manufacturer: "Aerocomp", model: "CA-6 Comp Air 6", variant: "", category: "fixedWing" },
    CA61: {
        manufacturer: "Cvjetkovic",
        model: "CA-61 Mini Ace",
        variant: "",
        category: "fixedWing",
    },
    CA65: { manufacturer: "Cvjetkovic", model: "CA-65 Skyfly", variant: "", category: "fixedWing" },
    CA7P: {
        manufacturer: "Aerocomp",
        model: "CA-7P Comp Air 7P",
        variant: "",
        category: "fixedWing",
    },
    CA7T: {
        manufacturer: "Aerocomp",
        model: "CA-7SLX Comp Air 7SLX",
        variant: "",
        category: "fixedWing",
    },
    CA8: { manufacturer: "Aerocomp", model: "CA-8 Comp Air 8", variant: "", category: "fixedWing" },
    CA9: { manufacturer: "Comp Air", model: "CA-9 Comp Air 9", variant: "", category: "fixedWing" },
    CABI: {
        manufacturer: "Universal Composite",
        model: "Carbon Bird",
        variant: "",
        category: "fixedWing",
    },
    CABN: { manufacturer: "Paramount", model: "Cabinaire", variant: "", category: "fixedWing" },
    CAD2: { manufacturer: "Class", model: "Bush Caddy R-80", variant: "", category: "fixedWing" },
    CAD4: { manufacturer: "Class", model: "Bush Caddy L-160", variant: "", category: "fixedWing" },
    CAML: { manufacturer: "Sopwith", model: "Camel", variant: "", category: "fixedWing" },
    CAMP: { manufacturer: "Grega", model: "GN-1 Aircamper", variant: "", category: "fixedWing" },
    CAN4: { manufacturer: "Campana", model: "AN-4", variant: "", category: "fixedWing" },
    CAPL: { manufacturer: "Capella", model: "Capella", variant: "", category: "fixedWing" },
    CAR: { manufacturer: "Aerocar", model: "Aerocar", variant: "", category: "fixedWing" },
    CARV: {
        manufacturer: "Aviation Traders",
        model: "ATL-98 Carvair",
        variant: "",
        category: "fixedWing",
    },
    CASS: { manufacturer: "Cassutt", model: "Special", variant: "", category: "fixedWing" },
    CAT: { manufacturer: "Boeing Canada", model: "Canso", variant: "", category: "fixedWing" },
    CAT1: {
        manufacturer: "Creative Flight",
        model: "MPA Aerocat SR",
        variant: "",
        category: "fixedWing",
    },
    CAT2: {
        manufacturer: "Creative Flight",
        model: "MPA Aerocat TR",
        variant: "",
        category: "fixedWing",
    },
    CAW: { manufacturer: "Cessna", model: "AW", variant: "", category: "fixedWing" },
    CB1: { manufacturer: "Hatz", model: "CB-1 Biplane", variant: "", category: "fixedWing" },
    CC11: {
        manufacturer: "Cub Crafters",
        model: "CC-11 Carbon Cub",
        variant: "",
        category: "fixedWing",
    },
    CC19: {
        manufacturer: "Cub Crafters",
        model: "CC-19 NXCub",
        variant: "",
        category: "fixedWing",
    },
    CD2: {
        manufacturer: "Claudius Dornier",
        model: "CD-2 Seastar",
        variant: "",
        category: "fixedWing",
    },
    CDC6: { manufacturer: "Cessna", model: "DC-6", variant: "", category: "fixedWing" },
    CDUS: { manufacturer: "Autogyro", model: "Calidus", variant: "", category: "rotorcraft" },
    CDW1: { manufacturer: "Chilton", model: "DW-1", variant: "", category: "fixedWing" },
    CE15: { manufacturer: "Chernov", model: "Che-15", variant: "", category: "fixedWing" },
    CE22: { manufacturer: "A-41", model: "VNS-41", variant: "", category: "fixedWing" },
    CE23: { manufacturer: "Chernov", model: "Che-23", variant: "", category: "fixedWing" },
    CE25: { manufacturer: "Chernov", model: "Che-25", variant: "", category: "fixedWing" },
    CE27: { manufacturer: "Chernov", model: "Che-27", variant: "", category: "fixedWing" },
    CE43: { manufacturer: "Cerva", model: "CE-43 Guepard", variant: "", category: "fixedWing" },
    CEL5: { manufacturer: "Otto", model: "Celera 500L", variant: "", category: "fixedWing" },
    CELR: { manufacturer: "Mirage", model: "Celerity", variant: "", category: "fixedWing" },
    CENT: { manufacturer: "Found", model: "100 Centennial", variant: "", category: "fixedWing" },
    CFRE: { manufacturer: "Colyaer", model: "Freedom", variant: "", category: "fixedWing" },
    CG3: { manufacturer: "Caudron", model: "G-3", variant: "", category: "fixedWing" },
    CGAN: { manufacturer: "Colyaer", model: "Gannet", variant: "", category: "fixedWing" },
    CH1: { manufacturer: "Aidc", model: "A-CH-1 Chung-Tsing", variant: "", category: "fixedWing" },
    CH10: { manufacturer: "Zenair", model: "CH-100 Mono-Z", variant: "", category: "fixedWing" },
    CH12: { manufacturer: "Cicare", model: "CH-12", variant: "", category: "rotorcraft" },
    CH14: { manufacturer: "Cicare", model: "CH-14 Aguilucho", variant: "", category: "rotorcraft" },
    CH15: { manufacturer: "Zenair", model: "CH-150 Acro-Z", variant: "", category: "fixedWing" },
    CH18: {
        manufacturer: "Zenair",
        model: "CH-180 Super Acro-Z",
        variant: "",
        category: "fixedWing",
    },
    CH20: { manufacturer: "Heintz", model: "CH-200 Zénith", variant: "", category: "fixedWing" },
    CH25: { manufacturer: "Zenair", model: "CH-250 Zénith", variant: "", category: "fixedWing" },
    CH2T: { manufacturer: "Amd", model: "CH-2000 Alarus", variant: "", category: "fixedWing" },
    CH3: { manufacturer: "Chrislea", model: "CH-3 Super Ace", variant: "", category: "fixedWing" },
    CH30: { manufacturer: "Zenair", model: "CH-300 Tri-Z", variant: "", category: "fixedWing" },
    CH40: { manufacturer: "Champion", model: "402 Lancer", variant: "", category: "fixedWing" },
    CH50: {
        manufacturer: "Zenair",
        model: "CH-50 Mini Zénith",
        variant: "",
        category: "fixedWing",
    },
    CH60: { manufacturer: "Amd", model: "CH-601 Zodiac", variant: "", category: "fixedWing" },
    CH62: { manufacturer: "Zenair", model: "CH-620 Gemini", variant: "", category: "fixedWing" },
    CH64: { manufacturer: "Zenair", model: "CH-640 Zodiac", variant: "", category: "fixedWing" },
    CH65: { manufacturer: "Amd", model: "CH-650 Zodiac", variant: "", category: "fixedWing" },
    CH7: {
        manufacturer: "Heli-Sport",
        model: "CH-7 Kompress",
        variant: "",
        category: "rotorcraft",
    },
    CH70: { manufacturer: "Aerobravo", model: "Bravo", variant: "", category: "fixedWing" },
    CH75: { manufacturer: "Amd", model: "CH-750 Stol", variant: "", category: "fixedWing" },
    CH7A: { manufacturer: "Aeronca", model: "7AC Champion", variant: "", category: "fixedWing" },
    CH7B: {
        manufacturer: "American Champion",
        model: "7GCAA Citabria Ultimate Adventure",
        variant: "",
        category: "fixedWing",
    },
    CH80: { manufacturer: "Czaw", model: "CH-801 Stol", variant: "", category: "fixedWing" },
    CHAN: { manufacturer: "Cadcor", model: "Chanute", variant: "", category: "fixedWing" },
    CHCS: { manufacturer: "Alpaero", model: "Choucas", variant: "", category: "fixedWing" },
    CHIC: { manufacturer: "Podesva", model: "Chico", variant: "", category: "fixedWing" },
    CHIF: { manufacturer: "Pawnee", model: "Chief", variant: "", category: "rotorcraft" },
    CHIN: { manufacturer: "Asap", model: "Chinook", variant: "", category: "fixedWing" },
    CHIP: { manufacturer: "Leger", model: "Super-Chipmunk", variant: "", category: "fixedWing" },
    CHP2: { manufacturer: "Belite", model: "CH-2A Chipper 2", variant: "", category: "fixedWing" },
    CHR1: {
        manufacturer: "Elmwood",
        model: "CA-05 Christavia Mk1",
        variant: "",
        category: "fixedWing",
    },
    CHR4: {
        manufacturer: "Elmwood",
        model: "CH-8 Christavia Mk4",
        variant: "",
        category: "fixedWing",
    },
    CHSY: { manufacturer: "Chayair", model: "Sycamore", variant: "", category: "rotorcraft" },
    CICA: { manufacturer: "Hydroplane", model: "Cicada", variant: "", category: "fixedWing" },
    CJ1: { manufacturer: "Corby", model: "CJ-1 Starlet", variant: "", category: "fixedWing" },
    CJ6: { manufacturer: "Hongdu", model: "BT-6", variant: "", category: "fixedWing" },
    CKUO: { manufacturer: "Aidc", model: "F-CK-1 Ching-Kuo", variant: "", category: "fixedWing" },
    CL2P: { manufacturer: "Canadair", model: "CL-215", variant: "", category: "fixedWing" },
    CL2T: { manufacturer: "Bombardier", model: "UD-14", variant: "", category: "fixedWing" },
    CL30: {
        manufacturer: "Bombardier",
        model: "BD-100 Challenger 300",
        variant: "",
        category: "fixedWing",
    },
    CL35: {
        manufacturer: "Bombardier",
        model: "BD-100 Challenger 350",
        variant: "",
        category: "fixedWing",
    },
    CL41: { manufacturer: "Canadair", model: "CL-41 Tutor", variant: "", category: "fixedWing" },
    CL4G: { manufacturer: "Canadair", model: "CL-44-O Guppy", variant: "", category: "fixedWing" },
    CL60: {
        manufacturer: "Bombardier",
        model: "CL-600 Challenger 650",
        variant: "",
        category: "fixedWing",
    },
    CLA: { manufacturer: "Callair", model: "A-2", variant: "", category: "fixedWing" },
    CLB1: {
        manufacturer: "Aero Commander",
        model: "B-1 Ag Commander",
        variant: "",
        category: "fixedWing",
    },
    CLBR: { manufacturer: "Fisher", model: "Celebrity", variant: "", category: "fixedWing" },
    CLD2: {
        manufacturer: "Rotortec",
        model: "Cloud Dancer 2",
        variant: "",
        category: "rotorcraft",
    },
    CLDS: { manufacturer: "Rearwin", model: "8090 Cloudster", variant: "", category: "fixedWing" },
    CLON: { manufacturer: "Autogyro", model: "Cavalon", variant: "", category: "rotorcraft" },
    CMA3: { manufacturer: "Colyaer", model: "Martin 3", variant: "", category: "fixedWing" },
    CMAS: { manufacturer: "Cessna", model: "C-34", variant: "", category: "fixedWing" },
    CMD1: {
        manufacturer: "Air Command",
        model: "Commander 147",
        variant: "",
        category: "rotorcraft",
    },
    CMDE: {
        manufacturer: "Air Command",
        model: "Commander Elite Side-by-Side",
        variant: "",
        category: "rotorcraft",
    },
    CMDT: {
        manufacturer: "Air Command",
        model: "Commander Elite Tandem",
        variant: "",
        category: "rotorcraft",
    },
    CN12: { manufacturer: "Circa", model: "Nieuport 12-7/8", variant: "", category: "fixedWing" },
    CN35: { manufacturer: "Airbus", model: "CN-235", variant: "", category: "fixedWing" },
    CNBR: { manufacturer: "Bac", model: "Canberra", variant: "", category: "fixedWing" },
    CNDR: {
        manufacturer: "General Avia",
        model: "F-20TP Condor",
        variant: "",
        category: "fixedWing",
    },
    CNUK: { manufacturer: "Fleet", model: "80 Canuck", variant: "", category: "fixedWing" },
    CO50: { manufacturer: "Cobalt", model: "Co-50 Valkyrie", variant: "", category: "fixedWing" },
    COAR: { manufacturer: "Cobra", model: "Arrow", variant: "", category: "fixedWing" },
    COBR: { manufacturer: "Deborde-Rolland", model: "Cobra", variant: "", category: "fixedWing" },
    COL3: { manufacturer: "Cessna", model: "LC-42 Corvalis", variant: "", category: "fixedWing" },
    COL4: { manufacturer: "Cessna", model: "400", variant: "", category: "fixedWing" },
    COLT: { manufacturer: "Texas Aircraft", model: "TA-01", variant: "", category: "fixedWing" },
    COMU: { manufacturer: "Helicom", model: "H-1 Commuter", variant: "", category: "rotorcraft" },
    CONI: {
        manufacturer: "Lockheed",
        model: "C-121 Constellation",
        variant: "",
        category: "fixedWing",
    },
    COOT: { manufacturer: "Aerocar", model: "Coot", variant: "", category: "fixedWing" },
    CORO: { manufacturer: "Corvus", model: "Corone", variant: "", category: "fixedWing" },
    CORR: { manufacturer: "Sivel", model: "SD-27 Corriedale", variant: "", category: "fixedWing" },
    CORS: {
        manufacturer: "Chance Vought",
        model: "AU Corsair",
        variant: "",
        category: "fixedWing",
    },
    CORV: { manufacturer: "Wolfsberg", model: "Corvus", variant: "", category: "fixedWing" },
    COUG: { manufacturer: "Acro Sport", model: "Cougar", variant: "", category: "fixedWing" },
    COUR: { manufacturer: "Helio", model: "H-250 Courier 2", variant: "", category: "fixedWing" },
    COY2: { manufacturer: "Rans", model: "S-6 Coyote 2", variant: "", category: "fixedWing" },
    COZJ: { manufacturer: "Co-Z", model: "CozyJet", variant: "", category: "fixedWing" },
    COZY: { manufacturer: "Aerocad", model: "AeroCanard", variant: "", category: "fixedWing" },
    CP10: { manufacturer: "Akrotech Europe", model: "CAP-10", variant: "", category: "fixedWing" },
    CP13: {
        manufacturer: "Caarp",
        model: "CP-1310 Super Emeraude",
        variant: "",
        category: "fixedWing",
    },
    CP20: { manufacturer: "Caarp", model: "CAP-20", variant: "", category: "fixedWing" },
    CP21: { manufacturer: "Mudry", model: "CAP-21", variant: "", category: "fixedWing" },
    CP22: { manufacturer: "Akrotech Europe", model: "CAP-222", variant: "", category: "fixedWing" },
    CP23: { manufacturer: "Akrotech Europe", model: "CAP-232", variant: "", category: "fixedWing" },
    CP30: {
        manufacturer: "Binder (1)",
        model: "CP-301 Smaragd",
        variant: "",
        category: "fixedWing",
    },
    CP32: {
        manufacturer: "Erpals",
        model: "CP-320 Super Emeraude",
        variant: "",
        category: "fixedWing",
    },
    CP60: { manufacturer: "Piel", model: "CP-60 Diamant", variant: "", category: "fixedWing" },
    CP65: {
        manufacturer: "Porterfield",
        model: "CP-65 Collegiate",
        variant: "",
        category: "fixedWing",
    },
    CP75: { manufacturer: "Piel", model: "CP-70 Béryl", variant: "", category: "fixedWing" },
    CP80: { manufacturer: "Piel", model: "CP-80 Zéphir", variant: "", category: "fixedWing" },
    CP90: { manufacturer: "Piel", model: "CP-90 Pinocchio 2", variant: "", category: "fixedWing" },
    CPNA: { manufacturer: "Pena", model: "Capena", variant: "", category: "fixedWing" },
    CPUP: { manufacturer: "Culp", model: "Sopwith Pup", variant: "", category: "fixedWing" },
    CR10: { manufacturer: "Dyn'aero", model: "CR-100", variant: "", category: "fixedWing" },
    CRBN: { manufacturer: "Curtiss", model: "50 Robin", variant: "", category: "fixedWing" },
    CRER: { manufacturer: "Rans", model: "S-7 Courier", variant: "", category: "fixedWing" },
    CRES: { manufacturer: "New Zealand", model: "Cresco", variant: "", category: "fixedWing" },
    CRIO: { manufacturer: "Microleve", model: "Corsario", variant: "", category: "fixedWing" },
    CRJ1: {
        manufacturer: "Canadair",
        model: "CL-600 Regional Jet CRJ-100",
        variant: "",
        category: "fixedWing",
    },
    CRJ2: { manufacturer: "Bombardier", model: "CRJ", variant: "200", category: "fixedWing" },
    CRJ7: { manufacturer: "Bombardier", model: "CRJ", variant: "700", category: "fixedWing" },
    CRJ9: { manufacturer: "Bombardier", model: "CRJ", variant: "900", category: "fixedWing" },
    CRJX: {
        manufacturer: "Bombardier",
        model: "CL-600 Regional Jet CRJ-1000",
        variant: "",
        category: "fixedWing",
    },
    CRUZ: { manufacturer: "Cag (2)", model: "PS-28 Cruiser", variant: "", category: "fixedWing" },
    CT4: { manufacturer: "Aesl", model: "CT-4 Airtrainer", variant: "", category: "fixedWing" },
    CTAH: { manufacturer: "Rainbow", model: "Cheetah", variant: "", category: "fixedWing" },
    CTLN: { manufacturer: "Fly Synthesis", model: "Catalina", variant: "", category: "fixedWing" },
    CUB2: { manufacturer: "Aces High", model: "Cuby 2", variant: "", category: "fixedWing" },
    CUCA: { manufacturer: "Culver", model: "LAR Cadet", variant: "", category: "fixedWing" },
    CUDA: {
        manufacturer: "Lancair",
        model: "NLA-275 Barracuda",
        variant: "",
        category: "fixedWing",
    },
    CULP: { manufacturer: "Culp", model: "Special", variant: "", category: "fixedWing" },
    CULV: { manufacturer: "Culver", model: "V", variant: "", category: "fixedWing" },
    CULX: { manufacturer: "Fisher Aero", model: "Culex", variant: "", category: "fixedWing" },
    CVLP: { manufacturer: "Convair", model: "C-131", variant: "", category: "fixedWing" },
    CVLT: {
        manufacturer: "Canadair",
        model: "CC-109 Cosmopolitan",
        variant: "",
        category: "fixedWing",
    },
    CX5: { manufacturer: "Thatcher", model: "CX-5", variant: "", category: "fixedWing" },
    CYGT: { manufacturer: "Hawker", model: "Cygnet", variant: "", category: "fixedWing" },
    D1: { manufacturer: "Derringer", model: "New Derringer", variant: "", category: "fixedWing" },
    D11: {
        manufacturer: "Aero-Difusion",
        model: "D-112 Popuplane",
        variant: "",
        category: "fixedWing",
    },
    D139: { manufacturer: "Dorna", model: "D-139 Blue Bird", variant: "", category: "fixedWing" },
    D140: { manufacturer: "Jodel", model: "D-140 Abeille", variant: "", category: "fixedWing" },
    D150: { manufacturer: "Jodel", model: "D-150 Mascaret", variant: "", category: "fixedWing" },
    D18: { manufacturer: "Jodel", model: "D-18", variant: "", category: "fixedWing" },
    D201: {
        manufacturer: "D'apuzzo",
        model: "D-201 Sportwing",
        variant: "",
        category: "fixedWing",
    },
    D21: { manufacturer: "Fokker", model: "D-21 Replica", variant: "", category: "fixedWing" },
    D228: { manufacturer: "Dornier", model: "228", variant: "", category: "fixedWing" },
    D24: {
        manufacturer: "Albaviation",
        model: "D-24 MagicONE",
        variant: "",
        category: "fixedWing",
    },
    D25: { manufacturer: "New Standard", model: "D-25", variant: "", category: "fixedWing" },
    D250: { manufacturer: "Centre Est", model: "DR-200", variant: "", category: "fixedWing" },
    D253: {
        manufacturer: "Centre Est",
        model: "DR-253 Regent",
        variant: "",
        category: "fixedWing",
    },
    D28D: {
        manufacturer: "Dornier",
        model: "128-2 Skyservant",
        variant: "",
        category: "fixedWing",
    },
    D28T: {
        manufacturer: "Dornier",
        model: "128-6 Turbo Skyservant",
        variant: "",
        category: "fixedWing",
    },
    D31: { manufacturer: "Druine", model: "D-31 Turbulent", variant: "", category: "fixedWing" },
    D328: { manufacturer: "Dornier", model: "C-146 Wolfhound", variant: "", category: "fixedWing" },
    D39: { manufacturer: "Akaflieg Darmstadt", model: "D-39", variant: "", category: "fixedWing" },
    D4: { manufacturer: "Auster", model: "D-4", variant: "", category: "fixedWing" },
    D5: { manufacturer: "Auster", model: "D-5", variant: "", category: "fixedWing" },
    D5TU: { manufacturer: "Druine", model: "D-5 Turbi", variant: "", category: "fixedWing" },
    D6: { manufacturer: "Auster", model: "D-6", variant: "", category: "fixedWing" },
    D6CR: { manufacturer: "Druine", model: "D-60 Condor", variant: "", category: "fixedWing" },
    D7: { manufacturer: "Fokker", model: "D-7 Replica", variant: "", category: "fixedWing" },
    D8: { manufacturer: "Fokker", model: "D-8 Replica", variant: "", category: "fixedWing" },
    DA2: { manufacturer: "Davis", model: "DA-2", variant: "", category: "fixedWing" },
    DA36: { manufacturer: "Diamond", model: "DA-36 E-Star", variant: "", category: "fixedWing" },
    DA40: { manufacturer: "Diamond", model: "DA-40 Club Star", variant: "", category: "fixedWing" },
    DA42: { manufacturer: "Cetc", model: "CA-42", variant: "", category: "fixedWing" },
    DA5: { manufacturer: "Davis", model: "DA-5", variant: "", category: "fixedWing" },
    DA50: { manufacturer: "Diamond", model: "DA-50", variant: "", category: "fixedWing" },
    DA62: { manufacturer: "Diamond", model: "DA-62", variant: "", category: "fixedWing" },
    DAHU: { manufacturer: "Pena", model: "Dahu", variant: "", category: "fixedWing" },
    DAKH: { manufacturer: "Fisher", model: "Dakota Hawk", variant: "", category: "fixedWing" },
    DAL1: { manufacturer: "Spezio", model: "DAL-1 Tuholer", variant: "", category: "fixedWing" },
    DAL4: { manufacturer: "Dallach", model: "D-4 Fascination", variant: "", category: "fixedWing" },
    DAL5: { manufacturer: "Dallach", model: "D-5 Evolution", variant: "", category: "fixedWing" },
    DART: { manufacturer: "Parrish", model: "Dart", variant: "", category: "fixedWing" },
    DC10: { manufacturer: "Boeing", model: "MD-10", variant: "", category: "fixedWing" },
    DC2: { manufacturer: "Douglas", model: "DC-2", variant: "", category: "fixedWing" },
    DC3: { manufacturer: "Douglas", model: "AC-47 Skytrain", variant: "", category: "fixedWing" },
    DC3S: { manufacturer: "Douglas", model: "C-117D", variant: "", category: "fixedWing" },
    DC3T: { manufacturer: "Basler", model: "BT-67 Turbo 67", variant: "", category: "fixedWing" },
    DC4: { manufacturer: "Douglas", model: "C-54 Skymaster", variant: "", category: "fixedWing" },
    DC6: { manufacturer: "Douglas", model: "C-118 Liftmaster", variant: "", category: "fixedWing" },
    DC7: { manufacturer: "Douglas", model: "DC-7", variant: "", category: "fixedWing" },
    DC86: { manufacturer: "Douglas", model: "DC-8-60", variant: "", category: "fixedWing" },
    DC87: { manufacturer: "Douglas", model: "DC-8-70", variant: "", category: "fixedWing" },
    DC91: { manufacturer: "Douglas", model: "DC-9-10", variant: "", category: "fixedWing" },
    DC92: { manufacturer: "Douglas", model: "DC-9-20", variant: "", category: "fixedWing" },
    DC93: { manufacturer: "Douglas", model: "DC-9-30", variant: "", category: "fixedWing" },
    DC94: { manufacturer: "Douglas", model: "DC-9-40", variant: "", category: "fixedWing" },
    DC95: { manufacturer: "Douglas", model: "DC-9-50", variant: "", category: "fixedWing" },
    DDUC: { manufacturer: "Bakeng", model: "Double Duce", variant: "", category: "fixedWing" },
    DEAG: { manufacturer: "Amax", model: "Double Eagle", variant: "", category: "rotorcraft" },
    DEFI: { manufacturer: "Rutan", model: "40 Defiant", variant: "", category: "fixedWing" },
    DELF: { manufacturer: "Lyavin", model: "Delfin", variant: "", category: "fixedWing" },
    DFL6: {
        manufacturer: "Damoure-Fabre",
        model: "DFL-6 Saphir",
        variant: "",
        category: "fixedWing",
    },
    DFLY: { manufacturer: "Slipstream", model: "Dragonfly", variant: "", category: "fixedWing" },
    DG15: { manufacturer: "Howard (1)", model: "DGA-15", variant: "", category: "fixedWing" },
    DG1T: { manufacturer: "Dg Flugzeugbau", model: "DG-1000T", variant: "", category: "fixedWing" },
    DG40: { manufacturer: "Glaser-Dirks", model: "DG-400", variant: "", category: "fixedWing" },
    DG50: { manufacturer: "Glaser-Dirks", model: "DG-500M", variant: "", category: "fixedWing" },
    DG60: { manufacturer: "Glaser-Dirks", model: "DG-600M", variant: "", category: "fixedWing" },
    DG80: { manufacturer: "Dg Flugzeugbau", model: "DG-800", variant: "", category: "fixedWing" },
    DH2T: {
        manufacturer: "De Havilland Canada",
        model: "DHC-2 Mk3 Turbo Beaver",
        variant: "",
        category: "fixedWing",
    },
    DH3T: {
        manufacturer: "De Havilland Canada",
        model: "DHC-3 Turbo Otter",
        variant: "",
        category: "fixedWing",
    },
    DH4T: {
        manufacturer: "De Havilland Canada",
        model: "DHC-4 Turbo Caribou",
        variant: "",
        category: "fixedWing",
    },
    DH60: { manufacturer: "De Havilland", model: "DH-60 Moth", variant: "", category: "fixedWing" },
    DH80: {
        manufacturer: "De Havilland",
        model: "DH-80 Puss Moth",
        variant: "",
        category: "fixedWing",
    },
    DH82: {
        manufacturer: "De Havilland",
        model: "DH-82 Queen Bee",
        variant: "",
        category: "fixedWing",
    },
    DH83: {
        manufacturer: "De Havilland",
        model: "DH-83 Fox Moth",
        variant: "",
        category: "fixedWing",
    },
    DH84: {
        manufacturer: "De Havilland",
        model: "DH-84 Dragon",
        variant: "",
        category: "fixedWing",
    },
    DH85: {
        manufacturer: "De Havilland",
        model: "DH-85 Leopard Moth",
        variant: "",
        category: "fixedWing",
    },
    DH87: {
        manufacturer: "De Havilland",
        model: "DH-87 Hornet Moth",
        variant: "",
        category: "fixedWing",
    },
    DH88: {
        manufacturer: "De Havilland",
        model: "DH-88 Comet Replica",
        variant: "",
        category: "fixedWing",
    },
    DH89: {
        manufacturer: "De Havilland",
        model: "DH-89 Dragon Rapide",
        variant: "",
        category: "fixedWing",
    },
    DH8A: {
        manufacturer: "de Havilland Canada",
        model: "Dash 8",
        variant: "100",
        category: "fixedWing",
    },
    DH8B: {
        manufacturer: "de Havilland Canada",
        model: "Dash 8",
        variant: "200",
        category: "fixedWing",
    },
    DH8C: {
        manufacturer: "de Havilland Canada",
        model: "Dash 8",
        variant: "300",
        category: "fixedWing",
    },
    DH8D: {
        manufacturer: "de Havilland Canada",
        model: "Dash 8",
        variant: "400",
        category: "fixedWing",
    },
    DH90: {
        manufacturer: "De Havilland",
        model: "DH-90 Dragonfly",
        variant: "",
        category: "fixedWing",
    },
    DH94: {
        manufacturer: "De Havilland",
        model: "DH-94 Moth Minor",
        variant: "",
        category: "fixedWing",
    },
    DHA3: {
        manufacturer: "De Havilland Australia",
        model: "DHA-3 Drover",
        variant: "",
        category: "fixedWing",
    },
    DHC1: {
        manufacturer: "De Havilland",
        model: "DHC-1 Chipmunk",
        variant: "",
        category: "fixedWing",
    },
    DHC2: {
        manufacturer: "Airtech (1)",
        model: "DHC-2-PZL3S Beaver",
        variant: "",
        category: "fixedWing",
    },
    DHC3: {
        manufacturer: "Airtech (1)",
        model: "DHC-3-1000 Otter",
        variant: "",
        category: "fixedWing",
    },
    DHC4: {
        manufacturer: "De Havilland Canada",
        model: "C-7 Caribou",
        variant: "",
        category: "fixedWing",
    },
    DHC5: {
        manufacturer: "De Havilland Canada",
        model: "C-8 Buffalo",
        variant: "",
        category: "fixedWing",
    },
    DHC6: {
        manufacturer: "de Havilland Canada",
        model: "DHC-6",
        variant: "Twin Otter",
        category: "fixedWing",
    },
    DHC7: {
        manufacturer: "De Havilland Canada",
        model: "DHC-7 Dash 7",
        variant: "",
        category: "fixedWing",
    },
    DIES: { manufacturer: "Pennec-Lucas", model: "Dieselis", variant: "", category: "fixedWing" },
    DIMO: {
        manufacturer: "Diamond",
        model: "HK-36 Eco Dimona",
        variant: "",
        category: "fixedWing",
    },
    DISC: { manufacturer: "Schempp-Hirth", model: "Discus 2T", variant: "", category: "fixedWing" },
    DJIN: { manufacturer: "Sud", model: "SO-1221 Djinn", variant: "", category: "rotorcraft" },
    DLH2: {
        manufacturer: "Lange",
        model: "E-1 Antares DLR-H2",
        variant: "",
        category: "fixedWing",
    },
    DNGO: { manufacturer: "Aeroric", model: "Dingo", variant: "", category: "fixedWing" },
    DO27: { manufacturer: "Casa", model: "C-127", variant: "", category: "fixedWing" },
    DO28: { manufacturer: "Dornier", model: "Do-28A", variant: "", category: "fixedWing" },
    DOCX: {
        manufacturer: "Kovach-Elmendorf",
        model: "ES-3 Doc's RX",
        variant: "",
        category: "fixedWing",
    },
    DON: { manufacturer: "Unikomtranso", model: "Don", variant: "", category: "fixedWing" },
    DOVE: {
        manufacturer: "De Havilland",
        model: "DH-104 Dove",
        variant: "",
        category: "fixedWing",
    },
    DR1: { manufacturer: "Fokker", model: "Dr-1 Replica", variant: "", category: "fixedWing" },
    DR10: {
        manufacturer: "Centre Est",
        model: "DR-100 Ambassadeur",
        variant: "",
        category: "fixedWing",
    },
    DR22: { manufacturer: "Centre Est", model: "DR-220 2+2", variant: "", category: "fixedWing" },
    DR30: {
        manufacturer: "Centre Est",
        model: "DR-315 Petit Prince",
        variant: "",
        category: "fixedWing",
    },
    DR40: { manufacturer: "Robin", model: "DR-400 2+2", variant: "", category: "fixedWing" },
    DRAG: { manufacturer: "Crae", model: "Dragon Fly", variant: "", category: "rotorcraft" },
    DRIF: { manufacturer: "Austflight", model: "Drifter", variant: "", category: "fixedWing" },
    DRTG: {
        manufacturer: "Applegate & Weyant",
        model: "Dart GC",
        variant: "",
        category: "fixedWing",
    },
    DSA1: {
        manufacturer: "Smith (1)",
        model: "DSA-1 Miniplane",
        variant: "",
        category: "fixedWing",
    },
    DSK: { manufacturer: "Killingsworth", model: "DSK-1 Hawk", variant: "", category: "fixedWing" },
    DSLK: { manufacturer: "Driggs", model: "Skylark", variant: "", category: "fixedWing" },
    DT45: { manufacturer: "Cetc", model: "TA-20", variant: "", category: "fixedWing" },
    DTA1: { manufacturer: "Verhees", model: "D-1 Delta", variant: "", category: "fixedWing" },
    DTA2: { manufacturer: "Verhees", model: "D-2 Delta", variant: "", category: "fixedWing" },
    DUB2: { manufacturer: "Dubna", model: "2 Osa", variant: "", category: "fixedWing" },
    DUCE: { manufacturer: "Bakeng", model: "Duce", variant: "", category: "fixedWing" },
    DUOD: {
        manufacturer: "Schempp-Hirth",
        model: "Duo Discus T",
        variant: "",
        category: "fixedWing",
    },
    DUR5: { manufacturer: "Durand", model: "Mk5", variant: "", category: "fixedWing" },
    DV1: { manufacturer: "Dova", model: "DV-1 Skylark", variant: "", category: "fixedWing" },
    DV2: { manufacturer: "Dova", model: "DV-2 Infinity", variant: "", category: "fixedWing" },
    DV20: { manufacturer: "Cetc", model: "DA-20", variant: "", category: "fixedWing" },
    DW1: { manufacturer: "Bellanca", model: "DW-1 Eagle", variant: "", category: "fixedWing" },
    DWD2: { manufacturer: "Dewoitine", model: "D-26", variant: "", category: "fixedWing" },
    DYH2: { manufacturer: "Dynali", model: "H-2", variant: "", category: "rotorcraft" },
    DYH3: { manufacturer: "Dynali", model: "H-3 EasyFlyer", variant: "", category: "rotorcraft" },
    E110: {
        manufacturer: "Embraer",
        model: "C-95 Bandeirante",
        variant: "",
        category: "fixedWing",
    },
    E120: {
        manufacturer: "Embraer",
        model: "EMB-120 Brasilia",
        variant: "",
        category: "fixedWing",
    },
    E121: { manufacturer: "Embraer", model: "EC-9 Xingu", variant: "", category: "fixedWing" },
    E135: { manufacturer: "Embraer", model: "ERJ-135", variant: "", category: "fixedWing" },
    E145: { manufacturer: "Embraer", model: "C-99", variant: "", category: "fixedWing" },
    E170: { manufacturer: "Embraer", model: "E170", variant: "", category: "fixedWing" },
    E175: { manufacturer: "Embraer", model: "E175", variant: "", category: "other" },
    E190: { manufacturer: "Embraer", model: "E190", variant: "", category: "fixedWing" },
    E195: { manufacturer: "Embraer", model: "E195", variant: "", category: "fixedWing" },
    E2: { manufacturer: "Grumman", model: "E-2 Daya", variant: "", category: "fixedWing" },
    E200: { manufacturer: "Extra", model: "EA-200", variant: "", category: "fixedWing" },
    E230: { manufacturer: "Extra", model: "EA-230", variant: "", category: "fixedWing" },
    E275: { manufacturer: "Embraer", model: "ERJ-190-500", variant: "", category: "fixedWing" },
    E290: { manufacturer: "Embraer", model: "ERJ-190-300", variant: "", category: "fixedWing" },
    E295: { manufacturer: "Embraer", model: "ERJ-190-400", variant: "", category: "fixedWing" },
    E29E: { manufacturer: "Binder (2)", model: "EB-29DE", variant: "", category: "fixedWing" },
    E2CB: { manufacturer: "Taylor (1)", model: "E-2 Cub", variant: "", category: "fixedWing" },
    E300: { manufacturer: "Extra", model: "EA-300", variant: "", category: "fixedWing" },
    E314: { manufacturer: "Embraer", model: "A-29", variant: "", category: "fixedWing" },
    E350: { manufacturer: "Cessna", model: "E350", variant: "", category: "fixedWing" },
    E35L: {
        manufacturer: "Embraer",
        model: "EMB-135BJ Legacy",
        variant: "",
        category: "fixedWing",
    },
    E390: { manufacturer: "Embraer", model: "KC-390", variant: "", category: "fixedWing" },
    E3CF: {
        manufacturer: "Boeing",
        model: "E-3A (CFM56) Sentry",
        variant: "",
        category: "fixedWing",
    },
    E3TF: {
        manufacturer: "Boeing",
        model: "E-3A (TF33) Sentry",
        variant: "",
        category: "fixedWing",
    },
    E400: { manufacturer: "Extra", model: "EA-400", variant: "", category: "fixedWing" },
    E45X: { manufacturer: "Embraer", model: "EMB-145XR", variant: "", category: "fixedWing" },
    E500: { manufacturer: "Extra", model: "EA-500", variant: "", category: "fixedWing" },
    E50P: {
        manufacturer: "Embraer",
        model: "EMB-500 Phenom 100",
        variant: "",
        category: "fixedWing",
    },
    E530: { manufacturer: "Cessna", model: "E530 Scorpion", variant: "", category: "fixedWing" },
    E545: {
        manufacturer: "Embraer",
        model: "EMB-545 Legacy 450",
        variant: "",
        category: "fixedWing",
    },
    E550: {
        manufacturer: "Embraer",
        model: "EMB-550 Legacy 500",
        variant: "",
        category: "fixedWing",
    },
    E55P: {
        manufacturer: "Embraer",
        model: "EMB-505 Phenom 300",
        variant: "",
        category: "fixedWing",
    },
    E6: { manufacturer: "Boeing", model: "E-6 Mercury", variant: "", category: "fixedWing" },
    E737: {
        manufacturer: "Boeing",
        model: "737-700 Wedgetail",
        variant: "",
        category: "fixedWing",
    },
    E75L: { manufacturer: "Embraer", model: "175 (long wing)", variant: "", category: "fixedWing" },
    E75S: {
        manufacturer: "Embraer",
        model: "175 (short wing)",
        variant: "",
        category: "fixedWing",
    },
    E767: { manufacturer: "Boeing", model: "E-767", variant: "", category: "fixedWing" },
    EA40: { manufacturer: "Eclipse", model: "Eclipse 400", variant: "", category: "fixedWing" },
    EA50: { manufacturer: "Eclipse", model: "Eclipse 500", variant: "", category: "fixedWing" },
    EAEA: { manufacturer: "Grosso", model: "Easy Eagle", variant: "", category: "fixedWing" },
    EAGL: { manufacturer: "Aviat", model: "Eagle", variant: "", category: "fixedWing" },
    EAGT: { manufacturer: "Ameagle", model: "American Eaglet", variant: "", category: "fixedWing" },
    EAGX: { manufacturer: "Ctrm", model: "Eagle 150", variant: "", category: "fixedWing" },
    EB29: { manufacturer: "Binder (2)", model: "EB-29", variant: "", category: "fixedWing" },
    EBOY: { manufacturer: "Fma", model: "20 El Boyero", variant: "", category: "fixedWing" },
    EC20: {
        manufacturer: "Airbus Helicopters",
        model: "H-120 Colibri",
        variant: "",
        category: "rotorcraft",
    },
    EC25: {
        manufacturer: "Airbus Helicopters",
        model: "H-225 Cougar Mk2+",
        variant: "",
        category: "rotorcraft",
    },
    EC30: {
        manufacturer: "Airbus Helicopters",
        model: "H-130",
        variant: "",
        category: "rotorcraft",
    },
    EC35: {
        manufacturer: "Airbus Helicopters",
        model: "EC135",
        variant: "",
        category: "rotorcraft",
    },
    EC45: {
        manufacturer: "Airbus Helicopters-Kawasaki",
        model: "H-145",
        variant: "",
        category: "rotorcraft",
    },
    EC55: {
        manufacturer: "Airbus Helicopters",
        model: "H-155",
        variant: "",
        category: "rotorcraft",
    },
    EC6: { manufacturer: "Croses", model: "EC-6 Criquet", variant: "", category: "fixedWing" },
    EC75: {
        manufacturer: "Airbus Helicopters-Harbin",
        model: "EC-175",
        variant: "",
        category: "rotorcraft",
    },
    ECHO: { manufacturer: "Tecnam", model: "P-92 Echo", variant: "", category: "fixedWing" },
    EDGE: { manufacturer: "Zivko", model: "Edge 540", variant: "", category: "fixedWing" },
    EDGT: { manufacturer: "Zivko", model: "Edge 540 T", variant: "", category: "fixedWing" },
    EF2: { manufacturer: "Bye Aerospace", model: "eFlyer 2", variant: "", category: "fixedWing" },
    EFAN: { manufacturer: "Airbus", model: "E-Fan", variant: "", category: "fixedWing" },
    EFOX: { manufacturer: "Aeropro", model: "Eurofox", variant: "", category: "fixedWing" },
    EFUS: { manufacturer: "Magnus", model: "MG-11 eFusion", variant: "", category: "fixedWing" },
    EGL3: { manufacturer: "Rotorway", model: "Eagle 300T", variant: "", category: "rotorcraft" },
    EGRT: { manufacturer: "Grob", model: "D-500 Egrett 2", variant: "", category: "fixedWing" },
    EH10: {
        manufacturer: "Agustawestland",
        model: "EH-101 Merlin",
        variant: "",
        category: "rotorcraft",
    },
    EL10: {
        manufacturer: "Ela Aviacion",
        model: "ELA-10 Eclipse",
        variant: "",
        category: "rotorcraft",
    },
    EL20: { manufacturer: "Elitar", model: "IE-202", variant: "", category: "fixedWing" },
    ELA7: { manufacturer: "Ela Aviacion", model: "ELA-07", variant: "", category: "rotorcraft" },
    ELF: { manufacturer: "Parnall", model: "Elf", variant: "", category: "fixedWing" },
    ELIT: { manufacturer: "Air", model: "Epic Elite", variant: "", category: "fixedWing" },
    ELPS: { manufacturer: "Explorer (1)", model: "Ellipse", variant: "", category: "fixedWing" },
    ELSP: { manufacturer: "A2 Cz", model: "Ellipse Spirit", variant: "", category: "fixedWing" },
    ELST: { manufacturer: "Putzer", model: "Elster", variant: "", category: "fixedWing" },
    ELTO: {
        manufacturer: "Continental Copters",
        model: "El Tomcat",
        variant: "",
        category: "rotorcraft",
    },
    ELTR: { manufacturer: "Elitar", model: "IE-101 Elitar", variant: "", category: "fixedWing" },
    EM10: { manufacturer: "Marganski", model: "EM-10 Bielik", variant: "", category: "fixedWing" },
    EM11: { manufacturer: "Marganski", model: "EM-11 Orka", variant: "", category: "fixedWing" },
    EN28: { manufacturer: "Enstrom", model: "280 Shark", variant: "", category: "rotorcraft" },
    EN48: { manufacturer: "Enstrom", model: "480", variant: "", category: "rotorcraft" },
    EP9: { manufacturer: "Edgar Percival", model: "EP-9", variant: "", category: "fixedWing" },
    EPER: { manufacturer: "Epervier (1)", model: "Epervier", variant: "", category: "fixedWing" },
    EPIC: { manufacturer: "Air", model: "Epic Dynasty", variant: "", category: "fixedWing" },
    EPX1: { manufacturer: "Epervier (2)", model: "X-1", variant: "", category: "fixedWing" },
    ERAC: { manufacturer: "Dickey", model: "E-Racer", variant: "", category: "fixedWing" },
    ERCO: {
        manufacturer: "Air Products",
        model: "F-1 Aircoupe",
        variant: "",
        category: "fixedWing",
    },
    ES11: { manufacturer: "Alpi", model: "AH-130 Syton", variant: "", category: "rotorcraft" },
    ES13: {
        manufacturer: "Clifford Aeroworks",
        model: "Spad 13 80%",
        variant: "",
        category: "fixedWing",
    },
    ESCA: { manufacturer: "Air", model: "Epic Escape", variant: "", category: "fixedWing" },
    ESCP: { manufacturer: "Just", model: "Escapade", variant: "", category: "fixedWing" },
    ETA: { manufacturer: "Binder (2)", model: "Eta", variant: "", category: "fixedWing" },
    ETAR: { manufacturer: "Dassault", model: "Super Etendard", variant: "", category: "fixedWing" },
    EUFI: { manufacturer: "Airbus", model: "Eurofighter 2000", variant: "", category: "fixedWing" },
    EUPA: { manufacturer: "Europa", model: "Europa", variant: "", category: "fixedWing" },
    EURT: { manufacturer: "Fft", model: "Eurotrainer 2000", variant: "", category: "fixedWing" },
    EV55: { manufacturer: "Evektor", model: "EV-55 Outback", variant: "", category: "fixedWing" },
    EV97: { manufacturer: "Evektor", model: "EV-97 EuroStar", variant: "", category: "fixedWing" },
    EVAN: { manufacturer: "Evangel", model: "4500 Evangel", variant: "", category: "fixedWing" },
    EVIC: { manufacturer: "Air", model: "Epic Victory", variant: "", category: "fixedWing" },
    EVOP: {
        manufacturer: "Lancair",
        model: "Evolution Piston",
        variant: "",
        category: "fixedWing",
    },
    EVOT: {
        manufacturer: "Evolution",
        model: "EVOT-550 Evolution",
        variant: "",
        category: "fixedWing",
    },
    EVSS: { manufacturer: "Evektor", model: "SportStar", variant: "", category: "fixedWing" },
    EX5T: { manufacturer: "Aea", model: "Explorer 500T", variant: "", category: "fixedWing" },
    EXEC: { manufacturer: "Huzhou Taixiang", model: "Exec", variant: "", category: "rotorcraft" },
    EXEJ: { manufacturer: "Rotorway", model: "JetExec", variant: "", category: "rotorcraft" },
    EXNG: { manufacturer: "Extra", model: "NG", variant: "", category: "fixedWing" },
    EXPL: { manufacturer: "Boeing", model: "MD-902 Explorer", variant: "", category: "rotorcraft" },
    EXPR: { manufacturer: "Express", model: "90", variant: "", category: "fixedWing" },
    EZFL: { manufacturer: "Aerocomp", model: "E-Z Flyer", variant: "", category: "fixedWing" },
    EZFT: {
        manufacturer: "Blue Yonder",
        model: "Twin E-Z Flyer",
        variant: "",
        category: "fixedWing",
    },
    EZHV: { manufacturer: "Blue Yonder", model: "E-Z Harvard", variant: "", category: "fixedWing" },
    EZKC: {
        manufacturer: "Blue Yonder",
        model: "E-Z King Cobra",
        variant: "",
        category: "fixedWing",
    },
    F1: { manufacturer: "Mitsubishi", model: "F-1", variant: "", category: "fixedWing" },
    F100: { manufacturer: "Fokker", model: "100", variant: "", category: "fixedWing" },
    F104: {
        manufacturer: "Canadair",
        model: "CF-104 Starfighter",
        variant: "",
        category: "fixedWing",
    },
    F117: {
        manufacturer: "Lockheed",
        model: "F-117 Night Hawk",
        variant: "",
        category: "fixedWing",
    },
    F13: { manufacturer: "Junkers", model: "F-13 Replica", variant: "", category: "fixedWing" },
    F14: { manufacturer: "Grumman", model: "F-14 Tomcat", variant: "", category: "fixedWing" },
    F15: { manufacturer: "Boeing", model: "F-15 Eagle", variant: "", category: "fixedWing" },
    F156: { manufacturer: "Fieseler", model: "Fi-156 Storch", variant: "", category: "fixedWing" },
    F16: {
        manufacturer: "Fokker",
        model: "F-16 Fighting Falcon",
        variant: "",
        category: "fixedWing",
    },
    F18H: { manufacturer: "Asta", model: "AF-18 Hornet", variant: "", category: "fixedWing" },
    F18S: { manufacturer: "Boeing", model: "EA-18 Growler", variant: "", category: "fixedWing" },
    F1FV: { manufacturer: "Avion", model: "F-1 Favorit", variant: "", category: "fixedWing" },
    F2: { manufacturer: "Mitsubishi", model: "F-2", variant: "", category: "fixedWing" },
    F22: {
        manufacturer: "Lockheed Martin",
        model: "F-22 Raptor",
        variant: "",
        category: "fixedWing",
    },
    F260: { manufacturer: "Aermacchi", model: "F-260F", variant: "", category: "fixedWing" },
    F26T: { manufacturer: "Aermacchi", model: "SF-260T", variant: "", category: "fixedWing" },
    F27: { manufacturer: "Conair", model: "F-27 Firefighter", variant: "", category: "fixedWing" },
    F28: { manufacturer: "Fokker", model: "F-28 Fellowship", variant: "", category: "fixedWing" },
    F2TH: { manufacturer: "Dassault", model: "Falcon 2000", variant: "", category: "fixedWing" },
    F30: { manufacturer: "Golden Avio", model: "F-30 Brio", variant: "", category: "fixedWing" },
    F35: {
        manufacturer: "Lockheed Martin",
        model: "F-35A Lightning 2",
        variant: "",
        category: "fixedWing",
    },
    F3F: { manufacturer: "Grumman", model: "F3F Replica", variant: "", category: "fixedWing" },
    F4: { manufacturer: "Iai", model: "F-4 Kurnass 2000", variant: "", category: "fixedWing" },
    F402: { manufacturer: "Falcon Air", model: "Falcon 402", variant: "", category: "fixedWing" },
    F406: { manufacturer: "Cessna", model: "F406 Caravan 2", variant: "", category: "fixedWing" },
    F41E: { manufacturer: "Aerosamara", model: "F-41 El'brus", variant: "", category: "fixedWing" },
    F421: { manufacturer: "Falcon Air", model: "Falcon 421", variant: "", category: "fixedWing" },
    F5: { manufacturer: "Aidc", model: "F-5 Chung-Cheng", variant: "", category: "fixedWing" },
    F50: { manufacturer: "Fokker", model: "50", variant: "", category: "fixedWing" },
    F5SA: { manufacturer: "Iriaf", model: "S-100 Saeghe", variant: "", category: "fixedWing" },
    F60: { manufacturer: "Fokker", model: "60", variant: "", category: "fixedWing" },
    F600: {
        manufacturer: "Siai-Marchetti",
        model: "SF-600 Canguro",
        variant: "",
        category: "fixedWing",
    },
    F7: { manufacturer: "Fokker", model: "F-7 Replica", variant: "", category: "fixedWing" },
    F70: { manufacturer: "Fokker", model: "70", variant: "", category: "fixedWing" },
    F8: { manufacturer: "Chance Vought", model: "F8 Crusader", variant: "", category: "fixedWing" },
    F86: { manufacturer: "Canadair", model: "CL-13 Sabre", variant: "", category: "fixedWing" },
    F8L: { manufacturer: "Aeromere", model: "F-8L Falco", variant: "", category: "fixedWing" },
    F900: { manufacturer: "Dassault", model: "Mystère 900", variant: "", category: "fixedWing" },
    F9F: { manufacturer: "Grumman", model: "F9F Panther", variant: "", category: "fixedWing" },
    FA01: {
        manufacturer: "Fläming Air",
        model: "FA-01 Saphir",
        variant: "",
        category: "fixedWing",
    },
    FA02: {
        manufacturer: "Fläming Air",
        model: "FA-02 Smaragd VLA",
        variant: "",
        category: "fixedWing",
    },
    FA03: {
        manufacturer: "Fläming Air",
        model: "FA-03 Smaragd TMG",
        variant: "",
        category: "fixedWing",
    },
    FA04: {
        manufacturer: "Fläming Air",
        model: "FA-04 Peregrine",
        variant: "",
        category: "fixedWing",
    },
    FA10: { manufacturer: "Dassault", model: "Mystère 10", variant: "", category: "fixedWing" },
    FA11: {
        manufacturer: "Fairchild (2)",
        model: "F-11 Husky",
        variant: "",
        category: "fixedWing",
    },
    FA20: { manufacturer: "Dassault", model: "Mystère 20", variant: "", category: "fixedWing" },
    FA24: {
        manufacturer: "Fairchild (1)",
        model: "F-24 Argus",
        variant: "",
        category: "fixedWing",
    },
    FA50: { manufacturer: "Dassault", model: "Mystère 50", variant: "", category: "fixedWing" },
    FA62: {
        manufacturer: "Fairchild (1)",
        model: "M-62 Cornell",
        variant: "",
        category: "fixedWing",
    },
    FA6X: { manufacturer: "Dassault", model: "Falcon 6X", variant: "", category: "fixedWing" },
    FA7X: { manufacturer: "Dassault", model: "Falcon 7X", variant: "", category: "fixedWing" },
    FA8X: { manufacturer: "Dassault", model: "Falcon 8X", variant: "", category: "fixedWing" },
    FAET: { manufacturer: "Atec", model: "321 Faeta", variant: "", category: "fixedWing" },
    FALC: {
        manufacturer: "American Aircraft",
        model: "Falcon XP",
        variant: "",
        category: "fixedWing",
    },
    FALM: { manufacturer: "Miles", model: "M-3 Falcon Major", variant: "", category: "fixedWing" },
    FANL: { manufacturer: "Rhein", model: "FL-1 Fanliner", variant: "", category: "fixedWing" },
    FANT: { manufacturer: "Rhein", model: "Fantrainer", variant: "", category: "fixedWing" },
    FB1A: { manufacturer: "Bowers", model: "Fly Baby 1A", variant: "", category: "fixedWing" },
    FB1B: { manufacturer: "Bowers", model: "Fly Baby 1B", variant: "", category: "fixedWing" },
    FB5: { manufacturer: "Euro-Fly", model: "FB-5 Star Light", variant: "", category: "fixedWing" },
    FBA2: { manufacturer: "Found", model: "FBA-2", variant: "", category: "fixedWing" },
    FC1: { manufacturer: "Chengdu", model: "FC-1 Xiaolong", variant: "", category: "fixedWing" },
    FD2E: { manufacturer: "Flight Design", model: "F-2e", variant: "", category: "fixedWing" },
    FDCT: { manufacturer: "Flight Design", model: "CT", variant: "", category: "fixedWing" },
    FDF2: { manufacturer: "Flight Design", model: "F-2", variant: "", category: "fixedWing" },
    FDMC: { manufacturer: "Flight Design", model: "MC", variant: "", category: "fixedWing" },
    FE51: {
        manufacturer: "Fighter Escort Wings",
        model: "P-51",
        variant: "",
        category: "fixedWing",
    },
    FEST: {
        manufacturer: "Aerostar (2)",
        model: "01 Festival",
        variant: "",
        category: "fixedWing",
    },
    FFLY: { manufacturer: "Fairey", model: "Firefly", variant: "", category: "fixedWing" },
    FG01: { manufacturer: "Farigoux", model: "FG-01 Origan", variant: "", category: "fixedWing" },
    FGT: {
        manufacturer: "Midwest Aerosport",
        model: "Formula GT",
        variant: "",
        category: "fixedWing",
    },
    FH11: { manufacturer: "Fairchild (1)", model: "FH-1100", variant: "", category: "rotorcraft" },
    FIBO: {
        manufacturer: "Starfire",
        model: "Firebolt Convertible",
        variant: "",
        category: "fixedWing",
    },
    FIKD: { manufacturer: "Fike", model: "D", variant: "", category: "fixedWing" },
    FIKE: { manufacturer: "Fike", model: "E", variant: "", category: "fixedWing" },
    FINC: { manufacturer: "Fleet", model: "10", variant: "", category: "fixedWing" },
    FJ10: { manufacturer: "Aerostar (1)", model: "FJ-100", variant: "", category: "fixedWing" },
    FJR3: { manufacturer: "Fajr", model: "F-3", variant: "", category: "fixedWing" },
    FK12: {
        manufacturer: "B & F Technik",
        model: "FK-12 Comet",
        variant: "",
        category: "fixedWing",
    },
    FK14: {
        manufacturer: "B & F Technik",
        model: "FK-14 Polaris",
        variant: "",
        category: "fixedWing",
    },
    FK9: { manufacturer: "B & F Technik", model: "FK-9", variant: "", category: "fixedWing" },
    FL3: { manufacturer: "Avia (1)", model: "L-3 Aviastarlet", variant: "", category: "fixedWing" },
    FL53: { manufacturer: "Meteor", model: "FL-53", variant: "", category: "fixedWing" },
    FL54: { manufacturer: "Meteor", model: "FL-54", variant: "", category: "fixedWing" },
    FL55: { manufacturer: "Meteor", model: "FL-55", variant: "", category: "fixedWing" },
    FLAM: { manufacturer: "Dassault", model: "MD-311 Flamant", variant: "", category: "fixedWing" },
    FLCO: { manufacturer: "Finmeccanica", model: "Falco", variant: "", category: "fixedWing" },
    FLE2: { manufacturer: "Fleet", model: "2", variant: "", category: "fixedWing" },
    FLE7: { manufacturer: "Fleet", model: "7", variant: "", category: "fixedWing" },
    FLIZ: { manufacturer: "Flitzer", model: "Z-1 Flitzer", variant: "", category: "fixedWing" },
    FLSH: { manufacturer: "Euro-Fly", model: "Flash", variant: "", category: "fixedWing" },
    FLSS: { manufacturer: "Flyer", model: "Flyer SS", variant: "", category: "fixedWing" },
    FM25: {
        manufacturer: "Flying Machines",
        model: "FM-250 Vampire",
        variant: "",
        category: "fixedWing",
    },
    FMGO: { manufacturer: "Aak", model: "Flamingo", variant: "", category: "fixedWing" },
    FN33: { manufacturer: "Nardi", model: "FN-333 Riviera", variant: "", category: "fixedWing" },
    FNKB: { manufacturer: "Akron", model: "Funk B", variant: "", category: "fixedWing" },
    FOOF: {
        manufacturer: "Stewart (2)",
        model: "JD-2 Foo Fighter",
        variant: "",
        category: "fixedWing",
    },
    FORT: { manufacturer: "Fleet", model: "60 Fort", variant: "", category: "fixedWing" },
    FOUG: {
        manufacturer: "Aerospatiale",
        model: "CM-170R Magister",
        variant: "",
        category: "fixedWing",
    },
    FOX: { manufacturer: "Denney", model: "Kitfox", variant: "", category: "fixedWing" },
    FOXT: { manufacturer: "Cita", model: "Gavilán", variant: "", category: "fixedWing" },
    FRBD: {
        manufacturer: "Northrop Grumman",
        model: "R-03 Firebird",
        variant: "",
        category: "fixedWing",
    },
    FREE: {
        manufacturer: "Cabrinha",
        model: "423 Free Spirit",
        variant: "",
        category: "fixedWing",
    },
    FREL: {
        manufacturer: "Aerospatiale",
        model: "SA-321 Super Frelon",
        variant: "",
        category: "rotorcraft",
    },
    FRNT: { manufacturer: "Sky Raider", model: "Frontier", variant: "", category: "fixedWing" },
    FRON: { manufacturer: "Frontier", model: "Frontier MD-2", variant: "", category: "fixedWing" },
    FS51: {
        manufacturer: "Falconar",
        model: "SAL P-51D Mustang",
        variant: "",
        category: "fixedWing",
    },
    FT30: { manufacturer: "Nai", model: "FT-300", variant: "", category: "fixedWing" },
    FU24: {
        manufacturer: "Air Parts",
        model: "Fletcher FU-24",
        variant: "",
        category: "fixedWing",
    },
    FURY: { manufacturer: "Hawker", model: "Fury", variant: "", category: "fixedWing" },
    FUSI: { manufacturer: "Corvus", model: "CH-11 Fusion", variant: "", category: "fixedWing" },
    FW02: { manufacturer: "Flywhale", model: "FW-02 Flywhale", variant: "", category: "fixedWing" },
    FW44: { manufacturer: "Fma", model: "Fw-44 Stieglitz", variant: "", category: "fixedWing" },
    FW90: {
        manufacturer: "Flug Werk",
        model: "Fw-190 Replica",
        variant: "",
        category: "fixedWing",
    },
    FWSB: { manufacturer: "Fleetwings", model: "F-4 Sea Bird", variant: "", category: "fixedWing" },
    FX1: { manufacturer: "Innovaviation", model: "FX-1", variant: "", category: "fixedWing" },
    G1: { manufacturer: "G1 Aviation", model: "G-1", variant: "", category: "fixedWing" },
    G103: { manufacturer: "Grob", model: "G-103C Twin 3SL", variant: "", category: "fixedWing" },
    G109: { manufacturer: "Grob", model: "G-109", variant: "", category: "fixedWing" },
    G115: { manufacturer: "Grob", model: "G-115", variant: "", category: "fixedWing" },
    G120: { manufacturer: "Grob", model: "G-120", variant: "", category: "fixedWing" },
    G12T: { manufacturer: "Grob", model: "G-120TP", variant: "", category: "fixedWing" },
    G140: { manufacturer: "Grob", model: "G-140TP", variant: "", category: "fixedWing" },
    G150: {
        manufacturer: "Gulfstream Aerospace",
        model: "Gulfstream G150",
        variant: "",
        category: "fixedWing",
    },
    G159: {
        manufacturer: "Grumman",
        model: "G-159 Gulfstream 1",
        variant: "",
        category: "fixedWing",
    },
    G15T: { manufacturer: "Grob", model: "G-115T Acro", variant: "", category: "fixedWing" },
    G160: { manufacturer: "Grob", model: "G-160 Ranger", variant: "", category: "fixedWing" },
    G164: {
        manufacturer: "Aero Mod",
        model: "G-164 Super Ag Max",
        variant: "",
        category: "fixedWing",
    },
    G180: {
        manufacturer: "General Aircraft",
        model: "G1-80 Skyfarer",
        variant: "",
        category: "fixedWing",
    },
    G200: { manufacturer: "Akrotech", model: "G-200", variant: "", category: "fixedWing" },
    G202: { manufacturer: "Akrotech", model: "G-202", variant: "", category: "fixedWing" },
    G21: { manufacturer: "Grumman", model: "G-21A Goose", variant: "", category: "fixedWing" },
    G21M: { manufacturer: "Mckinnon", model: "G-21C Goose", variant: "", category: "fixedWing" },
    G21T: {
        manufacturer: "Mckinnon",
        model: "G-21E Turbo Goose",
        variant: "",
        category: "fixedWing",
    },
    G222: { manufacturer: "Aeritalia", model: "G-222", variant: "", category: "fixedWing" },
    G250: {
        manufacturer: "Gulfstream Aerospace",
        model: "Gulfstream G250",
        variant: "",
        category: "fixedWing",
    },
    G280: {
        manufacturer: "Gulfstream Aerospace",
        model: "Gulfstream G280",
        variant: "",
        category: "fixedWing",
    },
    G2CA: { manufacturer: "Guimbal", model: "G-2 Cabri", variant: "", category: "rotorcraft" },
    G2GL: { manufacturer: "Soko", model: "G-2 Galeb", variant: "", category: "fixedWing" },
    G2T1: { manufacturer: "Great Lakes", model: "2T-1 Sport", variant: "", category: "fixedWing" },
    G3: { manufacturer: "Remos", model: "G-3 Mirage", variant: "", category: "fixedWing" },
    G44: {
        manufacturer: "Gannet",
        model: "G-44 Super Widgeon",
        variant: "",
        category: "fixedWing",
    },
    G46: { manufacturer: "Fiat", model: "G-46", variant: "", category: "fixedWing" },
    G4SG: { manufacturer: "Soko", model: "G-4 Super Galeb", variant: "", category: "fixedWing" },
    G59: { manufacturer: "Fiat", model: "G-59", variant: "", category: "fixedWing" },
    G64T: {
        manufacturer: "Ag-Cat",
        model: "G-164 Super Turbine",
        variant: "",
        category: "fixedWing",
    },
    G70: { manufacturer: "Groppo", model: "G-70", variant: "", category: "fixedWing" },
    G73: { manufacturer: "Grumman", model: "G-73 Mallard", variant: "", category: "fixedWing" },
    G73T: {
        manufacturer: "Frakes",
        model: "G-73T Turbo Mallard",
        variant: "",
        category: "fixedWing",
    },
    G800: { manufacturer: "Aerodis", model: "G-802 Orion", variant: "", category: "fixedWing" },
    G91: { manufacturer: "Fiat", model: "G-91R", variant: "", category: "fixedWing" },
    G96: { manufacturer: "Grumman", model: "C-1 Trader", variant: "", category: "fixedWing" },
    G97: { manufacturer: "Sai (2)", model: "G-97 Spotter", variant: "", category: "fixedWing" },
    GA10: { manufacturer: "Gippsaero", model: "GA-10", variant: "", category: "fixedWing" },
    GA20: { manufacturer: "Gippsland", model: "GA-200 Fatman", variant: "", category: "fixedWing" },
    GA4C: {
        manufacturer: "Gulfstream Aerospace",
        model: "G-7 Gulfstream G400",
        variant: "",
        category: "fixedWing",
    },
    GA5C: {
        manufacturer: "Gulfstream Aerospace",
        model: "G-7 Gulfstream G500",
        variant: "",
        category: "fixedWing",
    },
    GA6C: {
        manufacturer: "Gulfstream Aerospace",
        model: "G-7 Gulfstream G600",
        variant: "",
        category: "fixedWing",
    },
    GA7: {
        manufacturer: "Grumman American",
        model: "GA-7 Cougar",
        variant: "",
        category: "fixedWing",
    },
    GA7C: {
        manufacturer: "Gulfstream Aerospace",
        model: "G-8 Gulfstream G700",
        variant: "",
        category: "fixedWing",
    },
    GA8: { manufacturer: "Gippsaero", model: "GA-8 Airvan", variant: "", category: "fixedWing" },
    GA8C: {
        manufacturer: "Gulfstream Aerospace",
        model: "G-8 Gulfstream G800",
        variant: "",
        category: "fixedWing",
    },
    GABR: {
        manufacturer: "Blackshape",
        model: "Bk-160 Gabriel",
        variant: "",
        category: "fixedWing",
    },
    GALX: {
        manufacturer: "Gulfstream Aerospace",
        model: "Gulfstream G200",
        variant: "",
        category: "fixedWing",
    },
    GANT: { manufacturer: "Fairey", model: "Gannet", variant: "", category: "fixedWing" },
    GAUN: { manufacturer: "Gloster", model: "Gauntlet", variant: "", category: "fixedWing" },
    GAVI: {
        manufacturer: "Aero Mercantil",
        model: "358 Gavilan",
        variant: "",
        category: "fixedWing",
    },
    GAZL: { manufacturer: "Abhco", model: "SA-342 Gazelle", variant: "", category: "rotorcraft" },
    GB1: {
        manufacturer: "Game Composites",
        model: "GB-1 GameBird",
        variant: "",
        category: "fixedWing",
    },
    GBSP: { manufacturer: "Beets", model: "GB Special", variant: "", category: "fixedWing" },
    GC1: { manufacturer: "Globe", model: "GC-1 Swift", variant: "", category: "fixedWing" },
    GDUK: { manufacturer: "Ellison-Mahon", model: "Gweduck", variant: "", category: "fixedWing" },
    GEMI: { manufacturer: "Miles", model: "M-65 Gemini", variant: "", category: "fixedWing" },
    GENI: { manufacturer: "Ifb", model: "E-Genius", variant: "", category: "fixedWing" },
    GEPE: { manufacturer: "Aero Services", model: "Guêpe", variant: "", category: "fixedWing" },
    GJ11: {
        manufacturer: "Hongdu",
        model: "GJ-11 Sharp Sword",
        variant: "",
        category: "fixedWing",
    },
    GL5T: {
        manufacturer: "Bombardier",
        model: "BD-700 Global 5500",
        variant: "",
        category: "fixedWing",
    },
    GL7T: {
        manufacturer: "Bombardier",
        model: "BD-700 Global 7000",
        variant: "",
        category: "fixedWing",
    },
    GLAD: { manufacturer: "Gloster", model: "Gladiator", variant: "", category: "fixedWing" },
    GLAS: { manufacturer: "Glasair", model: "Glasair", variant: "", category: "fixedWing" },
    GLEX: {
        manufacturer: "Bombardier",
        model: "BD-700 Global 6500",
        variant: "",
        category: "fixedWing",
    },
    GLF2: {
        manufacturer: "Grumman",
        model: "C-20J Gulfstream 2SP",
        variant: "",
        category: "fixedWing",
    },
    GLF3: {
        manufacturer: "Gulfstream Aerospace",
        model: "C-20A Gulfstream 3",
        variant: "",
        category: "fixedWing",
    },
    GLF4: {
        manufacturer: "Gulfstream Aerospace",
        model: "C-20F Gulfstream 4",
        variant: "",
        category: "fixedWing",
    },
    GLF5: { manufacturer: "Gulfstream", model: "G-V", variant: "", category: "fixedWing" },
    GLF6: { manufacturer: "Gulfstream", model: "G650", variant: "", category: "fixedWing" },
    GLID: { manufacturer: "Generic", model: "Glider", variant: "", category: "glider" },
    GLSP: {
        manufacturer: "New Glastar",
        model: "Sportsman 2+2",
        variant: "",
        category: "fixedWing",
    },
    GLST: { manufacturer: "Glasair", model: "GlaStar", variant: "", category: "fixedWing" },
    GLTU: {
        manufacturer: "Stoddard-Hamilton",
        model: "Turbine Glasair",
        variant: "",
        category: "fixedWing",
    },
    GM17: { manufacturer: "Intracom", model: "GM-17 Viper", variant: "", category: "fixedWing" },
    GMGC: { manufacturer: "Ibis (2)", model: "Grand Magic", variant: "", category: "fixedWing" },
    GNAT: { manufacturer: "Folland", model: "Fo-144 Gnat", variant: "", category: "fixedWing" },
    GOBU: {
        manufacturer: "Butterfly",
        model: "Golden Butterfly",
        variant: "",
        category: "rotorcraft",
    },
    GOLF: { manufacturer: "Tecnam", model: "P-96 Golf", variant: "", category: "fixedWing" },
    GOOS: { manufacturer: "Quikkit", model: "Glass Goose", variant: "", category: "fixedWing" },
    GP1: {
        manufacturer: "Jihlavan",
        model: "Skyleader GP One",
        variant: "",
        category: "fixedWing",
    },
    GP3: { manufacturer: "Osprey", model: "GP-3 Osprey 2", variant: "", category: "fixedWing" },
    GP4: { manufacturer: "Osprey", model: "GP-4", variant: "", category: "fixedWing" },
    GPRO: { manufacturer: "Genevation", model: "GenPro", variant: "", category: "fixedWing" },
    GR51: { manufacturer: "Cameron", model: "P-51G Grand 51", variant: "", category: "fixedWing" },
    GRFN: { manufacturer: "Griffon Aero", model: "Griffon", variant: "", category: "fixedWing" },
    GRIF: { manufacturer: "Canada Air Rv", model: "Griffin", variant: "", category: "fixedWing" },
    GRIZ: {
        manufacturer: "Aerotek (2)",
        model: "Turbo Grizzly",
        variant: "",
        category: "fixedWing",
    },
    GSIS: { manufacturer: "Innovation", model: "Genesis", variant: "", category: "fixedWing" },
    GSPN: {
        manufacturer: "Grob",
        model: "G-180 SPn Utility Jet",
        variant: "",
        category: "fixedWing",
    },
    GUEP: { manufacturer: "Aero Services", model: "Guepard", variant: "", category: "fixedWing" },
    GURI: { manufacturer: "Aeromot", model: "AMT-600 Guri", variant: "", category: "fixedWing" },
    GX: { manufacturer: "Remos", model: "GX", variant: "", category: "fixedWing" },
    GY10: { manufacturer: "Gardan", model: "GY-100 Bagheera", variant: "", category: "fixedWing" },
    GY20: { manufacturer: "Cab", model: "GY-20 Minicab", variant: "", category: "fixedWing" },
    GY30: { manufacturer: "Cab", model: "GY-30 Supercab", variant: "", category: "fixedWing" },
    GY80: { manufacturer: "Gardan", model: "GY-80 Horizon", variant: "", category: "fixedWing" },
    GYRO: { manufacturer: "Generic", model: "Gyrocopter", variant: "", category: "rotorcraft" },
    H111: { manufacturer: "Casa", model: "2-111", variant: "", category: "fixedWing" },
    H12T: { manufacturer: "Hiller", model: "UH-12E3T", variant: "", category: "rotorcraft" },
    H160: {
        manufacturer: "Airbus Helicopters",
        model: "H-160",
        variant: "",
        category: "rotorcraft",
    },
    H2: { manufacturer: "Kaman", model: "K-20 Seasprite", variant: "", category: "rotorcraft" },
    H202: {
        manufacturer: "Hb-Aircraft",
        model: "HB-202 Fledger",
        variant: "",
        category: "fixedWing",
    },
    H207: {
        manufacturer: "Hb-Flugtechnik",
        model: "HB-207 Alfa",
        variant: "",
        category: "fixedWing",
    },
    H21: { manufacturer: "Vertol", model: "42 Work Horse", variant: "", category: "rotorcraft" },
    H25A: {
        manufacturer: "Hawker Siddeley",
        model: "HS-125-1",
        variant: "",
        category: "fixedWing",
    },
    H25B: {
        manufacturer: "British Aerospace",
        model: "BAe-125-700",
        variant: "",
        category: "fixedWing",
    },
    H25C: {
        manufacturer: "British Aerospace",
        model: "BAe-125-1000",
        variant: "",
        category: "fixedWing",
    },
    H269: { manufacturer: "Bredanardi", model: "NH-300", variant: "", category: "rotorcraft" },
    H40: { manufacturer: "Hoffmann", model: "H-40", variant: "", category: "fixedWing" },
    H43A: { manufacturer: "Kaman", model: "H-43A", variant: "", category: "rotorcraft" },
    H43B: { manufacturer: "Kaman", model: "HH-43B Huskie", variant: "", category: "rotorcraft" },
    H46: { manufacturer: "Boeing Vertol", model: "107", variant: "", category: "rotorcraft" },
    H47: {
        manufacturer: "Agustawestland",
        model: "ICH-47 Chinook",
        variant: "",
        category: "rotorcraft",
    },
    H500: { manufacturer: "Agusta", model: "NH-500", variant: "", category: "rotorcraft" },
    H53: {
        manufacturer: "Sikorsky",
        model: "CH-53A Sea Stallion",
        variant: "",
        category: "rotorcraft",
    },
    H53S: {
        manufacturer: "Sikorsky",
        model: "CH-53K King Stallion",
        variant: "",
        category: "rotorcraft",
    },
    H60: { manufacturer: "Asta", model: "S-70 Seahawk", variant: "", category: "rotorcraft" },
    H64: {
        manufacturer: "Boeing",
        model: "AH-64 Longbow Apache",
        variant: "",
        category: "rotorcraft",
    },
    HA2: { manufacturer: "Hollmann", model: "HA-2 Sportster", variant: "", category: "rotorcraft" },
    HA31: { manufacturer: "Hindustan", model: "HA-31 Basant", variant: "", category: "fixedWing" },
    HA4T: {
        manufacturer: "Hawker Beechcraft",
        model: "4000 Hawker 4000",
        variant: "",
        category: "fixedWing",
    },
    HAHU: {
        manufacturer: "Sindlinger",
        model: "HH-1 Hawker Hurricane",
        variant: "",
        category: "fixedWing",
    },
    HANS: { manufacturer: "Nal", model: "Hansa", variant: "", category: "fixedWing" },
    HAR: { manufacturer: "Alenia", model: "AV-8 Harrier", variant: "", category: "fixedWing" },
    HAW3: { manufacturer: "Groen", model: "H2X Hawk 3", variant: "", category: "rotorcraft" },
    HAWK: { manufacturer: "Bae Systems", model: "CT-155 Hawk", variant: "", category: "fixedWing" },
    HB21: {
        manufacturer: "Brditschka",
        model: "HB-21 Hobbylifter",
        variant: "",
        category: "fixedWing",
    },
    HB23: {
        manufacturer: "Brditschka",
        model: "HB-23 Hobbyliner",
        variant: "",
        category: "fixedWing",
    },
    HB3: { manufacturer: "Brditschka", model: "HB-3", variant: "", category: "fixedWing" },
    HCAT: { manufacturer: "Grumman", model: "F6F Hellcat", variant: "", category: "fixedWing" },
    HD34: { manufacturer: "Hurel-Dubois", model: "HD-34", variant: "", category: "fixedWing" },
    HDJT: { manufacturer: "Honda", model: "HA-420 HondaJet", variant: "", category: "fixedWing" },
    HEAD: {
        manufacturer: "Stewart (2)",
        model: "JD-1 Headwind",
        variant: "",
        category: "fixedWing",
    },
    HERN: {
        manufacturer: "De Havilland",
        model: "DH-114 Heron",
        variant: "",
        category: "fixedWing",
    },
    HF20: { manufacturer: "Hfb", model: "HFB-320 Hansa", variant: "", category: "fixedWing" },
    HI27: { manufacturer: "Hirth", model: "Hi-27 Acrostar", variant: "", category: "fixedWing" },
    HIGH: { manufacturer: "Just", model: "JA20 Highlander", variant: "", category: "fixedWing" },
    HIND: { manufacturer: "Hawker", model: "Hind", variant: "", category: "fixedWing" },
    HL2: { manufacturer: "Lambach", model: "HL-2 Replica", variant: "", category: "fixedWing" },
    HLC4: {
        manufacturer: "Halberstadt",
        model: "CL-4 Replica",
        variant: "",
        category: "fixedWing",
    },
    HLD4: { manufacturer: "Halberstadt", model: "D-4 Replica", variant: "", category: "fixedWing" },
    HM38: { manufacturer: "Falconar", model: "HM-380 Ladybug", variant: "", category: "fixedWing" },
    HN70: {
        manufacturer: "Nicollier",
        model: "HN-700 Menestrel 2",
        variant: "",
        category: "fixedWing",
    },
    HORN: { manufacturer: "Wallerkowski", model: "Hornisse", variant: "", category: "fixedWing" },
    HORZ: { manufacturer: "Fisher", model: "Horizon", variant: "", category: "fixedWing" },
    HR10: { manufacturer: "Robin", model: "HR-100 President", variant: "", category: "fixedWing" },
    HR20: { manufacturer: "Robin", model: "HR-200", variant: "", category: "fixedWing" },
    HRM9: { manufacturer: "Elbit", model: "Hermes 900", variant: "", category: "fixedWing" },
    HRNT: { manufacturer: "Aak", model: "Hornet", variant: "", category: "fixedWing" },
    HROC: { manufacturer: "Harmon", model: "Rocket", variant: "", category: "fixedWing" },
    HRON: { manufacturer: "Iai", model: "Heron", variant: "", category: "fixedWing" },
    HSMT: { manufacturer: "Rotorsmart", model: "HeliSmart", variant: "", category: "rotorcraft" },
    HT16: { manufacturer: "Hindustan", model: "HJT-16 Kiran", variant: "", category: "fixedWing" },
    HT2: { manufacturer: "Hindustan", model: "HT-2", variant: "", category: "fixedWing" },
    HT36: { manufacturer: "Hindustan", model: "HJT-36 Sitara", variant: "", category: "fixedWing" },
    HT40: { manufacturer: "Hindustan", model: "HTT-40", variant: "", category: "fixedWing" },
    HU1: {
        manufacturer: "Shenyang Sailplane",
        model: "HU-1 Seagull",
        variant: "",
        category: "fixedWing",
    },
    HU2: {
        manufacturer: "Shenyang Sailplane",
        model: "HU-2 Petrel",
        variant: "",
        category: "fixedWing",
    },
    HUCO: { manufacturer: "Bell", model: "209 HueyCobra", variant: "", category: "rotorcraft" },
    HUML: { manufacturer: "Hummel", model: "Hummel Bird", variant: "", category: "fixedWing" },
    HUNT: { manufacturer: "Hawker", model: "Hunter", variant: "", category: "fixedWing" },
    HURI: { manufacturer: "Hawker", model: "Hurricane", variant: "", category: "fixedWing" },
    HURK: { manufacturer: "Tai", model: "TT-32 Hürkuş", variant: "", category: "fixedWing" },
    HUSK: { manufacturer: "Aviat", model: "A-1 Husky", variant: "", category: "fixedWing" },
    HW4P: { manufacturer: "Groen", model: "Hawk 4", variant: "", category: "rotorcraft" },
    HW4T: { manufacturer: "Groen", model: "Jet Hawk 4T", variant: "", category: "rotorcraft" },
    HX2: { manufacturer: "Helowerks", model: "HX-2 Wasp", variant: "", category: "rotorcraft" },
    HYPR: { manufacturer: "P&m Aviation", model: "HypR", variant: "", category: "fixedWing" },
    I103: { manufacturer: "Ilyushin", model: "Il-103", variant: "", category: "fixedWing" },
    I112: { manufacturer: "Ilyushin", model: "Il-112", variant: "", category: "fixedWing" },
    I114: { manufacturer: "Ilyushin", model: "Il-114", variant: "", category: "fixedWing" },
    I115: { manufacturer: "Aisa", model: "I-115", variant: "", category: "fixedWing" },
    I11B: { manufacturer: "Aisa", model: "I-11B Peque", variant: "", category: "fixedWing" },
    I153: { manufacturer: "Polikarpov", model: "I-153", variant: "", category: "fixedWing" },
    I15B: { manufacturer: "Polikarpov", model: "I-15bis", variant: "", category: "fixedWing" },
    I16: { manufacturer: "Polikarpov", model: "I-16", variant: "", category: "fixedWing" },
    I22: {
        manufacturer: "Instytut Lotnictwa",
        model: "I-22 Iryda",
        variant: "",
        category: "fixedWing",
    },
    I23: {
        manufacturer: "Instytut Lotnictwa",
        model: "I-23 Manager",
        variant: "",
        category: "fixedWing",
    },
    I3: { manufacturer: "Interavia", model: "I-3", variant: "", category: "fixedWing" },
    I66: {
        manufacturer: "Iannotta",
        model: "I-66 San Francesco",
        variant: "",
        category: "fixedWing",
    },
    IA46: { manufacturer: "Dinfia", model: "IA-46 Ranquel", variant: "", category: "fixedWing" },
    IA58: { manufacturer: "Fma", model: "IA-58 Pucará", variant: "", category: "fixedWing" },
    IA63: { manufacturer: "Fma", model: "IA-63 Pampa", variant: "", category: "fixedWing" },
    IFUR: { manufacturer: "Isaacs", model: "Fury", variant: "", category: "fixedWing" },
    IL14: { manufacturer: "Avia (2)", model: "Av-14", variant: "", category: "fixedWing" },
    IL18: { manufacturer: "Ilyushin", model: "Il-18", variant: "", category: "fixedWing" },
    IL28: { manufacturer: "Harbin", model: "H-5", variant: "", category: "fixedWing" },
    IL38: { manufacturer: "Ilyushin", model: "Il-38", variant: "", category: "fixedWing" },
    IL62: { manufacturer: "Ilyushin", model: "Il-62", variant: "", category: "fixedWing" },
    IL76: { manufacturer: "Ilyushin", model: "Il-76", variant: "", category: "fixedWing" },
    IL86: { manufacturer: "Ilyushin", model: "Il-86", variant: "", category: "fixedWing" },
    IL96: { manufacturer: "Ilyushin", model: "Il-96", variant: "", category: "fixedWing" },
    IMPU: { manufacturer: "Impulse", model: "Impulse", variant: "", category: "fixedWing" },
    INCQ: { manufacturer: "Inpaer", model: "Conquest", variant: "", category: "fixedWing" },
    INEC: { manufacturer: "Inpaer", model: "Excel", variant: "", category: "fixedWing" },
    INEX: { manufacturer: "Inpaer", model: "Explorer", variant: "", category: "fixedWing" },
    INTG: { manufacturer: "Aura Aero", model: "Integral R", variant: "", category: "fixedWing" },
    ION: { manufacturer: "Ion", model: "Ion", variant: "", category: "fixedWing" },
    IP06: { manufacturer: "Ipe", model: "IPE-06 Curucaca", variant: "", category: "fixedWing" },
    IP10: { manufacturer: "Ipe", model: "IPE-010", variant: "", category: "fixedWing" },
    IP26: { manufacturer: "Ipai", model: "IPAI-26 Tuca", variant: "", category: "fixedWing" },
    IP6A: { manufacturer: "Ipe", model: "IPE-06A", variant: "", category: "fixedWing" },
    IPAN: { manufacturer: "Embraer", model: "EMB-200 Ipanema", variant: "", category: "fixedWing" },
    IR22: { manufacturer: "Ica", model: "IAR-826", variant: "", category: "fixedWing" },
    IR23: { manufacturer: "Ica", model: "IAR-823", variant: "", category: "fixedWing" },
    IR24: { manufacturer: "Ica", model: "IAR-824", variant: "", category: "fixedWing" },
    IR25: { manufacturer: "Ica", model: "IAR-825TP Triumf", variant: "", category: "fixedWing" },
    IR27: { manufacturer: "Bucuresti", model: "IAR-827 Dacic", variant: "", category: "fixedWing" },
    IR28: { manufacturer: "Ica", model: "IS-28MA", variant: "", category: "fixedWing" },
    IR46: { manufacturer: "Iar", model: "IAR-46 Katty", variant: "", category: "fixedWing" },
    IR99: { manufacturer: "Avioane", model: "IAR-99 Soim", variant: "", category: "fixedWing" },
    IRBS: { manufacturer: "Kubicek", model: "M-4 Irbis", variant: "", category: "fixedWing" },
    IS2: { manufacturer: "Instytut Lotnictwa", model: "IS-2", variant: "", category: "rotorcraft" },
    IS28: { manufacturer: "Iar", model: "IS-28M2", variant: "", category: "fixedWing" },
    ISAT: { manufacturer: "Aerojames", model: "01 Isatis", variant: "", category: "fixedWing" },
    ISPT: { manufacturer: "Isaacs", model: "Spitfire", variant: "", category: "fixedWing" },
    J1: { manufacturer: "Auster", model: "J-1 Aiglet", variant: "", category: "fixedWing" },
    J10: { manufacturer: "Chengdu", model: "F-10", variant: "", category: "fixedWing" },
    J177: {
        manufacturer: "Aerodynos",
        model: "JA-177 Evolution",
        variant: "",
        category: "fixedWing",
    },
    J2: { manufacturer: "Piper", model: "J-2 Cub", variant: "", category: "fixedWing" },
    J20: { manufacturer: "Chengdu", model: "J-20", variant: "", category: "fixedWing" },
    J3: {
        manufacturer: "American Legend",
        model: "AL-3C Legend Cub",
        variant: "",
        category: "fixedWing",
    },
    J300: { manufacturer: "Alms", model: "J-300 Joker", variant: "", category: "fixedWing" },
    J328: {
        manufacturer: "328 Support Services",
        model: "Dornier 328JET",
        variant: "",
        category: "fixedWing",
    },
    J4: { manufacturer: "Piper", model: "J-4 Cub Coupe", variant: "", category: "fixedWing" },
    J400: {
        manufacturer: "Jihlavan",
        model: "JA-400 Skyleader 400",
        variant: "",
        category: "fixedWing",
    },
    J40E: {
        manufacturer: "Zall Jihlavan",
        model: "JA-400 Skyleader 400 Electric",
        variant: "",
        category: "fixedWing",
    },
    J4B2: { manufacturer: "Barnett", model: "J4B2", variant: "", category: "rotorcraft" },
    J5: { manufacturer: "Piper", model: "AE Cub Cruiser", variant: "", category: "fixedWing" },
    J600: {
        manufacturer: "Acs (2)",
        model: "JA-600 Skyleader 600",
        variant: "",
        category: "fixedWing",
    },
    J8A: { manufacturer: "Shenyang", model: "JZ-8", variant: "", category: "fixedWing" },
    J8B: { manufacturer: "Shenyang", model: "JZ-8F", variant: "", category: "fixedWing" },
    JAB2: { manufacturer: "Jabiru", model: "Jabiru J160", variant: "", category: "fixedWing" },
    JAB4: { manufacturer: "Jabiru", model: "Jabiru J200", variant: "", category: "fixedWing" },
    JABI: { manufacturer: "Jabiru", model: "Jabiru LSA", variant: "", category: "fixedWing" },
    JACE: { manufacturer: "Ace", model: "Junior Ace", variant: "", category: "fixedWing" },
    JAG2: { manufacturer: "Jag Helicopter", model: "JAG", variant: "", category: "rotorcraft" },
    JAGR: { manufacturer: "Hindustan", model: "Jaguar", variant: "", category: "fixedWing" },
    JAJ5: { manufacturer: "Alpha", model: "J-5 Marco", variant: "", category: "fixedWing" },
    JAJ6: { manufacturer: "J & As", model: "J-6 Fregata", variant: "", category: "fixedWing" },
    JANU: { manufacturer: "Schempp-Hirth", model: "Janus BM", variant: "", category: "fixedWing" },
    JARO: {
        manufacturer: "Jackaroo",
        model: "Thruxton Jackaroo",
        variant: "",
        category: "fixedWing",
    },
    JAS4: { manufacturer: "Joby", model: "JAS-4", variant: "", category: "other" },
    JAST: { manufacturer: "Soko", model: "IJ-21 Jastreb", variant: "", category: "fixedWing" },
    JB15: { manufacturer: "Oberlerchner", model: "JOB-15", variant: "", category: "fixedWing" },
    JC01: { manufacturer: "Coupe", model: "JC-01", variant: "", category: "fixedWing" },
    JC02: { manufacturer: "Coupe", model: "JC-02", variant: "", category: "fixedWing" },
    JCOM: {
        manufacturer: "Aero Commander",
        model: "1121 Jet Commander",
        variant: "",
        category: "fixedWing",
    },
    JCRU: { manufacturer: "Aasi", model: "Jetcruzer", variant: "", category: "fixedWing" },
    JD2: { manufacturer: "Dyke", model: "JD-2 Delta", variant: "", category: "fixedWing" },
    JDOE: {
        manufacturer: "American Homebuilts",
        model: "John Doe",
        variant: "",
        category: "fixedWing",
    },
    JE2: { manufacturer: "Eich", model: "JE-2 Gyroplane", variant: "", category: "rotorcraft" },
    JFOX: { manufacturer: "Airo", model: "1", variant: "", category: "fixedWing" },
    JH7: {
        manufacturer: "Xian",
        model: "FBC-1 Flying Leopard",
        variant: "",
        category: "fixedWing",
    },
    JK05: { manufacturer: "Ekolot", model: "JK-05 Beetle", variant: "", category: "fixedWing" },
    JL9: {
        manufacturer: "Guizhou",
        model: "FTC-2000 Shanying",
        variant: "",
        category: "fixedWing",
    },
    JN76: {
        manufacturer: "Caudron",
        model: "CR-760 Cyclone Replica",
        variant: "",
        category: "fixedWing",
    },
    JPM1: { manufacturer: "Marie", model: "JPM-01 Medoc", variant: "", category: "fixedWing" },
    JPRO: { manufacturer: "Bac", model: "145 Jet Provost", variant: "", category: "fixedWing" },
    JRC1: { manufacturer: "Chalard", model: "JRC-01 Julcar", variant: "", category: "fixedWing" },
    JRO: { manufacturer: "Dta", model: "J-RO", variant: "", category: "rotorcraft" },
    JS1J: {
        manufacturer: "Jonker",
        model: "JS-1 Revelation (jet)",
        variant: "",
        category: "fixedWing",
    },
    JS2J: {
        manufacturer: "Jonker",
        model: "JS-2 Revenant (jet)",
        variant: "",
        category: "fixedWing",
    },
    JS2P: {
        manufacturer: "Jonker",
        model: "JS-2 Revenant (piston)",
        variant: "",
        category: "fixedWing",
    },
    JS3: { manufacturer: "Century", model: "Jetstream 3", variant: "", category: "fixedWing" },
    JS31: {
        manufacturer: "British Aerospace",
        model: "BAe-3100 Jetstream 31",
        variant: "",
        category: "fixedWing",
    },
    JS32: {
        manufacturer: "British Aerospace",
        model: "BAe-3200 Jetstream Super 31",
        variant: "",
        category: "fixedWing",
    },
    JS3E: {
        manufacturer: "Jonker",
        model: "JS-3 Rapture (electric)",
        variant: "",
        category: "fixedWing",
    },
    JS3J: {
        manufacturer: "Jonker",
        model: "JS-3 Rapture (jet)",
        variant: "",
        category: "fixedWing",
    },
    JS41: {
        manufacturer: "Ai(r)",
        model: "BAe-4100 Jetstream 41",
        variant: "",
        category: "fixedWing",
    },
    JSX: { manufacturer: "Sonex", model: "JSX SubSonex", variant: "", category: "fixedWing" },
    JT2: { manufacturer: "Taylor (4)", model: "JT-2 Titch", variant: "", category: "fixedWing" },
    JU52: { manufacturer: "Casa", model: "352L", variant: "", category: "fixedWing" },
    JUN1: {
        manufacturer: "Kaminskas",
        model: "RK-1 Jungster 1",
        variant: "",
        category: "fixedWing",
    },
    JUN2: {
        manufacturer: "Kaminskas",
        model: "RK-2 Jungster 2",
        variant: "",
        category: "fixedWing",
    },
    JUNR: { manufacturer: "Andreasson", model: "BA-7", variant: "", category: "fixedWing" },
    JUPI: { manufacturer: "Lammer Geyer", model: "Jupiter", variant: "", category: "fixedWing" },
    K100: { manufacturer: "Daher", model: "Kodiak 100", variant: "", category: "fixedWing" },
    K126: { manufacturer: "Ica", model: "Ka-126", variant: "", category: "rotorcraft" },
    K209: { manufacturer: "Fama", model: "K-209 KISS", variant: "", category: "rotorcraft" },
    K226: { manufacturer: "Kamov", model: "Ka-226 Sergei", variant: "", category: "rotorcraft" },
    K35E: {
        manufacturer: "Boeing",
        model: "KC-135E Stratotanker",
        variant: "",
        category: "fixedWing",
    },
    K35R: {
        manufacturer: "Boeing",
        model: "C-135FR Stratotanker",
        variant: "",
        category: "fixedWing",
    },
    K50: {
        manufacturer: "Korea Aerospace",
        model: "A-50 Golden Eagle",
        variant: "",
        category: "fixedWing",
    },
    K51: { manufacturer: "Kovacs", model: "K-51 Peregrino", variant: "", category: "fixedWing" },
    K8: { manufacturer: "Hongdu", model: "K-8 Karakorum", variant: "", category: "fixedWing" },
    K900: { manufacturer: "Daher", model: "Kodiak 200", variant: "", category: "fixedWing" },
    KA25: { manufacturer: "Kamov", model: "Ka-25", variant: "", category: "rotorcraft" },
    KA26: { manufacturer: "Kamov", model: "Ka-26", variant: "", category: "rotorcraft" },
    KA27: { manufacturer: "Kamov", model: "Ka-27", variant: "", category: "rotorcraft" },
    KA50: {
        manufacturer: "Kamov",
        model: "Ka-50 Black Shark",
        variant: "",
        category: "rotorcraft",
    },
    KA52: { manufacturer: "Kamov", model: "Ka-52 Alligator", variant: "", category: "rotorcraft" },
    KA62: { manufacturer: "Kamov", model: "Ka-60 Kasatka", variant: "", category: "rotorcraft" },
    KAAN: { manufacturer: "Tai", model: "TF Kaan", variant: "", category: "fixedWing" },
    KAK1: { manufacturer: "Kieger", model: "AK-1", variant: "", category: "fixedWing" },
    KAK2: { manufacturer: "Kieger", model: "AK-2", variant: "", category: "fixedWing" },
    KAK3: { manufacturer: "Junkers Profly", model: "Junka", variant: "", category: "fixedWing" },
    KAT3: { manufacturer: "Khrunichev", model: "AT-3", variant: "", category: "fixedWing" },
    KATB: { manufacturer: "Kari", model: "Twinbee", variant: "", category: "fixedWing" },
    KATR: { manufacturer: "Aerosamara", model: "Katran", variant: "", category: "fixedWing" },
    KC2: { manufacturer: "Kawasaki", model: "XC-2", variant: "", category: "fixedWing" },
    KE3: { manufacturer: "Boeing", model: "KE-3", variant: "", category: "fixedWing" },
    KEHA: { manufacturer: "Kestrel", model: "Float Hawk", variant: "", category: "fixedWing" },
    KELA: { manufacturer: "Keleher", model: "Lark", variant: "", category: "fixedWing" },
    KELD: { manufacturer: "Kelly", model: "D", variant: "", category: "fixedWing" },
    KERO: { manufacturer: "Rearwin", model: "2000 Ken-Royce", variant: "", category: "fixedWing" },
    KEST: { manufacturer: "Farnborough", model: "F-1 Kestrel", variant: "", category: "fixedWing" },
    KF21: {
        manufacturer: "Korea Aerospace",
        model: "KF-21 Boramae",
        variant: "",
        category: "fixedWing",
    },
    KFAB: {
        manufacturer: "Kitplanes For Africa",
        model: "Bushbaby",
        variant: "",
        category: "fixedWing",
    },
    KFAS: {
        manufacturer: "Kitplanes For Africa",
        model: "Safari",
        variant: "",
        category: "fixedWing",
    },
    KFIR: { manufacturer: "Iai", model: "Kfir", variant: "", category: "fixedWing" },
    KFIS: {
        manufacturer: "Anderson",
        model: "EA-1 Kingfisher",
        variant: "",
        category: "fixedWing",
    },
    KH4: { manufacturer: "Kawasaki", model: "KH-4", variant: "", category: "rotorcraft" },
    KIS2: { manufacturer: "Pulsar", model: "Sport 150", variant: "", category: "fixedWing" },
    KIS4: { manufacturer: "Pulsar", model: "Super Cruiser", variant: "", category: "fixedWing" },
    KITH: { manufacturer: "Bourdon", model: "Kitty Hawk", variant: "", category: "fixedWing" },
    KITI: {
        manufacturer: "Mitchell-Procter",
        model: "Kittiwake",
        variant: "",
        category: "fixedWing",
    },
    KIWI: { manufacturer: "Twi", model: "Kiwi", variant: "", category: "fixedWing" },
    KK60: { manufacturer: "Kari-Keen", model: "60 Coupe", variant: "", category: "fixedWing" },
    KL07: { manufacturer: "Bolkow", model: "F-207", variant: "", category: "fixedWing" },
    KL10: {
        manufacturer: "Flight Design-Vessel",
        model: "KLA-100",
        variant: "",
        category: "fixedWing",
    },
    KL25: { manufacturer: "Klemm", model: "L-25", variant: "", category: "fixedWing" },
    KL35: { manufacturer: "Klemm", model: "Kl-35", variant: "", category: "fixedWing" },
    KLBR: { manufacturer: "Kolb", model: "King Kolbra", variant: "", category: "fixedWing" },
    KM2: { manufacturer: "Fuji", model: "KM-2", variant: "", category: "fixedWing" },
    KMAX: { manufacturer: "Kaman", model: "K-1200 K-Max", variant: "", category: "rotorcraft" },
    KNTW: { manufacturer: "Payne", model: "Knight Twister", variant: "", category: "fixedWing" },
    KOLL: { manufacturer: "Kolb", model: "Laser", variant: "", category: "fixedWing" },
    KP2: { manufacturer: "Jihlavan", model: "KP-2 Rapid 200", variant: "", category: "fixedWing" },
    KP5: { manufacturer: "Jihlavan", model: "KP-5 Rapid 500", variant: "", category: "fixedWing" },
    KR1: { manufacturer: "Rand", model: "KR-1", variant: "", category: "fixedWing" },
    KR2: { manufacturer: "Rand", model: "KR-2", variant: "", category: "fixedWing" },
    KR21: { manufacturer: "Fairchild (1)", model: "KR-21", variant: "", category: "fixedWing" },
    KR30: { manufacturer: "Ekolot", model: "KR-030 Topaz", variant: "", category: "fixedWing" },
    KR31: { manufacturer: "Fairchild (1)", model: "KR-31", variant: "", category: "fixedWing" },
    KR34: { manufacturer: "Fairchild (1)", model: "KR-34", variant: "", category: "fixedWing" },
    KRAG: { manufacturer: "Soko", model: "J-20 Kraguj", variant: "", category: "fixedWing" },
    KRAH: { manufacturer: "Rock", model: "Krähe", variant: "", category: "fixedWing" },
    KRIC: { manufacturer: "Flsz", model: "DK-1 Der Kricket", variant: "", category: "fixedWing" },
    KSTK: {
        manufacturer: "Phoenix-Aviacor",
        model: "M-12 Kasatik",
        variant: "",
        category: "fixedWing",
    },
    KT1: { manufacturer: "Daewoo", model: "KT-1 Woong-Bee", variant: "", category: "fixedWing" },
    KTOO: { manufacturer: "Amax", model: "J-6 Karatoo", variant: "", category: "fixedWing" },
    KZ2: { manufacturer: "Sai (1)", model: "KZ-2 Kupe", variant: "", category: "fixedWing" },
    KZ3: { manufacturer: "Sai (1)", model: "KZ-3", variant: "", category: "fixedWing" },
    KZ4: { manufacturer: "Sai (1)", model: "KZ-4", variant: "", category: "fixedWing" },
    KZ7: { manufacturer: "Sai (1)", model: "KZ-7 Laerke", variant: "", category: "fixedWing" },
    KZ8: { manufacturer: "Sai (1)", model: "KZ-8", variant: "", category: "fixedWing" },
    KZLA: {
        manufacturer: "Baykar",
        model: "Bayraktar Kızılelma",
        variant: "",
        category: "fixedWing",
    },
    L10: { manufacturer: "Lockheed", model: "L-10 Electra", variant: "", category: "fixedWing" },
    L101: { manufacturer: "Lockheed", model: "L-1011 TriStar", variant: "", category: "fixedWing" },
    L11: { manufacturer: "Luscombe", model: "11A Sedan", variant: "", category: "fixedWing" },
    L11E: { manufacturer: "Luscombe", model: "11E", variant: "", category: "fixedWing" },
    L12: {
        manufacturer: "Lockheed",
        model: "C-40 Electra Junior",
        variant: "",
        category: "fixedWing",
    },
    L13: { manufacturer: "Convair", model: "L-13", variant: "", category: "fixedWing" },
    L13M: {
        manufacturer: "Let",
        model: "L-13 Blanik (single-engine conversions)",
        variant: "",
        category: "fixedWing",
    },
    L13S: {
        manufacturer: "Aerotechnik",
        model: "L-13S Super Vivat",
        variant: "",
        category: "fixedWing",
    },
    L14: { manufacturer: "Lockheed", model: "L-14 Hudson", variant: "", category: "fixedWing" },
    L15: { manufacturer: "Hongdu", model: "L-15 Lieying", variant: "", category: "fixedWing" },
    L159: { manufacturer: "Aero (2)", model: "L-159", variant: "", category: "fixedWing" },
    L18: { manufacturer: "Howard (2)", model: "250", variant: "", category: "fixedWing" },
    L181: {
        manufacturer: "Associated Air",
        model: "Liberty 181",
        variant: "",
        category: "fixedWing",
    },
    L188: { manufacturer: "Lockheed", model: "L-188 Electra", variant: "", category: "fixedWing" },
    L200: { manufacturer: "Let", model: "L-200 Morava", variant: "", category: "fixedWing" },
    L29: { manufacturer: "Aero (2)", model: "L-29 Delfin", variant: "", category: "fixedWing" },
    L29A: {
        manufacturer: "Lockheed",
        model: "L-1329 Jetstar 6",
        variant: "",
        category: "fixedWing",
    },
    L29B: {
        manufacturer: "Lockheed",
        model: "L-1329 Jetstar 2",
        variant: "",
        category: "fixedWing",
    },
    L37: { manufacturer: "Howard (2)", model: "500", variant: "", category: "fixedWing" },
    L380: { manufacturer: "Lederlin", model: "380 Ladybug", variant: "", category: "fixedWing" },
    L39: { manufacturer: "Aero (2)", model: "L-39 Albatros", variant: "", category: "fixedWing" },
    L4: { manufacturer: "Chaika", model: "L-4", variant: "", category: "fixedWing" },
    L40: {
        manufacturer: "Orličan (1)",
        model: "L-40 Meta Sokol",
        variant: "",
        category: "fixedWing",
    },
    L410: {
        manufacturer: "Aircraft Industries",
        model: "L-410 Turbolet",
        variant: "",
        category: "fixedWing",
    },
    L5: { manufacturer: "Stinson", model: "L-5 Sentinel", variant: "", category: "fixedWing" },
    L59: { manufacturer: "Aero (2)", model: "L-59", variant: "", category: "fixedWing" },
    L6: { manufacturer: "Aerovolga", model: "L-6", variant: "", category: "fixedWing" },
    L60: { manufacturer: "Aero (2)", model: "L-60 Brigadyr", variant: "", category: "fixedWing" },
    L610: { manufacturer: "Let", model: "L-610", variant: "", category: "fixedWing" },
    L70: { manufacturer: "Valmet", model: "L-70 Miltrainer", variant: "", category: "fixedWing" },
    L8: { manufacturer: "Luscombe", model: "8", variant: "", category: "fixedWing" },
    L90: { manufacturer: "Aermacchi", model: "M-290TP Redigo", variant: "", category: "fixedWing" },
    LA25: { manufacturer: "Lake", model: "LA-250 Renegade", variant: "", category: "fixedWing" },
    LA4: { manufacturer: "Lake", model: "LA-4", variant: "", category: "fixedWing" },
    LA60: { manufacturer: "Aermacchi", model: "AL-60", variant: "", category: "fixedWing" },
    LA6T: {
        manufacturer: "Aermacchi",
        model: "AL-60 Turbine Grizzly",
        variant: "",
        category: "fixedWing",
    },
    LA8: { manufacturer: "Aerovolga", model: "LA-8 Flagman", variant: "", category: "fixedWing" },
    LACO: { manufacturer: "Laven", model: "LACO-125", variant: "", category: "fixedWing" },
    LAE1: { manufacturer: "Lange", model: "E-1 Antares 20E", variant: "", category: "fixedWing" },
    LAKR: { manufacturer: "Laser", model: "Akro Z", variant: "", category: "fixedWing" },
    LAKX: { manufacturer: "Aeroplastika", model: "LAK-X", variant: "", category: "fixedWing" },
    LAMA: {
        manufacturer: "Aerospatiale",
        model: "SA-315 Lama",
        variant: "",
        category: "rotorcraft",
    },
    LANC: { manufacturer: "Avro", model: "683 Lancaster", variant: "", category: "fixedWing" },
    LAR1: { manufacturer: "Flaris", model: "LAR-1", variant: "", category: "fixedWing" },
    LARK: {
        manufacturer: "North American Rockwell",
        model: "100 Lark Commander",
        variant: "",
        category: "fixedWing",
    },
    LAST: { manufacturer: "Utva", model: "N-63 Lasta", variant: "", category: "fixedWing" },
    LBUG: {
        manufacturer: "Lightning Bug",
        model: "Lightning Bug",
        variant: "",
        category: "fixedWing",
    },
    LCA: { manufacturer: "Ada", model: "LCA Tejas", variant: "", category: "fixedWing" },
    LCB: { manufacturer: "Laird", model: "LC-B Commercial", variant: "", category: "fixedWing" },
    LCH: { manufacturer: "Hindustan", model: "LCH", variant: "", category: "rotorcraft" },
    LCR: { manufacturer: "Laird", model: "LC-R Speedwing", variant: "", category: "fixedWing" },
    LEG2: { manufacturer: "Lancair", model: "Legacy", variant: "", category: "fixedWing" },
    LEGD: { manufacturer: "Performance", model: "Legend", variant: "", category: "fixedWing" },
    LESP: { manufacturer: "Lancair", model: "Lancair ES-P", variant: "", category: "fixedWing" },
    LEV2: { manufacturer: "Tapanee", model: "Levitation 2", variant: "", category: "fixedWing" },
    LEV4: { manufacturer: "Tapanee", model: "Levitation 4", variant: "", category: "fixedWing" },
    LGEZ: { manufacturer: "Rutan", model: "61 Long-EZ", variant: "", category: "fixedWing" },
    LGND: { manufacturer: "Aeropilot", model: "Legend", variant: "", category: "fixedWing" },
    LH10: {
        manufacturer: "Lh Aviation",
        model: "LH-10 Ellipse",
        variant: "",
        category: "fixedWing",
    },
    LIBE: { manufacturer: "Liberty (1)", model: "Bellaire", variant: "", category: "fixedWing" },
    LION: { manufacturer: "Griffon", model: "Lionheart", variant: "", category: "fixedWing" },
    LJ23: { manufacturer: "Lear Jet", model: "23", variant: "", category: "fixedWing" },
    LJ24: { manufacturer: "Gates Learjet", model: "24", variant: "", category: "fixedWing" },
    LJ25: { manufacturer: "Gates Learjet", model: "25", variant: "", category: "fixedWing" },
    LJ28: { manufacturer: "Gates Learjet", model: "28", variant: "", category: "fixedWing" },
    LJ31: { manufacturer: "Gates Learjet", model: "31", variant: "", category: "fixedWing" },
    LJ35: { manufacturer: "Gates Learjet", model: "35", variant: "", category: "fixedWing" },
    LJ40: { manufacturer: "Learjet", model: "40", variant: "", category: "fixedWing" },
    LJ45: { manufacturer: "Learjet", model: "45", variant: "", category: "fixedWing" },
    LJ55: { manufacturer: "Gates Learjet", model: "55", variant: "", category: "fixedWing" },
    LJ60: { manufacturer: "Learjet", model: "60", variant: "", category: "fixedWing" },
    LJ70: { manufacturer: "Learjet", model: "70", variant: "", category: "fixedWing" },
    LJ75: { manufacturer: "Learjet", model: "75", variant: "", category: "fixedWing" },
    LK17: { manufacturer: "Lak", model: "LAK-17AT", variant: "", category: "fixedWing" },
    LK19: { manufacturer: "Lak", model: "LAK-19T", variant: "", category: "fixedWing" },
    LK20: { manufacturer: "Lak", model: "LAK-20M", variant: "", category: "fixedWing" },
    LK7E: { manufacturer: "Lak", model: "LAK-17 FES", variant: "", category: "fixedWing" },
    LM5: { manufacturer: "Lombardi", model: "LM-5 Aviastar", variant: "", category: "fixedWing" },
    LM5X: {
        manufacturer: "Light Miniature",
        model: "LM-5 Super Cub",
        variant: "",
        category: "fixedWing",
    },
    LM7: { manufacturer: "Lombardi", model: "LM-7", variant: "", category: "fixedWing" },
    LMC1: { manufacturer: "Chasle", model: "LMC-1 Sprintair", variant: "", category: "fixedWing" },
    LMK1: { manufacturer: "Cata", model: "LMK-1 Oryx", variant: "", category: "fixedWing" },
    LN27: {
        manufacturer: "Falcomposite",
        model: "LN-27 Furio",
        variant: "",
        category: "fixedWing",
    },
    LN3: {
        manufacturer: "Flygfabriken",
        model: "LN-3 Seagull",
        variant: "",
        category: "fixedWing",
    },
    LNC2: { manufacturer: "Lancair", model: "Lancair 200", variant: "", category: "fixedWing" },
    LNC4: { manufacturer: "Lancair", model: "Lancair 4", variant: "", category: "fixedWing" },
    LNCE: { manufacturer: "Lancair", model: "Lancair ES", variant: "", category: "fixedWing" },
    LNP4: {
        manufacturer: "Lancair",
        model: "Lancair PropJet 4",
        variant: "",
        category: "fixedWing",
    },
    LNT4: { manufacturer: "Lancair", model: "Sentry 4T", variant: "", category: "fixedWing" },
    LOCA: { manufacturer: "Aerolab", model: "LoCamp", variant: "", category: "fixedWing" },
    LOVE: { manufacturer: "Loving-Wayne", model: "WR-1 Love", variant: "", category: "fixedWing" },
    LP1: { manufacturer: "Lopresti", model: "LP-1 Fury", variant: "", category: "fixedWing" },
    LR2T: { manufacturer: "Load Ranger", model: "2000", variant: "", category: "rotorcraft" },
    LS10: { manufacturer: "Dg Flugzeugbau", model: "LS-10ST", variant: "", category: "fixedWing" },
    LS2: { manufacturer: "Hat", model: "LS-2", variant: "", category: "fixedWing" },
    LS8: { manufacturer: "Dg Flugzeugbau", model: "LS-8ST", variant: "", category: "fixedWing" },
    LS9: { manufacturer: "Rolladen-Schneider", model: "LS-9", variant: "", category: "fixedWing" },
    LSTR: { manufacturer: "Custom Flight", model: "Lite Star", variant: "", category: "fixedWing" },
    LTNG: { manufacturer: "Bac", model: "Lightning", variant: "", category: "fixedWing" },
    LUL5: { manufacturer: "Lucas", model: "L-5", variant: "", category: "fixedWing" },
    LUL6: { manufacturer: "Lucas", model: "L-6", variant: "", category: "fixedWing" },
    LUL7: { manufacturer: "Lucas", model: "L-7", variant: "", category: "fixedWing" },
    LUL8: { manufacturer: "Lucas", model: "L-8", variant: "", category: "fixedWing" },
    LW20: {
        manufacturer: "Howard Hughes",
        model: "Australian LightWing Speed SP-2000",
        variant: "",
        category: "fixedWing",
    },
    LW40: {
        manufacturer: "Howard Hughes",
        model: "Australian LightWing Speed SP-4000",
        variant: "",
        category: "fixedWing",
    },
    LWIN: {
        manufacturer: "Howard Hughes",
        model: "Australian LightWing GR-912",
        variant: "",
        category: "fixedWing",
    },
    LX32: { manufacturer: "Lilienthal", model: "X-32 Bekas", variant: "", category: "fixedWing" },
    LX34: { manufacturer: "Lilienthal", model: "X-34 Bekas", variant: "", category: "fixedWing" },
    LXR: { manufacturer: "Elixir", model: "Elixir", variant: "", category: "fixedWing" },
    LYNX: {
        manufacturer: "Agustawestland",
        model: "WG-13 Super Lynx",
        variant: "",
        category: "rotorcraft",
    },
    LYSA: { manufacturer: "National Steel", model: "Lysander", variant: "", category: "fixedWing" },
    M10: { manufacturer: "Aerostar (1)", model: "90", variant: "", category: "fixedWing" },
    M101: {
        manufacturer: "Myasishchev",
        model: "M-101 Expedition",
        variant: "",
        category: "fixedWing",
    },
    M106: { manufacturer: "Lambert", model: "M-106 Mission", variant: "", category: "fixedWing" },
    M108: { manufacturer: "Lambert", model: "M-108 Mission", variant: "", category: "fixedWing" },
    M110: { manufacturer: "Aviat", model: "110 Special", variant: "", category: "fixedWing" },
    M15: {
        manufacturer: "Pzl-Mielec",
        model: "M-15 Belphegor",
        variant: "",
        category: "fixedWing",
    },
    M17: {
        manufacturer: "Myasishchev",
        model: "M-17 Stratosfera",
        variant: "",
        category: "fixedWing",
    },
    M18: { manufacturer: "Aii", model: "AVA-303", variant: "", category: "fixedWing" },
    M18T: {
        manufacturer: "Melex",
        model: "T-45 Turbine Dromader",
        variant: "",
        category: "fixedWing",
    },
    M2: { manufacturer: "Kubicek", model: "M-2 Scout", variant: "", category: "fixedWing" },
    M200: {
        manufacturer: "Aero Commander",
        model: "200 Commander 200",
        variant: "",
        category: "fixedWing",
    },
    M20P: { manufacturer: "Aerostar (1)", model: "200", variant: "", category: "fixedWing" },
    M20T: { manufacturer: "Mooney", model: "M-20K 231", variant: "", category: "fixedWing" },
    M21: {
        manufacturer: "Pzl-Mielec",
        model: "M-21 Dromader Mini",
        variant: "",
        category: "fixedWing",
    },
    M212: { manufacturer: "Lambert", model: "M-212 Mission", variant: "", category: "fixedWing" },
    M22: { manufacturer: "Mooney", model: "M-22", variant: "", category: "fixedWing" },
    M24: {
        manufacturer: "Pzl-Mielec",
        model: "M-24 Dromader Super",
        variant: "",
        category: "fixedWing",
    },
    M26: { manufacturer: "Pzl-Mielec", model: "M-26 Air Wolf", variant: "", category: "fixedWing" },
    M28: { manufacturer: "Pzl-Mielec", model: "M-28 Skytruck", variant: "", category: "fixedWing" },
    M2HK: { manufacturer: "Miles", model: "M-2 Hawk Major", variant: "", category: "fixedWing" },
    M308: { manufacturer: "German Bianco", model: "MB-308", variant: "", category: "fixedWing" },
    M326: { manufacturer: "Aermacchi", model: "MB-326", variant: "", category: "fixedWing" },
    M339: { manufacturer: "Aermacchi", model: "MB-339", variant: "", category: "fixedWing" },
    M345: { manufacturer: "Leonardo", model: "M-345", variant: "", category: "fixedWing" },
    M346: { manufacturer: "Aermacchi", model: "M-346 Master", variant: "", category: "fixedWing" },
    M360: {
        manufacturer: "Aircraft Technologies",
        model: "Meyer-360",
        variant: "",
        category: "fixedWing",
    },
    M36J: { manufacturer: "Matra", model: "M-360 Jupiter", variant: "", category: "fixedWing" },
    M4: { manufacturer: "Maule", model: "M-4", variant: "", category: "fixedWing" },
    M404: { manufacturer: "Martin", model: "404", variant: "", category: "fixedWing" },
    M5: { manufacturer: "Maule", model: "M-5", variant: "", category: "fixedWing" },
    M55: {
        manufacturer: "Myasishchev",
        model: "M-55 Geophysica",
        variant: "",
        category: "fixedWing",
    },
    M6: { manufacturer: "Maule", model: "M-6 Super Rocket", variant: "", category: "fixedWing" },
    M600: { manufacturer: "Piper", model: "PA-46-600TP M600", variant: "", category: "fixedWing" },
    M7: { manufacturer: "Maule", model: "M-7-235 Orion", variant: "", category: "fixedWing" },
    M700: {
        manufacturer: "Piper",
        model: "PA-46-701TP M700 Fury",
        variant: "",
        category: "fixedWing",
    },
    M74: {
        manufacturer: "Texas Helicopter",
        model: "M-74 Wasp",
        variant: "",
        category: "rotorcraft",
    },
    M7T: { manufacturer: "Maule", model: "M-7-420", variant: "", category: "fixedWing" },
    M8: { manufacturer: "Maule", model: "M-8", variant: "", category: "fixedWing" },
    M9: { manufacturer: "Maule", model: "M-9", variant: "", category: "fixedWing" },
    MA1: { manufacturer: "Emair", model: "MA-1 Paymaster", variant: "", category: "fixedWing" },
    MA5: { manufacturer: "Marquart", model: "MA-5 Charger", variant: "", category: "fixedWing" },
    MA60: { manufacturer: "Xian", model: "MA-60", variant: "", category: "fixedWing" },
    MA6H: { manufacturer: "Xian", model: "MA-60H", variant: "", category: "fixedWing" },
    MAGC: { manufacturer: "Eagle Aviation", model: "EA-100", variant: "", category: "fixedWing" },
    MAGI: { manufacturer: "Miles", model: "M-14 Magister", variant: "", category: "fixedWing" },
    MAGN: { manufacturer: "Airdale", model: "Magnum", variant: "", category: "fixedWing" },
    MAJR: { manufacturer: "Falconar", model: "Cubmajor", variant: "", category: "fixedWing" },
    MAKO: { manufacturer: "Lancair", model: "NLA-450 Mako", variant: "", category: "fixedWing" },
    MAMB: { manufacturer: "Melbourne", model: "MA-2 Mamba", variant: "", category: "fixedWing" },
    MAME: { manufacturer: "Aerocomp", model: "Merlin", variant: "", category: "fixedWing" },
    MARS: { manufacturer: "Martin", model: "170 Mars", variant: "", category: "fixedWing" },
    MAVR: { manufacturer: "Aea", model: "Maverick", variant: "", category: "fixedWing" },
    MB70: { manufacturer: "Brochet", model: "MB-72", variant: "", category: "fixedWing" },
    MB80: { manufacturer: "Brochet", model: "MB-80", variant: "", category: "fixedWing" },
    MC01: { manufacturer: "Montaer", model: "MC-01", variant: "", category: "fixedWing" },
    MC10: { manufacturer: "Colomban", model: "MC-10 Cricri", variant: "", category: "fixedWing" },
    MC23: { manufacturer: "Irkut", model: "MC-21-300", variant: "", category: "fixedWing" },
    MC45: { manufacturer: "Meyers", model: "MAC-145", variant: "", category: "fixedWing" },
    MC90: { manufacturer: "Monocoupe", model: "90", variant: "", category: "fixedWing" },
    MCOU: { manufacturer: "Chris Tena", model: "Mini Coupe", variant: "", category: "fixedWing" },
    MCOY: { manufacturer: "Montana", model: "Coyote", variant: "", category: "fixedWing" },
    MCR1: { manufacturer: "Colomban", model: "MC-100 Ban-Bi", variant: "", category: "fixedWing" },
    MCR4: {
        manufacturer: "Dyn'aero",
        model: "Lafayette 4 Revolution",
        variant: "",
        category: "fixedWing",
    },
    MCRR: { manufacturer: "Dyn'aero", model: "MCR-R180", variant: "", category: "fixedWing" },
    MCUL: { manufacturer: "Kimball", model: "McCullocoupe", variant: "", category: "fixedWing" },
    MD11: { manufacturer: "Boeing", model: "MD-11", variant: "", category: "fixedWing" },
    MD3: {
        manufacturer: "Datwyler",
        model: "MD-3 Swiss Trainer",
        variant: "",
        category: "fixedWing",
    },
    MD3R: { manufacturer: "Flyitalia", model: "MD-3 Rider", variant: "", category: "fixedWing" },
    MD52: { manufacturer: "Boeing", model: "MD-520N", variant: "", category: "rotorcraft" },
    MD60: { manufacturer: "Boeing", model: "MD-600N", variant: "", category: "rotorcraft" },
    MD81: { manufacturer: "Boeing", model: "MD-81", variant: "", category: "fixedWing" },
    MD82: { manufacturer: "Boeing", model: "MD-82", variant: "", category: "fixedWing" },
    MD83: { manufacturer: "Boeing", model: "MD-83", variant: "", category: "fixedWing" },
    MD87: { manufacturer: "Boeing", model: "MD-87", variant: "", category: "fixedWing" },
    MD88: { manufacturer: "Boeing", model: "MD-88", variant: "", category: "fixedWing" },
    MD90: { manufacturer: "Boeing", model: "MD-90", variant: "", category: "fixedWing" },
    ME08: {
        manufacturer: "Messerschmitt",
        model: "Bf-108 Taifun",
        variant: "",
        category: "fixedWing",
    },
    ME09: { manufacturer: "Hispano", model: "HA-1112 Buchon", variant: "", category: "fixedWing" },
    ME62: { manufacturer: "Legend Flyers", model: "Me-262", variant: "", category: "fixedWing" },
    MEAD: { manufacturer: "Mead", model: "Adventure", variant: "", category: "fixedWing" },
    MEL2: { manufacturer: "Garrison", model: "Melmoth 2", variant: "", category: "fixedWing" },
    MERK: { manufacturer: "Aviaton", model: "Merkury", variant: "", category: "fixedWing" },
    MESS: { manufacturer: "Miles", model: "M-38 Messenger", variant: "", category: "fixedWing" },
    METR: {
        manufacturer: "Armstrong Whitworth",
        model: "Meteor",
        variant: "",
        category: "fixedWing",
    },
    MF17: { manufacturer: "Pakistan", model: "Mushshak", variant: "", category: "fixedWing" },
    MG15: { manufacturer: "Aero (2)", model: "CS-102", variant: "", category: "fixedWing" },
    MG17: { manufacturer: "Chengdu", model: "FT-5", variant: "", category: "fixedWing" },
    MG19: { manufacturer: "Guizhou", model: "F-6", variant: "", category: "fixedWing" },
    MG21: {
        manufacturer: "Aerostar (2)",
        model: "MiG-21 Lancer",
        variant: "",
        category: "fixedWing",
    },
    MG23: {
        manufacturer: "Hindustan",
        model: "MiG-27 Bahadur",
        variant: "",
        category: "fixedWing",
    },
    MG25: { manufacturer: "Mikoyan", model: "MiG-25", variant: "", category: "fixedWing" },
    MG29: { manufacturer: "Mapo", model: "MiG-29", variant: "", category: "fixedWing" },
    MG31: { manufacturer: "Mapo", model: "MiG-31", variant: "", category: "fixedWing" },
    MG44: { manufacturer: "Mikoyan", model: "MiG 1-44", variant: "", category: "fixedWing" },
    MGIC: { manufacturer: "Kaiser", model: "Magic", variant: "", category: "fixedWing" },
    MGNM: {
        manufacturer: "Aviation Enterprises",
        model: "Magnum",
        variant: "",
        category: "fixedWing",
    },
    MGUL: { manufacturer: "Percival", model: "E Mew Gull", variant: "", category: "fixedWing" },
    MH20: { manufacturer: "Mitsubishi", model: "MH-2000", variant: "", category: "rotorcraft" },
    MH46: { manufacturer: "Aerosette", model: "MH-46 Eclipse", variant: "", category: "fixedWing" },
    MI10: { manufacturer: "Mil", model: "Mi-10", variant: "", category: "rotorcraft" },
    MI14: { manufacturer: "Mil", model: "Mi-14", variant: "", category: "rotorcraft" },
    MI2: { manufacturer: "Mil", model: "Mi-2", variant: "", category: "rotorcraft" },
    MI24: { manufacturer: "Mil", model: "AH-2 Sabre", variant: "", category: "rotorcraft" },
    MI26: { manufacturer: "Mil", model: "Mi-26", variant: "", category: "rotorcraft" },
    MI28: { manufacturer: "Mil", model: "Mi-28", variant: "", category: "rotorcraft" },
    MI34: { manufacturer: "Mil", model: "Mi-34", variant: "", category: "rotorcraft" },
    MI38: { manufacturer: "Mil", model: "Mi-38", variant: "", category: "rotorcraft" },
    MI4: { manufacturer: "Harbin", model: "Z-5", variant: "", category: "rotorcraft" },
    MI6: { manufacturer: "Mil", model: "Mi-6", variant: "", category: "rotorcraft" },
    MI8: { manufacturer: "Mil", model: "Mi-8", variant: "", category: "rotorcraft" },
    MIDR: { manufacturer: "Acba", model: "ACBA-8 Midour 2", variant: "", category: "fixedWing" },
    MIMP: { manufacturer: "Aerocar", model: "Mini-Imp", variant: "", category: "fixedWing" },
    MIMU: {
        manufacturer: "Bushby",
        model: "MM-1 Midget Mustang",
        variant: "",
        category: "fixedWing",
    },
    MIR2: { manufacturer: "Dassault", model: "Mirage 2000", variant: "", category: "fixedWing" },
    MIRA: { manufacturer: "Atlas", model: "Cheetah", variant: "", category: "fixedWing" },
    MITE: { manufacturer: "Mooney", model: "M-18 Mite", variant: "", category: "fixedWing" },
    MJ10: { manufacturer: "Jurca", model: "MJ-10 Spit", variant: "", category: "fixedWing" },
    MJ12: { manufacturer: "Jurca", model: "MJ-12 P-40", variant: "", category: "fixedWing" },
    MJ1H: {
        manufacturer: "Jurca",
        model: "MJ-100 Spitfire Replica",
        variant: "",
        category: "fixedWing",
    },
    MJ2: { manufacturer: "Jurca", model: "MJ-2 Tempête", variant: "", category: "fixedWing" },
    MJ4: { manufacturer: "Jurca", model: "MJ-4 Shadow", variant: "", category: "fixedWing" },
    MJ5: { manufacturer: "Jurca", model: "MJ-5 Sirocco", variant: "", category: "fixedWing" },
    MJ51: { manufacturer: "Jurca", model: "MJ-51 Spérocco", variant: "", category: "fixedWing" },
    MJ53: { manufacturer: "Jurca", model: "MJ-53 Autan", variant: "", category: "fixedWing" },
    MJ7: { manufacturer: "Boeve", model: "P-51 Mustang", variant: "", category: "fixedWing" },
    MJ77: { manufacturer: "Jurca", model: "MJ-77 Gnatsum", variant: "", category: "fixedWing" },
    MJ8: { manufacturer: "Jurca", model: "MJ-8 1-Nine-0", variant: "", category: "fixedWing" },
    MJ80: {
        manufacturer: "Jurca",
        model: "MJ-80 Focke-Wulf Fw-190 Replica",
        variant: "",
        category: "fixedWing",
    },
    MJ9: { manufacturer: "Jurca", model: "MJ-9", variant: "", category: "fixedWing" },
    MJ90: {
        manufacturer: "Jurca",
        model: "MJ-90 Messerschmitt Bf-109 Replica",
        variant: "",
        category: "fixedWing",
    },
    MLER: { manufacturer: "Alanne", model: "Moottori-Lerche", variant: "", category: "fixedWing" },
    MM14: { manufacturer: "Magni", model: "M-14 Scout", variant: "", category: "rotorcraft" },
    MM16: {
        manufacturer: "Magni",
        model: "M-16 Tandem Trainer",
        variant: "",
        category: "rotorcraft",
    },
    MM19: { manufacturer: "Magni", model: "M-19 Shark", variant: "", category: "rotorcraft" },
    MM21: { manufacturer: "Magni", model: "M-21", variant: "", category: "rotorcraft" },
    MM22: { manufacturer: "Magni", model: "M-22 Voyager", variant: "", category: "rotorcraft" },
    MM24: { manufacturer: "Magni", model: "M-24 Orion", variant: "", category: "rotorcraft" },
    MMAX: { manufacturer: "Mad Max Aero", model: "Mad Max", variant: "", category: "rotorcraft" },
    MMUT: { manufacturer: "Mara Wing", model: "1-L Malamut", variant: "", category: "fixedWing" },
    MNEX: {
        manufacturer: "Norman",
        model: "Nordic 8 Mini Explorer",
        variant: "",
        category: "fixedWing",
    },
    MOCU: { manufacturer: "Culp", model: "MonoCulp", variant: "", category: "fixedWing" },
    MOGO: { manufacturer: "Kinetic", model: "Mountain Goat", variant: "", category: "fixedWing" },
    MOL1: { manufacturer: "Molniya", model: "1", variant: "", category: "fixedWing" },
    MON2: { manufacturer: "Montanair", model: "2180 Spirit", variant: "", category: "fixedWing" },
    MONA: { manufacturer: "Miles", model: "M-17 Monarch", variant: "", category: "fixedWing" },
    MONI: { manufacturer: "Monnett", model: "Moni", variant: "", category: "fixedWing" },
    MOR2: { manufacturer: "Bishop", model: "AeroTec 2150", variant: "", category: "fixedWing" },
    MOSP: { manufacturer: "Mong", model: "Sport", variant: "", category: "fixedWing" },
    MOSQ: {
        manufacturer: "De Havilland",
        model: "DH-98 Mosquito",
        variant: "",
        category: "fixedWing",
    },
    MOTO: { manufacturer: "Humbert", model: "Moto Du Ciel", variant: "", category: "fixedWing" },
    MP02: { manufacturer: "Aero-Kros", model: "MP-02 Czajka", variant: "", category: "fixedWing" },
    MP20: { manufacturer: "Plan", model: "MP-205 Busard", variant: "", category: "fixedWing" },
    MR25: {
        manufacturer: "Murphy",
        model: "SR-2500 Super Rebel",
        variant: "",
        category: "fixedWing",
    },
    MR35: { manufacturer: "Murphy", model: "SR-3500 Moose", variant: "", category: "fixedWing" },
    MR3T: { manufacturer: "Murphy", model: "SR-3500 T-Moose", variant: "", category: "fixedWing" },
    MRAI: { manufacturer: "Monnett", model: "Monerai P", variant: "", category: "fixedWing" },
    MRF1: { manufacturer: "Dassault", model: "Mirage F1", variant: "", category: "fixedWing" },
    MRMD: { manufacturer: "Csa", model: "Mermaid", variant: "", category: "fixedWing" },
    MRTN: { manufacturer: "Mirage", model: "Marathon", variant: "", category: "fixedWing" },
    MS1: { manufacturer: "Mysky", model: "MS-1", variant: "", category: "fixedWing" },
    MS23: { manufacturer: "Morane-Saulnier", model: "MS-230", variant: "", category: "fixedWing" },
    MS31: { manufacturer: "Morane-Saulnier", model: "MS-315", variant: "", category: "fixedWing" },
    MS73: {
        manufacturer: "Morane-Saulnier",
        model: "MS-733 Alcyon",
        variant: "",
        category: "fixedWing",
    },
    MS76: { manufacturer: "Dinfia", model: "MS-760 Paris", variant: "", category: "fixedWing" },
    MSAI: { manufacturer: "Morane-Saulnier", model: "AI", variant: "", category: "fixedWing" },
    MSQ2: { manufacturer: "Backcountry", model: "Mackey SQ-2", variant: "", category: "fixedWing" },
    MT: { manufacturer: "Autogyro", model: "MT-03", variant: "", category: "rotorcraft" },
    MT2: { manufacturer: "Mitsubishi", model: "T-2", variant: "", category: "fixedWing" },
    MU2: { manufacturer: "Mitsubishi", model: "LR-1", variant: "", category: "fixedWing" },
    MU23: {
        manufacturer: "Akaflieg Munchen",
        model: "Mü-23 Saurier",
        variant: "",
        category: "fixedWing",
    },
    MU30: {
        manufacturer: "Mitsubishi",
        model: "MU-300 Diamond",
        variant: "",
        category: "fixedWing",
    },
    MUS2: { manufacturer: "Bushby", model: "M-2 Mustang 2", variant: "", category: "fixedWing" },
    MVN1: { manufacturer: "Mven", model: "1 Fermer", variant: "", category: "fixedWing" },
    MVRK: { manufacturer: "Phoenix (2)", model: "Maverick", variant: "", category: "fixedWing" },
    MX10: {
        manufacturer: "Aeroandina",
        model: "MXP-100 Aventura",
        variant: "",
        category: "fixedWing",
    },
    MX1T: {
        manufacturer: "Aero-East-Europe",
        model: "MXP-155 Tayrona",
        variant: "",
        category: "fixedWing",
    },
    MX2: { manufacturer: "Mx Aircraft", model: "MX-2", variant: "", category: "fixedWing" },
    MX58: {
        manufacturer: "Aero-East-Europe",
        model: "MXP-158 Embera",
        variant: "",
        category: "fixedWing",
    },
    MX65: {
        manufacturer: "Aerotec (2)",
        model: "MXP-650 Amigo",
        variant: "",
        category: "fixedWing",
    },
    MX80: {
        manufacturer: "Aeroandina",
        model: "MXP-800 Fantasy",
        variant: "",
        category: "fixedWing",
    },
    MXS: { manufacturer: "Mx Aircraft", model: "MXS", variant: "", category: "fixedWing" },
    MY12: { manufacturer: "Mylius", model: "MY-102 Tornado", variant: "", category: "fixedWing" },
    MY13: { manufacturer: "Mylius", model: "MY-103 Mistral", variant: "", category: "fixedWing" },
    MYA4: { manufacturer: "Myasishchev", model: "3M", variant: "", category: "fixedWing" },
    MYS4: {
        manufacturer: "Dassault",
        model: "MD-454A Mystère 4A",
        variant: "",
        category: "fixedWing",
    },
    N110: { manufacturer: "Nord", model: "1100 Noralpha", variant: "", category: "fixedWing" },
    N120: { manufacturer: "Nord", model: "1200 Norecrin", variant: "", category: "fixedWing" },
    N219: {
        manufacturer: "Dirgantara Indonesia",
        model: "N-219 Nurtanio",
        variant: "",
        category: "fixedWing",
    },
    N250: { manufacturer: "Nusantara", model: "N-250", variant: "", category: "fixedWing" },
    N260: {
        manufacturer: "Max Holste",
        model: "MH-260 Super Broussard",
        variant: "",
        category: "fixedWing",
    },
    N262: { manufacturer: "Aerospatiale", model: "Mohawk 298", variant: "", category: "fixedWing" },
    N3: {
        manufacturer: "Nostalgair",
        model: "N-3 Citabriette",
        variant: "",
        category: "fixedWing",
    },
    N320: { manufacturer: "Nord", model: "3202", variant: "", category: "fixedWing" },
    N340: { manufacturer: "Nord", model: "3400", variant: "", category: "fixedWing" },
    N3N: {
        manufacturer: "Naval Aircraft Factory",
        model: "N3N",
        variant: "",
        category: "fixedWing",
    },
    N5: { manufacturer: "Hongdu", model: "N-5", variant: "", category: "fixedWing" },
    NA40: { manufacturer: "Unis", model: "NA-40 Bongo", variant: "", category: "rotorcraft" },
    NARN: {
        manufacturer: "Korea Aerospace",
        model: "KC-100 Naraon",
        variant: "",
        category: "fixedWing",
    },
    NAVI: {
        manufacturer: "North American",
        model: "L-17 Navion",
        variant: "",
        category: "fixedWing",
    },
    NC85: { manufacturer: "Nord", model: "NC-854", variant: "", category: "fixedWing" },
    ND1T: {
        manufacturer: "Ndn",
        model: "NDN-1T Turbo Firecracker",
        variant: "",
        category: "fixedWing",
    },
    NDAC: {
        manufacturer: "Normand Dube",
        model: "Aerocruiser",
        variant: "",
        category: "fixedWing",
    },
    NDAT: {
        manufacturer: "Normand Dube",
        model: "Aerocruiser Turbo 450",
        variant: "",
        category: "fixedWing",
    },
    NDIC: { manufacturer: "Norman", model: "Nordic 2", variant: "", category: "fixedWing" },
    NFEX: { manufacturer: "Nf-Aircraft", model: "Explorer", variant: "", category: "fixedWing" },
    NG4: { manufacturer: "Brm Aero", model: "NG-4", variant: "", category: "fixedWing" },
    NG5: { manufacturer: "Brm Aero", model: "Bristell NG-5", variant: "", category: "fixedWing" },
    NH90: {
        manufacturer: "Australian Aerospace",
        model: "MRH-90",
        variant: "",
        category: "rotorcraft",
    },
    NI28: { manufacturer: "Nieuport", model: "28 Replica", variant: "", category: "fixedWing" },
    NIBB: { manufacturer: "Aviamilano", model: "F-14 Nibbio", variant: "", category: "fixedWing" },
    NIMB: {
        manufacturer: "Schempp-Hirth",
        model: "Nimbus 4DLM",
        variant: "",
        category: "fixedWing",
    },
    NIPR: {
        manufacturer: "Avions Fairey",
        model: "T-66 Nipper",
        variant: "",
        category: "fixedWing",
    },
    NM5: { manufacturer: "Nal", model: "NM-5", variant: "", category: "fixedWing" },
    NMCU: { manufacturer: "Enaer", model: "ECH-02 Namcu", variant: "", category: "fixedWing" },
    NNJA: { manufacturer: "Best Off", model: "Nynja", variant: "", category: "fixedWing" },
    NOMA: { manufacturer: "Gaf", model: "N-2 Nomad", variant: "", category: "fixedWing" },
    NORA: { manufacturer: "Nord", model: "2501 Noratlas", variant: "", category: "fixedWing" },
    NORS: { manufacturer: "Ccf", model: "Norseman", variant: "", category: "fixedWing" },
    NPOR: { manufacturer: "Redfern", model: "Nieuport 17", variant: "", category: "fixedWing" },
    NST6: {
        manufacturer: "Custom Flight",
        model: "North Star 6 Galaxie",
        variant: "",
        category: "fixedWing",
    },
    NSTR: {
        manufacturer: "Custom Flight",
        model: "North Star",
        variant: "",
        category: "fixedWing",
    },
    NT10: { manufacturer: "Nuwaco", model: "T-10", variant: "", category: "fixedWing" },
    NXT: { manufacturer: "Nemesis", model: "NXT", variant: "", category: "fixedWing" },
    O1: { manufacturer: "Cessna", model: "305 Bird Dog", variant: "", category: "fixedWing" },
    O3: { manufacturer: "Lockheed", model: "YO-3", variant: "", category: "fixedWing" },
    OCNR: {
        manufacturer: "Tissot-Charbonnier",
        model: "TC-120 Océanair",
        variant: "",
        category: "fixedWing",
    },
    OH1: { manufacturer: "Kawasaki", model: "OH-1", variant: "", category: "rotorcraft" },
    OKHO: { manufacturer: "Aero-Astra", model: "Okhotnik", variant: "", category: "rotorcraft" },
    OM1: { manufacturer: "Morrisey", model: "OM-1 Bravo", variant: "", category: "fixedWing" },
    OMGA: { manufacturer: "Isae", model: "Omega", variant: "", category: "fixedWing" },
    OMLA: { manufacturer: "Omac", model: "Laser 300", variant: "", category: "fixedWing" },
    OMSD: { manufacturer: "Atec", model: "Omsider", variant: "", category: "fixedWing" },
    ONE: { manufacturer: "Airnet", model: "One", variant: "", category: "fixedWing" },
    ONEX: { manufacturer: "Sonex", model: "Onex", variant: "", category: "fixedWing" },
    OPCA: {
        manufacturer: "Brooklands",
        model: "OA-7 Optica Scout",
        variant: "",
        category: "fixedWing",
    },
    OR10: { manufacturer: "Orion", model: "10", variant: "", category: "fixedWing" },
    OR12: { manufacturer: "Orion", model: "SK-12", variant: "", category: "fixedWing" },
    OSCR: { manufacturer: "Afic", model: "RSA-200 Falcon", variant: "", category: "fixedWing" },
    OVOD: { manufacturer: "Vitek", model: "Ovod", variant: "", category: "fixedWing" },
    OZZI: {
        manufacturer: "Buchanan",
        model: "BAC-204 Mosquito",
        variant: "",
        category: "fixedWing",
    },
    P06T: { manufacturer: "Tecnam", model: "P-2006T", variant: "", category: "fixedWing" },
    P1: { manufacturer: "Kawasaki", model: "P-1", variant: "", category: "fixedWing" },
    P100: { manufacturer: "Pottier", model: "P-100", variant: "", category: "fixedWing" },
    P130: {
        manufacturer: "Pottier",
        model: "P-130 Bleu Citron",
        variant: "",
        category: "fixedWing",
    },
    P136: { manufacturer: "Piaggio", model: "P-136", variant: "", category: "fixedWing" },
    P148: { manufacturer: "Piaggio", model: "P-148", variant: "", category: "fixedWing" },
    P149: { manufacturer: "Focke-Wulf", model: "FWP-149", variant: "", category: "fixedWing" },
    P180: { manufacturer: "Piaggio", model: "P-180 Avanti", variant: "", category: "fixedWing" },
    P18T: {
        manufacturer: "Smith Aviation",
        model: "PA-18T Super Cub",
        variant: "",
        category: "fixedWing",
    },
    P19: {
        manufacturer: "Aviamilano",
        model: "P-19 Scricciolo",
        variant: "",
        category: "fixedWing",
    },
    P1HH: {
        manufacturer: "Piaggio",
        model: "P-1HH Hammerhead",
        variant: "",
        category: "fixedWing",
    },
    P2: { manufacturer: "Lockheed", model: "L-426 Neptune", variant: "", category: "fixedWing" },
    P208: { manufacturer: "Tecnam", model: "P-2008", variant: "", category: "fixedWing" },
    P210: { manufacturer: "Aviones Colombia", model: "P210", variant: "", category: "fixedWing" },
    P212: { manufacturer: "Tecnam", model: "P-2012 Traveller", variant: "", category: "fixedWing" },
    P220: { manufacturer: "Aero (3)", model: "P-220-AT2", variant: "", category: "fixedWing" },
    P230: { manufacturer: "Pottier", model: "P-230 Panda", variant: "", category: "fixedWing" },
    P250: { manufacturer: "Pottier", model: "P-250 Xerus", variant: "", category: "fixedWing" },
    P25B: {
        manufacturer: "Laviasa",
        model: "PA-25 Biplaza Puelche",
        variant: "",
        category: "fixedWing",
    },
    P27: { manufacturer: "Gryf", model: "P-27 Skyster", variant: "", category: "fixedWing" },
    P270: { manufacturer: "Pottier", model: "P-270 Amster", variant: "", category: "fixedWing" },
    P28A: {
        manufacturer: "Aicsa",
        model: "PA-28-140 Cherokee",
        variant: "",
        category: "fixedWing",
    },
    P28B: {
        manufacturer: "Aicsa",
        model: "PA-28-201T Turbo Dakota",
        variant: "",
        category: "fixedWing",
    },
    P28R: {
        manufacturer: "Aicsa",
        model: "PA-28R-180 Cherokee Arrow",
        variant: "",
        category: "fixedWing",
    },
    P28S: {
        manufacturer: "Aicsa",
        model: "PA-28R-201T Turbo Cherokee Arrow 3",
        variant: "",
        category: "fixedWing",
    },
    P28T: {
        manufacturer: "Chincul",
        model: "PA-A-28RT-201 Arrow 4",
        variant: "",
        category: "fixedWing",
    },
    P28U: {
        manufacturer: "Aicsa",
        model: "PA-28RT-201T Turbo Arrow 4",
        variant: "",
        category: "fixedWing",
    },
    P3: { manufacturer: "Kawasaki", model: "EP-3 Orion", variant: "", category: "fixedWing" },
    P32R: { manufacturer: "Aicsa", model: "PA-32R-300 Lance", variant: "", category: "fixedWing" },
    P32T: {
        manufacturer: "Aicsa",
        model: "PA-32RT Turbo Lance 2",
        variant: "",
        category: "fixedWing",
    },
    P337: {
        manufacturer: "Cessna",
        model: "T337G Pressurized Skymaster",
        variant: "",
        category: "fixedWing",
    },
    P38: { manufacturer: "Lockheed", model: "F-5 Lightning", variant: "", category: "fixedWing" },
    P39: { manufacturer: "Bell", model: "P-39 Airacobra", variant: "", category: "fixedWing" },
    P40: { manufacturer: "Curtiss", model: "P-40 Warhawk", variant: "", category: "fixedWing" },
    P46T: { manufacturer: "Jetprop", model: "DLX", variant: "", category: "fixedWing" },
    P47: {
        manufacturer: "Republic",
        model: "F-47 Thunderbolt",
        variant: "",
        category: "fixedWing",
    },
    P4Y: { manufacturer: "Convair", model: "P4Y Privateer", variant: "", category: "fixedWing" },
    P50: { manufacturer: "Pottier", model: "P-50 Bouvreuil", variant: "", category: "fixedWing" },
    P51: {
        manufacturer: "Commonwealth (1)",
        model: "CA-18 Mustang",
        variant: "",
        category: "fixedWing",
    },
    P57: {
        manufacturer: "Partenavia",
        model: "P-57 Fachiro 2",
        variant: "",
        category: "fixedWing",
    },
    P60: { manufacturer: "Pottier", model: "P-60 Minacro", variant: "", category: "fixedWing" },
    P61: {
        manufacturer: "Northrop",
        model: "P-61 Black Widow",
        variant: "",
        category: "fixedWing",
    },
    P63: { manufacturer: "Bell", model: "P-63 Kingcobra", variant: "", category: "fixedWing" },
    P66P: { manufacturer: "Piaggio", model: "P-166", variant: "", category: "fixedWing" },
    P66T: { manufacturer: "Piaggio", model: "P-166DL3", variant: "", category: "fixedWing" },
    P68: { manufacturer: "Partenavia", model: "P-68", variant: "", category: "fixedWing" },
    P68T: {
        manufacturer: "Partenavia",
        model: "AP-68TP-300 Spartacus",
        variant: "",
        category: "fixedWing",
    },
    P70: { manufacturer: "Pottier", model: "P-70", variant: "", category: "fixedWing" },
    P750: { manufacturer: "Pacific Aerospace", model: "750XL", variant: "", category: "fixedWing" },
    P8: { manufacturer: "Boeing", model: "P-8 Poseidon", variant: "", category: "fixedWing" },
    P80: { manufacturer: "Pottier", model: "P-80", variant: "", category: "fixedWing" },
    P82: {
        manufacturer: "North American",
        model: "F-82 Twin Mustang",
        variant: "",
        category: "fixedWing",
    },
    PA11: {
        manufacturer: "American Legend",
        model: "AL-11C Legend Cub Special",
        variant: "",
        category: "fixedWing",
    },
    PA12: {
        manufacturer: "Backcountry",
        model: "PA-12 Super Cruiser",
        variant: "",
        category: "fixedWing",
    },
    PA14: {
        manufacturer: "Piper",
        model: "PA-14 Family Cruiser",
        variant: "",
        category: "fixedWing",
    },
    PA15: { manufacturer: "Piper", model: "PA-15 Vagabond", variant: "", category: "fixedWing" },
    PA16: { manufacturer: "Piper", model: "PA-16 Clipper", variant: "", category: "fixedWing" },
    PA17: { manufacturer: "Piper", model: "PA-17 Vagabond", variant: "", category: "fixedWing" },
    PA18: {
        manufacturer: "Airframes-Dakota Cub",
        model: "S-18 Super 18",
        variant: "",
        category: "fixedWing",
    },
    PA20: { manufacturer: "Piper", model: "PA-20 Pacer", variant: "", category: "fixedWing" },
    PA22: { manufacturer: "Piper", model: "PA-22 Caribbean", variant: "", category: "fixedWing" },
    PA23: { manufacturer: "Miller (1)", model: "Jet Profile", variant: "", category: "fixedWing" },
    PA24: { manufacturer: "Piper", model: "PA-24 Comanche", variant: "", category: "fixedWing" },
    PA25: { manufacturer: "Chincul", model: "PA-A-25 Pawnee", variant: "", category: "fixedWing" },
    PA27: {
        manufacturer: "Chincul",
        model: "PA-A-23-250 Aztec",
        variant: "",
        category: "fixedWing",
    },
    PA30: {
        manufacturer: "Aicsa",
        model: "PA-30 Twin Comanche",
        variant: "",
        category: "fixedWing",
    },
    PA31: { manufacturer: "Aicsa", model: "PA-31-310 Navajo", variant: "", category: "fixedWing" },
    PA32: {
        manufacturer: "Aicsa",
        model: "PA-32 Cherokee Six",
        variant: "",
        category: "fixedWing",
    },
    PA34: { manufacturer: "Piper", model: "PA-34", variant: "Seneca", category: "fixedWing" },
    PA36: {
        manufacturer: "Aicsa",
        model: "PA-36 Pawnee Brave",
        variant: "",
        category: "fixedWing",
    },
    PA38: { manufacturer: "Aicsa", model: "PA-38 Tomahawk", variant: "", category: "fixedWing" },
    PA44: { manufacturer: "Aicsa", model: "PA-44 Seminole", variant: "", category: "fixedWing" },
    PA46: { manufacturer: "Piper", model: "PA-46-310P Malibu", variant: "", category: "fixedWing" },
    PACE: {
        manufacturer: "Bellanca",
        model: "CH-300 Pacemaker",
        variant: "",
        category: "fixedWing",
    },
    PAGO: { manufacturer: "Alms", model: "ADV-01 Papango", variant: "", category: "fixedWing" },
    PANT: { manufacturer: "Rotec", model: "Panther 2", variant: "", category: "fixedWing" },
    PAR1: { manufacturer: "Paradise", model: "P-1", variant: "", category: "fixedWing" },
    PAR4: { manufacturer: "Paradise", model: "P-4", variant: "", category: "fixedWing" },
    PARA: {
        manufacturer: "Generic",
        model: "Paraglider/Paramotor",
        variant: "",
        category: "other",
    },
    PARL: { manufacturer: "Pietenpol", model: "Aerial", variant: "", category: "fixedWing" },
    PAT4: { manufacturer: "Neiva", model: "NE-821 Caraja", variant: "", category: "fixedWing" },
    PAUL: { manufacturer: "Neiva", model: "56 Paulistinha", variant: "", category: "fixedWing" },
    PAV4: { manufacturer: "Carter", model: "PAV-4", variant: "", category: "rotorcraft" },
    PAY1: {
        manufacturer: "Chincul",
        model: "PA-A-31T1-500 Cheyenne 1",
        variant: "",
        category: "fixedWing",
    },
    PAY2: {
        manufacturer: "Aicsa",
        model: "PA-31T2-620 Cheyenne 2XL",
        variant: "",
        category: "fixedWing",
    },
    PAY3: {
        manufacturer: "Aicsa",
        model: "PA-42-720 Cheyenne 3",
        variant: "",
        category: "fixedWing",
    },
    PAY4: {
        manufacturer: "Piper",
        model: "PA-42-1000 Cheyenne 400",
        variant: "",
        category: "fixedWing",
    },
    PC12: { manufacturer: "Pilatus", model: "PC-12", variant: "", category: "fixedWing" },
    PC21: { manufacturer: "Pilatus", model: "E-27", variant: "", category: "fixedWing" },
    PC24: { manufacturer: "Pilatus", model: "PC-24", variant: "", category: "fixedWing" },
    PC6P: { manufacturer: "Pilatus", model: "PC-6 Porter", variant: "", category: "fixedWing" },
    PC6T: {
        manufacturer: "Fairchild (1)",
        model: "PC-6 Heli-Porter",
        variant: "",
        category: "fixedWing",
    },
    PC7: { manufacturer: "Atlas", model: "PC-7 Astra", variant: "", category: "fixedWing" },
    PC9: { manufacturer: "Hawker De Havilland", model: "PC-9", variant: "", category: "fixedWing" },
    PCA2: { manufacturer: "Pitcairn-Cierva", model: "PCA-2", variant: "", category: "rotorcraft" },
    PDIG: {
        manufacturer: "Vstol",
        model: "SST-2000 Pairadigm",
        variant: "",
        category: "fixedWing",
    },
    PECR: {
        manufacturer: "Pro-Composites",
        model: "Personal Cruiser",
        variant: "",
        category: "fixedWing",
    },
    PEGA: {
        manufacturer: "General Avia",
        model: "F-20 Pegaso",
        variant: "",
        category: "fixedWing",
    },
    PEGZ: { manufacturer: "Pegase Aero", model: "Pegazair", variant: "", category: "fixedWing" },
    PELI: { manufacturer: "Flyer", model: "Pelican", variant: "", category: "fixedWing" },
    PEMB: { manufacturer: "Hunting", model: "P-66 Pembroke", variant: "", category: "fixedWing" },
    PETL: { manufacturer: "Aero Itba", model: "Petrel", variant: "", category: "fixedWing" },
    PETR: { manufacturer: "Aac", model: "SeaStar", variant: "", category: "fixedWing" },
    PGEE: { manufacturer: "Holcomb", model: "Perigee", variant: "", category: "fixedWing" },
    PGK1: {
        manufacturer: "Western",
        model: "PGK-1 Hirondelle",
        variant: "",
        category: "fixedWing",
    },
    PHIL: {
        manufacturer: "Vtol Aircraft",
        model: "Phillicopter",
        variant: "",
        category: "rotorcraft",
    },
    PHIX: { manufacturer: "Phenix", model: "Phenix", variant: "", category: "rotorcraft" },
    PIAE: { manufacturer: "Pipistrel", model: "WattsUp", variant: "", category: "fixedWing" },
    PIAT: { manufacturer: "Pipistrel", model: "Alpha Trainer", variant: "", category: "fixedWing" },
    PICO: {
        manufacturer: "Eurospace",
        model: "F-15 Excalibur",
        variant: "",
        category: "fixedWing",
    },
    PILL: { manufacturer: "Casa", model: "T-35 Pillán", variant: "", category: "fixedWing" },
    PINO: {
        manufacturer: "General Avia",
        model: "F-22 Pinguino",
        variant: "",
        category: "fixedWing",
    },
    PIPA: { manufacturer: "Pipistrel", model: "Panthera", variant: "", category: "fixedWing" },
    PISI: { manufacturer: "Pipistrel", model: "Sinus", variant: "", category: "fixedWing" },
    PIT4: {
        manufacturer: "Pipistrel",
        model: "Taurus Electro G4",
        variant: "",
        category: "fixedWing",
    },
    PITA: { manufacturer: "Pipistrel", model: "Taurus 503", variant: "", category: "fixedWing" },
    PITE: {
        manufacturer: "Pipistrel",
        model: "Taurus Electro G2",
        variant: "",
        category: "fixedWing",
    },
    PIVE: {
        manufacturer: "Pipistrel",
        model: "Virus (electric)",
        variant: "",
        category: "fixedWing",
    },
    PIVI: { manufacturer: "Pipistrel", model: "Surveyor", variant: "", category: "fixedWing" },
    PK11: { manufacturer: "Pik", model: "PIK-11 Tumppu", variant: "", category: "fixedWing" },
    PK15: { manufacturer: "Pik", model: "PIK-15 Hinu", variant: "", category: "fixedWing" },
    PK18: { manufacturer: "Pik", model: "PIK-18 Sytky", variant: "", category: "fixedWing" },
    PK20: { manufacturer: "Eiri", model: "PIK-20E", variant: "", category: "fixedWing" },
    PK21: { manufacturer: "Pik", model: "PIK-21 Super Sytky", variant: "", category: "fixedWing" },
    PK23: { manufacturer: "Pik", model: "PIK-23 Suhinu", variant: "", category: "fixedWing" },
    PK25: { manufacturer: "Pik", model: "PIK-25 Varttimarkka", variant: "", category: "fixedWing" },
    PKAN: { manufacturer: "Uetz", model: "U-3M Pelikan", variant: "", category: "fixedWing" },
    PL1: { manufacturer: "Pazmany", model: "PL-1 Laminar", variant: "", category: "fixedWing" },
    PL12: {
        manufacturer: "Flight Engineers",
        model: "PL-12 Airtruk",
        variant: "",
        category: "fixedWing",
    },
    PL2: { manufacturer: "Pazmany", model: "PL-2", variant: "", category: "fixedWing" },
    PL4: { manufacturer: "Pazmany", model: "PL-4", variant: "", category: "fixedWing" },
    PL9: { manufacturer: "Pazmany", model: "PL-9 Stork", variant: "", category: "fixedWing" },
    PLUS: {
        manufacturer: "Taylorcraft (2)",
        model: "Auster 1",
        variant: "",
        category: "fixedWing",
    },
    PNR2: { manufacturer: "Alpi", model: "Pioneer 230", variant: "", category: "fixedWing" },
    PNR3: { manufacturer: "Alpi", model: "Pioneer 300", variant: "", category: "fixedWing" },
    PNR4: { manufacturer: "Alpi", model: "Pioneer 400", variant: "", category: "fixedWing" },
    PNTH: {
        manufacturer: "Sport Performance",
        model: "Panther",
        variant: "",
        category: "fixedWing",
    },
    PO2: { manufacturer: "Css", model: "CSS-13", variant: "", category: "fixedWing" },
    PO60: { manufacturer: "Potez", model: "60 Sauterelle", variant: "", category: "fixedWing" },
    POLI: { manufacturer: "Alvarez", model: "Polliwagen", variant: "", category: "fixedWing" },
    PONY: { manufacturer: "Reda", model: "Pony", variant: "", category: "fixedWing" },
    PP2: { manufacturer: "Pilatus", model: "P-2", variant: "", category: "fixedWing" },
    PP3: { manufacturer: "Pilatus", model: "P-3", variant: "", category: "fixedWing" },
    PPRO: { manufacturer: "Hunting", model: "P-56 Provost", variant: "", category: "fixedWing" },
    PRBP: {
        manufacturer: "Howard Hughes",
        model: "Pocket Rocket PR-Bipe",
        variant: "",
        category: "fixedWing",
    },
    PRBR: {
        manufacturer: "Howard Hughes",
        model: "Pocket Rocket PR-Breeze",
        variant: "",
        category: "fixedWing",
    },
    PRCE: {
        manufacturer: "Percival",
        model: "P-57 Sea Prince",
        variant: "",
        category: "fixedWing",
    },
    PREN: { manufacturer: "Percival", model: "P-40 Prentice", variant: "", category: "fixedWing" },
    PRET: { manufacturer: "Gm&t", model: "Pretty Flight", variant: "", category: "fixedWing" },
    PREX: {
        manufacturer: "Private Explorer",
        model: "Private Explorer",
        variant: "",
        category: "fixedWing",
    },
    PRIM: { manufacturer: "Blackshape", model: "Bk-100 Prime", variant: "", category: "fixedWing" },
    PRM1: {
        manufacturer: "Hawker Beechcraft",
        model: "390 Premier 1",
        variant: "",
        category: "fixedWing",
    },
    PROC: { manufacturer: "Percival", model: "P-28 Proctor", variant: "", category: "fixedWing" },
    PROT: { manufacturer: "Csa", model: "Parrot", variant: "", category: "fixedWing" },
    PROW: { manufacturer: "Morse", model: "364 Prowler", variant: "", category: "fixedWing" },
    PRPR: {
        manufacturer: "Howard Hughes",
        model: "Pocket Rocket PR-582",
        variant: "",
        category: "fixedWing",
    },
    PRTS: { manufacturer: "Rutan", model: "281 Proteus", variant: "", category: "fixedWing" },
    PRXT: {
        manufacturer: "Private Explorer",
        model: "T-Explorer",
        variant: "",
        category: "fixedWing",
    },
    PSTM: { manufacturer: "Podesva", model: "Stearman", variant: "", category: "fixedWing" },
    PSW4: {
        manufacturer: "Pzl-Swidnik",
        model: "SW-4 Maluch",
        variant: "",
        category: "rotorcraft",
    },
    PT21: { manufacturer: "Pottier", model: "P-210 Coati", variant: "", category: "fixedWing" },
    PT22: { manufacturer: "Ryan", model: "PT-22 Recruit", variant: "", category: "fixedWing" },
    PT70: { manufacturer: "Pottier", model: "P-170", variant: "", category: "fixedWing" },
    PT80: { manufacturer: "Pottier", model: "P-180", variant: "", category: "fixedWing" },
    PTMS: {
        manufacturer: "Kimball",
        model: "S-12 Macho Stinker",
        variant: "",
        category: "fixedWing",
    },
    PTRL: { manufacturer: "Nash", model: "Petrel", variant: "", category: "fixedWing" },
    PTS1: {
        manufacturer: "Aerotek (1)",
        model: "Pitts S-1 Special",
        variant: "",
        category: "fixedWing",
    },
    PTS2: {
        manufacturer: "Aerotek (1)",
        model: "Pitts S-2 Special",
        variant: "",
        category: "fixedWing",
    },
    PTSS: {
        manufacturer: "Aviat",
        model: "S-1-11 Super Stinker",
        variant: "",
        category: "fixedWing",
    },
    PUL6: { manufacturer: "Pulsar", model: "Super Pulsar 600", variant: "", category: "fixedWing" },
    PULR: { manufacturer: "P&m Aviation", model: "PulsR", variant: "", category: "fixedWing" },
    PULS: { manufacturer: "Aero Designs", model: "Pulsar", variant: "", category: "fixedWing" },
    PUMA: {
        manufacturer: "Aerospatiale",
        model: "CH-33 Puma",
        variant: "",
        category: "rotorcraft",
    },
    PUP: { manufacturer: "Beagle", model: "B-121 Pup", variant: "", category: "fixedWing" },
    PURS: { manufacturer: "Rans", model: "S-11 Pursuit", variant: "", category: "fixedWing" },
    PUSH: { manufacturer: "Prescott", model: "Pusher", variant: "", category: "fixedWing" },
    PVTR: { manufacturer: "Privateer", model: "Privateer", variant: "", category: "fixedWing" },
    PZ01: { manufacturer: "Lipnur", model: "PZL-101 Gawron", variant: "", category: "fixedWing" },
    PZ02: { manufacturer: "Pzl-Okecie", model: "PZL-102 Kos", variant: "", category: "fixedWing" },
    PZ04: { manufacturer: "Lipnur", model: "Gelatik", variant: "", category: "fixedWing" },
    PZ06: {
        manufacturer: "Pzl-Okecie",
        model: "PZL-106A Kruk",
        variant: "",
        category: "fixedWing",
    },
    PZ12: {
        manufacturer: "Pzl-Okecie",
        model: "PZL-112 Junior",
        variant: "",
        category: "fixedWing",
    },
    PZ3T: {
        manufacturer: "Pzl-Okecie",
        model: "PZL-130 Orlik",
        variant: "",
        category: "fixedWing",
    },
    PZ4M: {
        manufacturer: "Pzl-Okecie",
        model: "PZL-104M Wilga 2000",
        variant: "",
        category: "fixedWing",
    },
    PZ6T: {
        manufacturer: "Pzl-Okecie",
        model: "PZL-106AT Turbo Javelin",
        variant: "",
        category: "fixedWing",
    },
    Q01: { manufacturer: "Reiner Stemme", model: "Q-01", variant: "", category: "fixedWing" },
    Q1: {
        manufacturer: "General Atomics",
        model: "MQ-1 Predator",
        variant: "",
        category: "fixedWing",
    },
    Q25: { manufacturer: "Boeing", model: "MQ-25 Stingray", variant: "", category: "fixedWing" },
    Q28: {
        manufacturer: "Boeing Australia",
        model: "MQ-28 Ghost Bat",
        variant: "",
        category: "fixedWing",
    },
    Q4: {
        manufacturer: "Northrop Grumman",
        model: "RQ-4 Global Hawk",
        variant: "",
        category: "fixedWing",
    },
    Q5: { manufacturer: "Hongdu", model: "A-5", variant: "", category: "fixedWing" },
    Q58: { manufacturer: "Kratos", model: "XQ-58 Valkyrie", variant: "", category: "fixedWing" },
    Q9: {
        manufacturer: "General Atomics",
        model: "MQ-9 Reaper",
        variant: "",
        category: "fixedWing",
    },
    QAIL: { manufacturer: "Aerosport", model: "Quail", variant: "", category: "fixedWing" },
    QALT: { manufacturer: "Fmp", model: "Qualt", variant: "", category: "fixedWing" },
    QIC2: { manufacturer: "Quickie", model: "Quickie Q2", variant: "", category: "fixedWing" },
    QINT: { manufacturer: "Schempp-Hirth", model: "Quintus", variant: "", category: "fixedWing" },
    QR01: { manufacturer: "Quercy", model: "CQR-01", variant: "", category: "fixedWing" },
    QUAS: { manufacturer: "Aeroalcool", model: "Quasar Lite", variant: "", category: "fixedWing" },
    QUIC: { manufacturer: "Quickie", model: "Quickie", variant: "", category: "fixedWing" },
    R100: { manufacturer: "Robin", model: "R-1180 Aiglon", variant: "", category: "fixedWing" },
    R109: {
        manufacturer: "Aircraft Spruce",
        model: "DR-109 Rhino",
        variant: "",
        category: "fixedWing",
    },
    R11: { manufacturer: "Rupert", model: "R-11", variant: "", category: "fixedWing" },
    R12: { manufacturer: "Rupert", model: "R-12 Vimana", variant: "", category: "fixedWing" },
    R135: { manufacturer: "Boeing", model: "TC-135", variant: "", category: "fixedWing" },
    R185: { manufacturer: "Johnson", model: "Rocket 185", variant: "", category: "fixedWing" },
    R2: { manufacturer: "Sau", model: "R-02 Robert", variant: "", category: "fixedWing" },
    R200: {
        manufacturer: "Alpha Aviation",
        model: "R-2160 Alpha 160",
        variant: "",
        category: "fixedWing",
    },
    R22: { manufacturer: "Aero Eli", model: "Yo-Yo", variant: "", category: "rotorcraft" },
    R300: { manufacturer: "Robin", model: "R-300", variant: "", category: "fixedWing" },
    R4: { manufacturer: "Sikorsky", model: "H-4 Hoverfly", variant: "", category: "rotorcraft" },
    R44: { manufacturer: "Robinson", model: "R44", variant: "", category: "rotorcraft" },
    R66: { manufacturer: "Robinson", model: "R-66", variant: "", category: "rotorcraft" },
    R721: {
        manufacturer: "Boeing",
        model: "727-100RE Super 27",
        variant: "",
        category: "fixedWing",
    },
    R722: {
        manufacturer: "Boeing",
        model: "727-200RE Super 27",
        variant: "",
        category: "fixedWing",
    },
    R90F: { manufacturer: "Ruschmeyer", model: "R-90-230FG", variant: "", category: "fixedWing" },
    R90R: { manufacturer: "Ruschmeyer", model: "MF-85", variant: "", category: "fixedWing" },
    R90T: { manufacturer: "Ruschmeyer", model: "R-90-420AT", variant: "", category: "fixedWing" },
    RA14: { manufacturer: "Adam (1)", model: "RA-14 Loisirs", variant: "", category: "fixedWing" },
    RA17: { manufacturer: "Adam (1)", model: "RA-17", variant: "", category: "fixedWing" },
    RAF2: {
        manufacturer: "Rotary Air Force",
        model: "RAF-2000",
        variant: "",
        category: "rotorcraft",
    },
    RAID: {
        manufacturer: "Sky Raider",
        model: "Super Sky Raider",
        variant: "",
        category: "fixedWing",
    },
    RAIL: { manufacturer: "Aerosport", model: "Rail", variant: "", category: "fixedWing" },
    RALL: {
        manufacturer: "Morane-Saulnier",
        model: "MS-880 Rallye Club",
        variant: "",
        category: "fixedWing",
    },
    RANG: { manufacturer: "Navion", model: "Rangemaster", variant: "", category: "fixedWing" },
    RARO: { manufacturer: "Altitude", model: "Radial Rocket", variant: "", category: "fixedWing" },
    RAV3: { manufacturer: "Ravin", model: "Ravin 300", variant: "", category: "fixedWing" },
    RAV5: { manufacturer: "Ravin", model: "Ravin 500", variant: "", category: "fixedWing" },
    RAZM: { manufacturer: "Saint Germain", model: "Raz-Mut", variant: "", category: "fixedWing" },
    RBEL: { manufacturer: "Murphy", model: "Rebel", variant: "", category: "fixedWing" },
    RC3: { manufacturer: "Republic", model: "RC-3 Seabee", variant: "", category: "fixedWing" },
    RC70: { manufacturer: "Fuji", model: "FA-300", variant: "", category: "fixedWing" },
    RCAL: { manufacturer: "Murphy", model: "Radical", variant: "", category: "fixedWing" },
    RD03: { manufacturer: "Duruble", model: "RD-03 Edelweiss", variant: "", category: "fixedWing" },
    RD20: {
        manufacturer: "Denize",
        model: "RD-20 Raid Driver",
        variant: "",
        category: "fixedWing",
    },
    RDH2: { manufacturer: "Redfern", model: "DH-2", variant: "", category: "fixedWing" },
    RELI: { manufacturer: "Stinson", model: "AT-19 Reliant", variant: "", category: "fixedWing" },
    RENE: { manufacturer: "Murphy", model: "Renegade", variant: "", category: "fixedWing" },
    REV6: { manufacturer: "Groen", model: "Revcon 6G", variant: "", category: "rotorcraft" },
    RF10: { manufacturer: "Aeromot", model: "AMT-100 Ximango", variant: "", category: "fixedWing" },
    RF3: { manufacturer: "Alpavia", model: "RF-3", variant: "", category: "fixedWing" },
    RF4: { manufacturer: "Alpavia", model: "RF-4", variant: "", category: "fixedWing" },
    RF47: { manufacturer: "Arc Atlantique", model: "RF-47", variant: "", category: "fixedWing" },
    RF5: { manufacturer: "Aero Jaen", model: "RF-5 Serrania", variant: "", category: "fixedWing" },
    RF6: { manufacturer: "Fournier", model: "RF-6", variant: "", category: "fixedWing" },
    RF9: { manufacturer: "Abs", model: "RF-9", variant: "", category: "fixedWing" },
    RFAL: { manufacturer: "Dassault", model: "Rafale", variant: "", category: "fixedWing" },
    RGNT: { manufacturer: "Neiva", model: "C-42 Regente", variant: "", category: "fixedWing" },
    RISN: { manufacturer: "Porto", model: "Risen", variant: "", category: "fixedWing" },
    RJ03: { manufacturer: "Junqua", model: "RJ-03 Ibis", variant: "", category: "fixedWing" },
    RJ1H: { manufacturer: "Ai(r)", model: "RJ-100 Avroliner", variant: "", category: "fixedWing" },
    RJ70: { manufacturer: "Ai(r)", model: "RJ-70 Avroliner", variant: "", category: "fixedWing" },
    RJ85: { manufacturer: "Ai(r)", model: "RJ-85 Avroliner", variant: "", category: "fixedWing" },
    RK5: { manufacturer: "Kalinauskas", model: "RK-5", variant: "", category: "fixedWing" },
    RLU1: { manufacturer: "Rlu", model: "RLU-1 Breezy", variant: "", category: "fixedWing" },
    RMOU: {
        manufacturer: "Hillberg",
        model: "EH1-01 Rotormouse",
        variant: "",
        category: "rotorcraft",
    },
    RNGR: { manufacturer: "Dac", model: "RangeR", variant: "", category: "fixedWing" },
    RODS: {
        manufacturer: "Aircraft Spruce",
        model: "DR-107 One Design",
        variant: "",
        category: "fixedWing",
    },
    ROND: { manufacturer: "Ambrosini", model: "F-4 Rondone", variant: "", category: "fixedWing" },
    ROSE: { manufacturer: "Maupin", model: "Windrose", variant: "", category: "fixedWing" },
    RP1: { manufacturer: "Mitsubishi", model: "RP-1", variant: "", category: "rotorcraft" },
    RPUP: {
        manufacturer: "Little Wing",
        model: "LW-4 Roto-Pup",
        variant: "",
        category: "rotorcraft",
    },
    RS12: { manufacturer: "Rans", model: "S-12 Airaile", variant: "", category: "fixedWing" },
    RS18: {
        manufacturer: "Sportavia-Putzer",
        model: "RS-180 Sportsman",
        variant: "",
        category: "fixedWing",
    },
    RS20: { manufacturer: "Rans", model: "S-20 Raven", variant: "", category: "fixedWing" },
    RS21: { manufacturer: "Rans", model: "S-21 Outbound", variant: "", category: "fixedWing" },
    RTA4: { manufacturer: "Rtaf", model: "RTAF-4 Chandra", variant: "", category: "fixedWing" },
    RUBI: { manufacturer: "Scintex", model: "ML-250 Rubis", variant: "", category: "fixedWing" },
    RV10: { manufacturer: "Van's", model: "RV-10", variant: "", category: "fixedWing" },
    RV12: { manufacturer: "Van's", model: "RV-12", variant: "", category: "fixedWing" },
    RV14: { manufacturer: "Van's", model: "RV-14", variant: "", category: "fixedWing" },
    RV15: { manufacturer: "Van's", model: "RV-15", variant: "", category: "fixedWing" },
    RV3: { manufacturer: "Van's", model: "RV-3", variant: "", category: "fixedWing" },
    RV4: { manufacturer: "Van's", model: "RV-4", variant: "", category: "fixedWing" },
    RV4T: { manufacturer: "Van's", model: "RV-4T", variant: "", category: "fixedWing" },
    RV6: { manufacturer: "Aiep", model: "Air Beetle", variant: "", category: "fixedWing" },
    RV7: { manufacturer: "Van's", model: "RV-7", variant: "", category: "fixedWing" },
    RV8: { manufacturer: "Van's", model: "RV-8", variant: "", category: "fixedWing" },
    RV9: { manufacturer: "Van's", model: "RV-9", variant: "", category: "fixedWing" },
    RVAL: { manufacturer: "Atlas", model: "CSH-2 Rooivalk", variant: "", category: "rotorcraft" },
    RVST: { manufacturer: "Rupert", model: "VimanaSteel", variant: "", category: "fixedWing" },
    RW19: { manufacturer: "Ragwing", model: "RW-19 Stork", variant: "", category: "fixedWing" },
    RW20: { manufacturer: "Ragwing", model: "RW-20 Stork", variant: "", category: "fixedWing" },
    RW22: {
        manufacturer: "Ragwing",
        model: "RW-22 Tiger Moth",
        variant: "",
        category: "fixedWing",
    },
    RW26: { manufacturer: "Ragwing", model: "RW-26 Special 2", variant: "", category: "fixedWing" },
    RW3: { manufacturer: "Rhein", model: "RW-3 Multoplane", variant: "", category: "fixedWing" },
    RYSA: { manufacturer: "Progress", model: "Rysachok", variant: "", category: "fixedWing" },
    RYST: { manufacturer: "Ryan", model: "PT-20", variant: "", category: "fixedWing" },
    S05F: {
        manufacturer: "Siai-Marchetti",
        model: "S-205-18F",
        variant: "",
        category: "fixedWing",
    },
    S05R: {
        manufacturer: "Siai-Marchetti",
        model: "S-205-18R",
        variant: "",
        category: "fixedWing",
    },
    S1: { manufacturer: "Arctic", model: "S-1 Arctic Tern", variant: "", category: "fixedWing" },
    S10: { manufacturer: "Stinson", model: "10 Voyager", variant: "", category: "fixedWing" },
    S107: { manufacturer: "Spratt", model: "107", variant: "", category: "fixedWing" },
    S108: { manufacturer: "Piper", model: "108 Station Wagon", variant: "", category: "fixedWing" },
    S10S: { manufacturer: "Stemme", model: "S-10", variant: "", category: "fixedWing" },
    S11: { manufacturer: "Fokker", model: "S-11 Instructor", variant: "", category: "fixedWing" },
    S12: { manufacturer: "Spencer", model: "S-12 Air Car", variant: "", category: "fixedWing" },
    S122: { manufacturer: "Skyline", model: "SL-122 Pchelka", variant: "", category: "fixedWing" },
    S12S: {
        manufacturer: "Stemme",
        model: "S-12 Twin Voyager",
        variant: "",
        category: "fixedWing",
    },
    S15S: { manufacturer: "Stemme", model: "S-15", variant: "", category: "fixedWing" },
    S15U: { manufacturer: "Safran", model: "S-15 Patroller", variant: "", category: "fixedWing" },
    S160: { manufacturer: "Sunward", model: "SA160", variant: "", category: "fixedWing" },
    S200: { manufacturer: "Sipa", model: "S-200 Minijet", variant: "", category: "fixedWing" },
    S202: { manufacturer: "Sgau", model: "S-202", variant: "", category: "fixedWing" },
    S208: { manufacturer: "Siai-Marchetti", model: "S-208", variant: "", category: "fixedWing" },
    S21: { manufacturer: "Macdonald", model: "S-21", variant: "", category: "fixedWing" },
    S211: { manufacturer: "Padc", model: "S-211", variant: "", category: "fixedWing" },
    S223: { manufacturer: "Casa", model: "223 Flamingo", variant: "", category: "fixedWing" },
    S22T: { manufacturer: "Cirrus", model: "SR-22T", variant: "", category: "fixedWing" },
    S274: { manufacturer: "Irgc", model: "Shahed 274", variant: "", category: "rotorcraft" },
    S278: { manufacturer: "Hesa", model: "Shahed 278", variant: "", category: "rotorcraft" },
    S285: { manufacturer: "Hesa", model: "Shahed 285", variant: "", category: "rotorcraft" },
    S2P: { manufacturer: "Conair", model: "Firecat", variant: "", category: "fixedWing" },
    S2T: { manufacturer: "Conair", model: "Turbo Firecat", variant: "", category: "fixedWing" },
    S3: { manufacturer: "Lockheed", model: "L-394 Viking", variant: "", category: "fixedWing" },
    S330: { manufacturer: "Schweizer", model: "269D 330", variant: "", category: "rotorcraft" },
    S355: { manufacturer: "Scaled", model: "355 Firebird", variant: "", category: "fixedWing" },
    S360: {
        manufacturer: "Aerospatiale",
        model: "SA-360 Dauphin",
        variant: "",
        category: "rotorcraft",
    },
    S37: { manufacturer: "Sukhoi", model: "S-37 Berkut", variant: "", category: "fixedWing" },
    S38: { manufacturer: "Sikorsky", model: "S-38 Replica", variant: "", category: "fixedWing" },
    S39: { manufacturer: "Sikorsky", model: "S-39", variant: "", category: "fixedWing" },
    S4: { manufacturer: "Arctic", model: "S-4 Privateer", variant: "", category: "fixedWing" },
    S400: { manufacturer: "Sgau", model: "S-400 Kapitan", variant: "", category: "fixedWing" },
    S401: { manufacturer: "Scaled", model: "401 Sierra", variant: "", category: "fixedWing" },
    S434: { manufacturer: "Sikorsky", model: "S-434", variant: "", category: "rotorcraft" },
    S45: { manufacturer: "Partenair", model: "S-45 Mystere", variant: "", category: "fixedWing" },
    S450: {
        manufacturer: "Aero-East-Europe",
        model: "SILA-450",
        variant: "",
        category: "fixedWing",
    },
    S51: { manufacturer: "Sikorsky", model: "H-5", variant: "", category: "rotorcraft" },
    S51D: { manufacturer: "Stewart (1)", model: "S-51D", variant: "", category: "fixedWing" },
    S52: { manufacturer: "Sikorsky", model: "S-52", variant: "", category: "rotorcraft" },
    S55P: { manufacturer: "Sikorsky", model: "CH-19", variant: "", category: "rotorcraft" },
    S55T: { manufacturer: "Sikorsky", model: "S-55T", variant: "", category: "rotorcraft" },
    S58P: {
        manufacturer: "Sikorsky",
        model: "CH-34 Chocktaw",
        variant: "",
        category: "rotorcraft",
    },
    S58T: { manufacturer: "Sikorsky", model: "S-58DT", variant: "", category: "rotorcraft" },
    S6: { manufacturer: "Stemme", model: "S-6", variant: "", category: "fixedWing" },
    S601: {
        manufacturer: "Aerospatiale",
        model: "SN-601 Corvette",
        variant: "",
        category: "fixedWing",
    },
    S61: { manufacturer: "Agusta", model: "AS-61A", variant: "", category: "rotorcraft" },
    S61R: { manufacturer: "Agusta", model: "AS-61R", variant: "", category: "rotorcraft" },
    S62: { manufacturer: "Sikorsky", model: "HH-52 Seaguard", variant: "", category: "rotorcraft" },
    S64: { manufacturer: "Sikorsky", model: "CH-54 Tarhe", variant: "", category: "rotorcraft" },
    S65C: {
        manufacturer: "Aerospatiale",
        model: "SA-365C Dauphin 2",
        variant: "",
        category: "rotorcraft",
    },
    S716: { manufacturer: "Stratos", model: "716", variant: "", category: "fixedWing" },
    S76: { manufacturer: "Sikorsky", model: "S-76", variant: "", category: "rotorcraft" },
    S900: { manufacturer: "Sipa", model: "S-901", variant: "", category: "fixedWing" },
    S92: { manufacturer: "Sikorsky", model: "CH-148 Cyclone", variant: "", category: "rotorcraft" },
    S97: { manufacturer: "Sikorsky", model: "S-97 Raider", variant: "", category: "rotorcraft" },
    SA02: { manufacturer: "K & S", model: "SA-102.5 Cavalier", variant: "", category: "fixedWing" },
    SA03: { manufacturer: "K & S", model: "SA-103 Cavalier", variant: "", category: "fixedWing" },
    SA04: { manufacturer: "K & S", model: "SA-104 Cavalier", variant: "", category: "fixedWing" },
    SA05: {
        manufacturer: "K & S",
        model: "SA-105 Super Cavalier",
        variant: "",
        category: "fixedWing",
    },
    SA10: { manufacturer: "Stolp", model: "SA-100 Starduster", variant: "", category: "fixedWing" },
    SA11: { manufacturer: "Stits", model: "SA-11 Playmate", variant: "", category: "fixedWing" },
    SA2: { manufacturer: "Icp", model: "SA-2 Rampage", variant: "", category: "fixedWing" },
    SA20: { manufacturer: "Beriev", model: "SA-20", variant: "", category: "fixedWing" },
    SA3: { manufacturer: "Stits", model: "SA-3 Playboy", variant: "", category: "fixedWing" },
    SA30: {
        manufacturer: "Stolp",
        model: "SA-300 Starduster Too",
        variant: "",
        category: "fixedWing",
    },
    SA37: { manufacturer: "Schweizer", model: "RG-8 Condor", variant: "", category: "fixedWing" },
    SA38: {
        manufacturer: "Schweizer",
        model: "RU-38A Twin Condor",
        variant: "",
        category: "fixedWing",
    },
    SA50: { manufacturer: "Stolp", model: "SA-500 Starlet", variant: "", category: "fixedWing" },
    SA6: { manufacturer: "Stits", model: "SA-6 Flut-R-Bug", variant: "", category: "fixedWing" },
    SA6E: { manufacturer: "Sreya", model: "SA-6 Envoy", variant: "", category: "fixedWing" },
    SA7: { manufacturer: "Stits", model: "SA-7 Sky-Coupe", variant: "", category: "fixedWing" },
    SA70: { manufacturer: "Stolp", model: "SA-700 Acroduster", variant: "", category: "fixedWing" },
    SA75: {
        manufacturer: "Stolp",
        model: "SA-750 Acroduster Too",
        variant: "",
        category: "fixedWing",
    },
    SA8T: {
        manufacturer: "Schweizer",
        model: "RU-38B Twin Condor",
        variant: "",
        category: "fixedWing",
    },
    SAB2: { manufacturer: "Arnet Pereyra", model: "Sabre 2", variant: "", category: "fixedWing" },
    SABA: { manufacturer: "Paravar Pars", model: "Saba", variant: "", category: "fixedWing" },
    SABW: { manufacturer: "Azalea", model: "Saberwing", variant: "", category: "fixedWing" },
    SACE: { manufacturer: "Ace", model: "Super Ace", variant: "", category: "fixedWing" },
    SACR: { manufacturer: "Smith (3)", model: "Acro Advanced", variant: "", category: "fixedWing" },
    SAFF: { manufacturer: "Halsted", model: "Saffire", variant: "", category: "fixedWing" },
    SAH1: { manufacturer: "Fls", model: "Sprint", variant: "", category: "fixedWing" },
    SAKO: { manufacturer: "Rans", model: "S-10 Sakota", variant: "", category: "fixedWing" },
    SALB: { manufacturer: "Skygear", model: "Albatross", variant: "", category: "fixedWing" },
    SAM: { manufacturer: "Sam Aircraft", model: "Sam", variant: "", category: "fixedWing" },
    SAND: { manufacturer: "Short", model: "S-25 Sandringham", variant: "", category: "fixedWing" },
    SAPH: { manufacturer: "Piel", model: "CP-1320 Saphir", variant: "", category: "fixedWing" },
    SASH: { manufacturer: "Shark Aircraft", model: "Shark", variant: "", category: "fixedWing" },
    SASP: {
        manufacturer: "Supermarine Aircraft",
        model: "Spitfire Mk25",
        variant: "",
        category: "fixedWing",
    },
    SASY: { manufacturer: "Protech", model: "PT-2 ProStar", variant: "", category: "fixedWing" },
    SATA: { manufacturer: "Casa", model: "HA-220 Super Saeta", variant: "", category: "fixedWing" },
    SAVA: { manufacturer: "Sadler", model: "Vampire", variant: "", category: "fixedWing" },
    SAVG: { manufacturer: "Alms", model: "Callao", variant: "", category: "fixedWing" },
    SB05: { manufacturer: "Saab", model: "105", variant: "", category: "fixedWing" },
    SB1: { manufacturer: "Sikorsky", model: "S-100 Defiant", variant: "", category: "rotorcraft" },
    SB20: { manufacturer: "Saab", model: "2000", variant: "", category: "fixedWing" },
    SB29: { manufacturer: "Saab", model: "29", variant: "", category: "fixedWing" },
    SB32: { manufacturer: "Saab", model: "32 Lansen", variant: "", category: "fixedWing" },
    SB35: { manufacturer: "Saab", model: "35 Draken", variant: "", category: "fixedWing" },
    SB37: { manufacturer: "Saab", model: "37 Viggen", variant: "", category: "fixedWing" },
    SB39: { manufacturer: "Saab", model: "39 Gripen", variant: "", category: "fixedWing" },
    SB7: { manufacturer: "Seabird", model: "SB-7 Seeker", variant: "", category: "fixedWing" },
    SB91: { manufacturer: "De Schelde", model: "91 Safir", variant: "", category: "fixedWing" },
    SBD: { manufacturer: "Douglas", model: "A-24 Dauntless", variant: "", category: "fixedWing" },
    SBLS: { manufacturer: "Slipstream", model: "SkyBlaster", variant: "", category: "fixedWing" },
    SBOY: { manufacturer: "Interplane", model: "Skyboy", variant: "", category: "fixedWing" },
    SBR1: {
        manufacturer: "North American",
        model: "CT-39 Sabreliner",
        variant: "",
        category: "fixedWing",
    },
    SBR2: {
        manufacturer: "North American Rockwell",
        model: "NA-265 Sabre 75",
        variant: "",
        category: "fixedWing",
    },
    SC01: { manufacturer: "Fft", model: "SC-01 Speed Canard", variant: "", category: "fixedWing" },
    SC7: { manufacturer: "Short", model: "SC-7 Skyliner", variant: "", category: "fixedWing" },
    SCAM: { manufacturer: "Aerosport", model: "Scamp", variant: "", category: "fixedWing" },
    SCEP: { manufacturer: "Slipstream", model: "Scepter", variant: "", category: "fixedWing" },
    SCII: {
        manufacturer: "Sport Copter",
        model: "SportCopter 2",
        variant: "",
        category: "rotorcraft",
    },
    SCOM: { manufacturer: "Airdale", model: "Comet", variant: "", category: "fixedWing" },
    SCOR: { manufacturer: "Rotorway", model: "Scorpion", variant: "", category: "rotorcraft" },
    SCOU: { manufacturer: "Westland", model: "Scout", variant: "", category: "rotorcraft" },
    SCRO: {
        manufacturer: "Akaflieg Munchen",
        model: "Mü-30 Schlacro",
        variant: "",
        category: "fixedWing",
    },
    SCTR: { manufacturer: "Flaglor", model: "Scooter", variant: "", category: "fixedWing" },
    SCUB: { manufacturer: "Aero Kuhlmann", model: "Scub", variant: "", category: "fixedWing" },
    SCW1: { manufacturer: "Scwal", model: "101", variant: "", category: "fixedWing" },
    SD2: { manufacturer: "Spacek", model: "SD-2 SportMaster", variant: "", category: "fixedWing" },
    SD4: { manufacturer: "Tomark", model: "SD-4 Viper", variant: "", category: "fixedWing" },
    SDUS: { manufacturer: "Stolp", model: "Super Starduster", variant: "", category: "fixedWing" },
    SE5A: { manufacturer: "Raf", model: "SE-5A", variant: "", category: "fixedWing" },
    SE5R: {
        manufacturer: "Replica Plans",
        model: "SE-5A Replica",
        variant: "",
        category: "fixedWing",
    },
    SEAT: { manufacturer: "Seawind", model: "Turbine Seawind", variant: "", category: "fixedWing" },
    SEAW: { manufacturer: "Seawind", model: "Seawind", variant: "", category: "fixedWing" },
    SERA: { manufacturer: "Servoplant", model: "Aerocraft", variant: "", category: "fixedWing" },
    SF2: { manufacturer: "Hapi", model: "SF-2 Cygnet", variant: "", category: "fixedWing" },
    SF23: { manufacturer: "Scheibe", model: "SF-23 Sperling", variant: "", category: "fixedWing" },
    SF24: {
        manufacturer: "Scheibe",
        model: "SF-24 Motorspatz",
        variant: "",
        category: "fixedWing",
    },
    SF25: { manufacturer: "Loravia", model: "SF-25 Falke", variant: "", category: "fixedWing" },
    SF27: { manufacturer: "Scheibe", model: "SF-27M", variant: "", category: "fixedWing" },
    SF28: {
        manufacturer: "Scheibe",
        model: "SF-28 Tandem Falke",
        variant: "",
        category: "fixedWing",
    },
    SF31: {
        manufacturer: "Sportavia-Putzer",
        model: "SFS-31 Milan",
        variant: "",
        category: "fixedWing",
    },
    SF34: { manufacturer: "Saab", model: "340", variant: "", category: "fixedWing" },
    SF35: { manufacturer: "Scheibe", model: "SF-35", variant: "", category: "fixedWing" },
    SF36: { manufacturer: "Scheibe", model: "SF-36", variant: "", category: "fixedWing" },
    SF50: { manufacturer: "Cirrus", model: "SJ-X Vision", variant: "", category: "fixedWing" },
    SG37: { manufacturer: "Schweizer", model: "SGM-2-37", variant: "", category: "fixedWing" },
    SG70: { manufacturer: "Glass", model: "SG-70 STOLGlass", variant: "", category: "fixedWing" },
    SG92: {
        manufacturer: "Technoavia",
        model: "SM-92T Turbo Finist",
        variant: "",
        category: "fixedWing",
    },
    SGRA: { manufacturer: "Prestige", model: "Storm Rally", variant: "", category: "fixedWing" },
    SGUP: {
        manufacturer: "Aero Spacelines",
        model: "377SGT Super Guppy",
        variant: "",
        category: "fixedWing",
    },
    SH09: { manufacturer: "Kopter", model: "SH-09", variant: "", category: "rotorcraft" },
    SH33: { manufacturer: "Short", model: "330", variant: "", category: "fixedWing" },
    SH36: { manufacturer: "Short", model: "360", variant: "", category: "fixedWing" },
    SH4: { manufacturer: "Silvercraft", model: "SH-4", variant: "", category: "rotorcraft" },
    SH5: { manufacturer: "Harbin", model: "PS-5", variant: "", category: "fixedWing" },
    SHAC: { manufacturer: "Avro", model: "696 Shackleton", variant: "", category: "fixedWing" },
    SHAW: {
        manufacturer: "Armstrong Whitworth",
        model: "Sea Hawk",
        variant: "",
        category: "fixedWing",
    },
    SHEA: { manufacturer: "Seaflight", model: "Shearwater", variant: "", category: "fixedWing" },
    SHEK: { manufacturer: "Rans", model: "S-16 Shekari", variant: "", category: "fixedWing" },
    SHER: { manufacturer: "Sherpa", model: "Sherpa", variant: "", category: "fixedWing" },
    SHIP: { manufacturer: "Generic", model: "Airship", variant: "", category: "lta" },
    SHOE: { manufacturer: "Mercury", model: "Shoestring", variant: "", category: "fixedWing" },
    SHOP: { manufacturer: "Salvay-Stark", model: "Skyhopper", variant: "", category: "fixedWing" },
    SHOR: {
        manufacturer: "Higher Class",
        model: "Super Hornet",
        variant: "",
        category: "fixedWing",
    },
    SHRK: { manufacturer: "Shark Aero", model: "Shark", variant: "", category: "fixedWing" },
    SHRT: { manufacturer: "Sherpa", model: "Sherpa K-650T", variant: "", category: "fixedWing" },
    SIDE: { manufacturer: "Smyth", model: "S Sidewinder", variant: "", category: "fixedWing" },
    SIGM: { manufacturer: "Elitar", model: "Sigma", variant: "", category: "fixedWing" },
    SILH: {
        manufacturer: "Lunds Tekniske",
        model: "SA-60 Silhouette",
        variant: "",
        category: "fixedWing",
    },
    SIR2: { manufacturer: "Rhein", model: "Sirius 2", variant: "", category: "fixedWing" },
    SIRA: { manufacturer: "Tecnam", model: "P-2002 Sierra", variant: "", category: "fixedWing" },
    SJ30: { manufacturer: "Emivest", model: "SJ-30", variant: "", category: "fixedWing" },
    SK10: { manufacturer: "Skyeton", model: "K-10 Swift", variant: "", category: "fixedWing" },
    SK70: { manufacturer: "Starkraft", model: "SK-700", variant: "", category: "fixedWing" },
    SKAR: { manufacturer: "Iii", model: "Sky Arrow", variant: "", category: "fixedWing" },
    SKIF: { manufacturer: "Aerolites", model: "AeroSkiff", variant: "", category: "fixedWing" },
    SKIM: { manufacturer: "Colonial", model: "C-1 Skimmer", variant: "", category: "fixedWing" },
    SKRA: { manufacturer: "Best Off", model: "Sky Ranger", variant: "", category: "fixedWing" },
    SKYC: { manufacturer: "Oma Sud", model: "Skycar", variant: "", category: "fixedWing" },
    SKYO: {
        manufacturer: "Skyote Aeromarine",
        model: "Skyote",
        variant: "",
        category: "fixedWing",
    },
    SKYR: {
        manufacturer: "Commonwealth (2)",
        model: "185 Skyranger",
        variant: "",
        category: "fixedWing",
    },
    SL1: { manufacturer: "Star-Lite", model: "SL-1 Star-Lite", variant: "", category: "fixedWing" },
    SL39: { manufacturer: "Mapo", model: "SL-39", variant: "", category: "fixedWing" },
    SL90: {
        manufacturer: "Aviotechnica",
        model: "SL-90 Leshii",
        variant: "",
        category: "fixedWing",
    },
    SLCH: { manufacturer: "Scaled", model: "351 Stratolaunch", variant: "", category: "fixedWing" },
    SLG2: {
        manufacturer: "Airplane Factory",
        model: "Sling LSA",
        variant: "",
        category: "fixedWing",
    },
    SLG4: {
        manufacturer: "Airplane Factory",
        model: "Sling 4 TSi",
        variant: "",
        category: "fixedWing",
    },
    SLH4: {
        manufacturer: "Sling Aircraft",
        model: "Sling 4 High Wing",
        variant: "",
        category: "fixedWing",
    },
    SLK3: { manufacturer: "Slick", model: "360", variant: "", category: "fixedWing" },
    SLK5: { manufacturer: "Slick", model: "540", variant: "", category: "fixedWing" },
    SM01: {
        manufacturer: "Stern-Mallick",
        model: "SM-01 Vega",
        variant: "",
        category: "fixedWing",
    },
    SM19: { manufacturer: "Siai-Marchetti", model: "SM-1019", variant: "", category: "fixedWing" },
    SM20: { manufacturer: "Technoavia", model: "SM-2000", variant: "", category: "fixedWing" },
    SM60: {
        manufacturer: "Stinson",
        model: "SM-6000 Tri-Motor",
        variant: "",
        category: "fixedWing",
    },
    SM92: {
        manufacturer: "Moravan",
        model: "Zlin Z-400 Rhino",
        variant: "",
        category: "fixedWing",
    },
    SMAX: { manufacturer: "Airmax", model: "M-22 SeaMax", variant: "", category: "fixedWing" },
    SMB2: {
        manufacturer: "Dassault",
        model: "Super Mystère B2",
        variant: "",
        category: "fixedWing",
    },
    SNAD: {
        manufacturer: "Calumet",
        model: "636 Snobird Adventurer",
        variant: "",
        category: "rotorcraft",
    },
    SNAP: { manufacturer: "Dallair", model: "FR-01 Snap", variant: "", category: "fixedWing" },
    SNGY: { manufacturer: "Ciac", model: "Synergy", variant: "", category: "fixedWing" },
    SNOS: { manufacturer: "Vsr", model: "SR-1 Snoshoo", variant: "", category: "fixedWing" },
    SNS2: { manufacturer: "Sorrell", model: "SNS-2 Guppy", variant: "", category: "fixedWing" },
    SNS7: { manufacturer: "Sorrell", model: "SNS-7 Hiperbipe", variant: "", category: "fixedWing" },
    SNS9: { manufacturer: "Sorrell", model: "SNS-9 EXP-2", variant: "", category: "fixedWing" },
    SNTA: { manufacturer: "Airsport", model: "Sonata", variant: "", category: "fixedWing" },
    SOK2: { manufacturer: "Soko", model: "2", variant: "", category: "fixedWing" },
    SOKL: { manufacturer: "Mraz", model: "M-1 Sokol", variant: "", category: "fixedWing" },
    SOL2: { manufacturer: "Solar Impulse", model: "2", variant: "", category: "fixedWing" },
    SOLI: { manufacturer: "Rutan", model: "77 Solitaire", variant: "", category: "fixedWing" },
    SONX: { manufacturer: "Sonex", model: "Sonex", variant: "", category: "fixedWing" },
    SORA: { manufacturer: "Acs (1)", model: "ACS-100 Sora", variant: "", category: "fixedWing" },
    SP20: { manufacturer: "Lanshe", model: "SP-26", variant: "", category: "fixedWing" },
    SP33: {
        manufacturer: "Spectrum",
        model: "S-33 Independence",
        variant: "",
        category: "fixedWing",
    },
    SP55: { manufacturer: "Technoavia", model: "SP-55", variant: "", category: "fixedWing" },
    SP6E: { manufacturer: "Sauser", model: "P-6E Replica", variant: "", category: "fixedWing" },
    SP7: { manufacturer: "Spartan", model: "7 Executive", variant: "", category: "fixedWing" },
    SP91: { manufacturer: "Technoavia", model: "SP-91 Slava", variant: "", category: "fixedWing" },
    SP95: { manufacturer: "Technoavia", model: "SP-95", variant: "", category: "fixedWing" },
    SPA2: {
        manufacturer: "Stark-Trefethen",
        model: "Sport-Aire 2",
        variant: "",
        category: "fixedWing",
    },
    SPAR: { manufacturer: "Iii", model: "Speed Arrow", variant: "", category: "fixedWing" },
    SPDR: { manufacturer: "Rearwin", model: "6000 Speedster", variant: "", category: "fixedWing" },
    SPEL: { manufacturer: "Loehle", model: "Spitfire Elite", variant: "", category: "fixedWing" },
    SPGY: {
        manufacturer: "Aircraft Designs",
        model: "Sportster Gyro",
        variant: "",
        category: "rotorcraft",
    },
    SPHA: {
        manufacturer: "American Autogyro",
        model: "SparrowHawk",
        variant: "",
        category: "rotorcraft",
    },
    SPIR: { manufacturer: "Questair", model: "Spirit", variant: "", category: "fixedWing" },
    SPIT: { manufacturer: "Supermarine", model: "Seafire", variant: "", category: "fixedWing" },
    SPKR: { manufacturer: "Tl Ultralight", model: "Sparker", variant: "", category: "fixedWing" },
    SPOR: { manufacturer: "Amax", model: "Sport 1700", variant: "", category: "fixedWing" },
    SPR2: { manufacturer: "Carlson", model: "Sparrow 2", variant: "", category: "fixedWing" },
    SPRT: { manufacturer: "Practavia", model: "Sprite", variant: "", category: "fixedWing" },
    SPST: { manufacturer: "Götaverken", model: "GV-38", variant: "", category: "fixedWing" },
    SPUP: {
        manufacturer: "Airdrome Aeroplanes",
        model: "Sopwith Pup Replica",
        variant: "",
        category: "fixedWing",
    },
    SQ2T: { manufacturer: "Glassic", model: "SQ-2000", variant: "", category: "fixedWing" },
    SQES: { manufacturer: "Innovation", model: "SkyQuest", variant: "", category: "fixedWing" },
    SR01: { manufacturer: "Eurodisplay", model: "SR-01 Magic", variant: "", category: "fixedWing" },
    SR20: { manufacturer: "Cirrus", model: "SR-20", variant: "", category: "fixedWing" },
    SR22: { manufacturer: "Cirrus", model: "SR-22", variant: "", category: "fixedWing" },
    SRAC: { manufacturer: "Sport Racer", model: "Sport Racer", variant: "", category: "fixedWing" },
    SRAI: { manufacturer: "Great Plains", model: "Sonerai", variant: "", category: "fixedWing" },
    SRAS: {
        manufacturer: "D'apuzzo",
        model: "D-260 Senior Aero Sport",
        variant: "",
        category: "fixedWing",
    },
    SRAY: { manufacturer: "Dornier", model: "S-Ray 007", variant: "", category: "fixedWing" },
    SREY: {
        manufacturer: "Progressive Aerodyne",
        model: "SeaRey",
        variant: "",
        category: "fixedWing",
    },
    SS2: { manufacturer: "Rutan", model: "339 Space Ship Two", variant: "", category: "fixedWing" },
    SS2P: {
        manufacturer: "Aero Commander",
        model: "S-2 Ag Commander",
        variant: "",
        category: "fixedWing",
    },
    SS2T: {
        manufacturer: "Ayres",
        model: "S-2R-G Turbo Thrush",
        variant: "",
        category: "fixedWing",
    },
    SSAB: {
        manufacturer: "North American",
        model: "F-100 Super Sabre",
        variant: "",
        category: "fixedWing",
    },
    SSC: {
        manufacturer: "Butterfly",
        model: "Super Sky Cycle",
        variant: "",
        category: "rotorcraft",
    },
    SSTL: { manufacturer: "Just", model: "JA30 SuperSTOL", variant: "", category: "fixedWing" },
    SSTM: { manufacturer: "Sg Aviation", model: "Sea Storm", variant: "", category: "fixedWing" },
    ST1: { manufacturer: "Phillips", model: "ST-1 Speedtwin", variant: "", category: "fixedWing" },
    ST10: { manufacturer: "Socata", model: "ST-10 Diplomate", variant: "", category: "fixedWing" },
    ST3: { manufacturer: "Stearman", model: "C-3", variant: "", category: "fixedWing" },
    ST30: { manufacturer: "Panzl", model: "S-330", variant: "", category: "fixedWing" },
    ST4: {
        manufacturer: "Stearman",
        model: "4 Junior Speedmail",
        variant: "",
        category: "fixedWing",
    },
    ST6: { manufacturer: "Stearman", model: "6 Cloudboy", variant: "", category: "fixedWing" },
    ST60: { manufacturer: "Staudacher", model: "S-600", variant: "", category: "fixedWing" },
    ST75: { manufacturer: "Boeing", model: "75 Kaydet", variant: "", category: "fixedWing" },
    ST87: { manufacturer: "Stern", model: "ST-87 Europlane", variant: "", category: "fixedWing" },
    STAL: {
        manufacturer: "Aircraft Designs",
        model: "Stallion",
        variant: "",
        category: "fixedWing",
    },
    STAR: { manufacturer: "Beech", model: "2000 Starship", variant: "", category: "fixedWing" },
    STAT: {
        manufacturer: "Aircraft Designs",
        model: "Turbine Stallion",
        variant: "",
        category: "fixedWing",
    },
    STCH: { manufacturer: "Fly Synthesis", model: "Storch", variant: "", category: "fixedWing" },
    STFF: { manufacturer: "Statler", model: "Firefly", variant: "", category: "fixedWing" },
    STG2: { manufacturer: "Rans", model: "S-18 Stinger 2", variant: "", category: "fixedWing" },
    STIL: { manufacturer: "Terzi", model: "T-9 Stiletto", variant: "", category: "fixedWing" },
    STLN: { manufacturer: "Helio", model: "AU-24 Stallion", variant: "", category: "fixedWing" },
    STOR: { manufacturer: "Slepcev", model: "SS-4 Storch", variant: "", category: "fixedWing" },
    STR2: { manufacturer: "Strojnik", model: "S-2", variant: "", category: "fixedWing" },
    STRA: { manufacturer: "Striplin", model: "Lone Ranger", variant: "", category: "fixedWing" },
    STRE: { manufacturer: "Tl Ultralight", model: "Stream", variant: "", category: "fixedWing" },
    STRI: {
        manufacturer: "Sopwith",
        model: "Triplane Replica",
        variant: "",
        category: "fixedWing",
    },
    STRK: { manufacturer: "Bac", model: "167 Strikemaster", variant: "", category: "fixedWing" },
    STRM: { manufacturer: "Sg Aviation", model: "Storm", variant: "", category: "fixedWing" },
    STST: { manufacturer: "Cfm", model: "SA-2 Star Streak", variant: "", category: "fixedWing" },
    SU17: { manufacturer: "Sukhoi", model: "Su-17", variant: "", category: "fixedWing" },
    SU24: { manufacturer: "Sukhoi", model: "Su-24", variant: "", category: "fixedWing" },
    SU25: { manufacturer: "Sukhoi", model: "Su-25", variant: "", category: "fixedWing" },
    SU26: { manufacturer: "Sukhoi", model: "Su-26", variant: "", category: "fixedWing" },
    SU27: { manufacturer: "Hindustan", model: "Su-30", variant: "", category: "fixedWing" },
    SU29: { manufacturer: "Sukhoi", model: "Su-29", variant: "", category: "fixedWing" },
    SU31: { manufacturer: "Sukhoi", model: "Su-31", variant: "", category: "fixedWing" },
    SU38: { manufacturer: "Sukhoi", model: "Su-38", variant: "", category: "fixedWing" },
    SU57: { manufacturer: "Sukhoi", model: "T-50", variant: "", category: "fixedWing" },
    SU7: { manufacturer: "Sukhoi", model: "Su-7", variant: "", category: "fixedWing" },
    SU80: { manufacturer: "Sukhoi", model: "Su-80", variant: "", category: "fixedWing" },
    SU95: { manufacturer: "Sukhoi", model: "Superjet 100-95", variant: "", category: "fixedWing" },
    SUBA: { manufacturer: "Fuji", model: "FA-200 Aero Subaru", variant: "", category: "fixedWing" },
    SUCO: { manufacturer: "Bell", model: "209 SuperCobra", variant: "", category: "rotorcraft" },
    SUNV: { manufacturer: "Fokker", model: "Super Universal", variant: "", category: "fixedWing" },
    SURN: {
        manufacturer: "Korea Aerospace",
        model: "KUH-1 Surion",
        variant: "",
        category: "rotorcraft",
    },
    SURU: { manufacturer: "Ipt", model: "IPT-16 Surubim", variant: "", category: "fixedWing" },
    SUSO: {
        manufacturer: "Vstol",
        model: "SS-2000 Super Solution",
        variant: "",
        category: "fixedWing",
    },
    SV4: { manufacturer: "Aiaa", model: "SV-4", variant: "", category: "fixedWing" },
    SVNH: {
        manufacturer: "Aerotec (2)",
        model: "MXP-740 Savannah",
        variant: "",
        category: "fixedWing",
    },
    SW18: { manufacturer: "Skywood", model: "SW-18 Teddy", variant: "", category: "fixedWing" },
    SW2: {
        manufacturer: "Swearingen",
        model: "SA-26 Merlin 2",
        variant: "",
        category: "fixedWing",
    },
    SW3: {
        manufacturer: "Fairchild (1)",
        model: "SA-227TT Fairchild 300",
        variant: "",
        category: "fixedWing",
    },
    SW4: { manufacturer: "Fairchild (1)", model: "C-26 Metro", variant: "", category: "fixedWing" },
    SWAK: { manufacturer: "Anglin", model: "Space Walker 1", variant: "", category: "fixedWing" },
    SWAT: { manufacturer: "Bharat", model: "LT-1 Swati", variant: "", category: "fixedWing" },
    SWFT: { manufacturer: "Scaled", model: "400 Swift", variant: "", category: "fixedWing" },
    SWIN: { manufacturer: "S-Wing", model: "S-Wing", variant: "", category: "fixedWing" },
    SWOR: { manufacturer: "Blackburn", model: "Swordfish", variant: "", category: "fixedWing" },
    SX30: { manufacturer: "Swearingen", model: "SX-300", variant: "", category: "fixedWing" },
    SYCA: { manufacturer: "Bristol", model: "171 Sycamore", variant: "", category: "rotorcraft" },
    SYMP: { manufacturer: "Omf", model: "OMF-100 Symphony", variant: "", category: "fixedWing" },
    SYNC: { manufacturer: "Fly Synthesis", model: "Syncro", variant: "", category: "fixedWing" },
    SZ45: { manufacturer: "Szd", model: "SZD-45 Ogar", variant: "", category: "fixedWing" },
    SZ9M: {
        manufacturer: "Szd",
        model: "SZD-9bis Bocian M-2000",
        variant: "",
        category: "fixedWing",
    },
    T1: { manufacturer: "Fuji", model: "T-1", variant: "", category: "fixedWing" },
    T10: { manufacturer: "Tmm-Avia", model: "T-10 Avia-Tor", variant: "", category: "fixedWing" },
    T101: {
        manufacturer: "Aeroprogress",
        model: "T-101 Grach",
        variant: "",
        category: "fixedWing",
    },
    T134: { manufacturer: "Tupolev", model: "Tu-134", variant: "", category: "fixedWing" },
    T154: { manufacturer: "Tupolev", model: "Tu-154", variant: "", category: "fixedWing" },
    T160: { manufacturer: "Tupolev", model: "Tu-160", variant: "", category: "fixedWing" },
    T18: { manufacturer: "Classic Sport", model: "S-18", variant: "", category: "fixedWing" },
    T19: { manufacturer: "Thk", model: "T-19 Türkkuşu", variant: "", category: "fixedWing" },
    T2: {
        manufacturer: "North American",
        model: "T-2 Buckeye",
        variant: "",
        category: "fixedWing",
    },
    T204: { manufacturer: "Tupolev", model: "Tu-204", variant: "", category: "fixedWing" },
    T206: {
        manufacturer: "Cessna",
        model: "T206 Turbo Stationair",
        variant: "",
        category: "fixedWing",
    },
    T210: {
        manufacturer: "Cessna",
        model: "T210 Turbo Centurion",
        variant: "",
        category: "fixedWing",
    },
    T211: { manufacturer: "Ad Aerospace", model: "T-211", variant: "", category: "fixedWing" },
    T22M: { manufacturer: "Tupolev", model: "Tu-22M", variant: "", category: "fixedWing" },
    T250: {
        manufacturer: "Anderson-Greenwood",
        model: "T-250 Aries",
        variant: "",
        category: "fixedWing",
    },
    T28: { manufacturer: "Hamilton", model: "T-28 Nomair", variant: "", category: "fixedWing" },
    T30: { manufacturer: "Terzi", model: "T-30 Katana", variant: "", category: "fixedWing" },
    T33: {
        manufacturer: "Canadair",
        model: "CL-30 Silver Star",
        variant: "",
        category: "fixedWing",
    },
    T334: { manufacturer: "Tupolev", model: "Tu-334", variant: "", category: "fixedWing" },
    T34P: { manufacturer: "Beech", model: "45 Mentor", variant: "", category: "fixedWing" },
    T34T: {
        manufacturer: "Beech",
        model: "T-34C Turbo Mentor",
        variant: "",
        category: "fixedWing",
    },
    T35: { manufacturer: "Temco", model: "T-35 Buckaroo", variant: "", category: "fixedWing" },
    T37: { manufacturer: "Cessna", model: "318A", variant: "", category: "fixedWing" },
    T38: { manufacturer: "Northrop", model: "AT-38 Talon", variant: "", category: "fixedWing" },
    T4: { manufacturer: "Kawasaki", model: "T-4", variant: "", category: "fixedWing" },
    T40: { manufacturer: "Turner", model: "T-40", variant: "", category: "fixedWing" },
    T411: {
        manufacturer: "Aeroprogress",
        model: "T-411 Aist-2",
        variant: "",
        category: "fixedWing",
    },
    T415: { manufacturer: "Khrunichev", model: "T-415 Snegir", variant: "", category: "fixedWing" },
    T419: {
        manufacturer: "Khrunichev",
        model: "AT-419 Strekoza",
        variant: "",
        category: "fixedWing",
    },
    T5: { manufacturer: "Fuji", model: "KM-2D", variant: "", category: "fixedWing" },
    T50: { manufacturer: "Cessna", model: "AT-8 Bobcat", variant: "", category: "fixedWing" },
    T51: { manufacturer: "Titan", model: "T-51 Mustang", variant: "", category: "fixedWing" },
    T6: { manufacturer: "Ccf", model: "T-6 Harvard", variant: "", category: "fixedWing" },
    T7: { manufacturer: "Fuji", model: "T-7", variant: "", category: "fixedWing" },
    TA15: {
        manufacturer: "Taylorcraft (1)",
        model: "15 Foursome",
        variant: "",
        category: "fixedWing",
    },
    TA16: { manufacturer: "Thurston", model: "TA-16 Seafire", variant: "", category: "fixedWing" },
    TA20: {
        manufacturer: "Taylorcraft (1)",
        model: "20 Ranchwagon",
        variant: "",
        category: "fixedWing",
    },
    TAA1: { manufacturer: "Scaled", model: "TAA-1", variant: "", category: "fixedWing" },
    TAGO: { manufacturer: "Cita", model: "Tiluchi", variant: "", category: "fixedWing" },
    TAIL: {
        manufacturer: "Aircraft Spruce",
        model: "W-10 Tailwind",
        variant: "",
        category: "fixedWing",
    },
    TAMP: { manufacturer: "Socata", model: "TB-9 Tampico", variant: "", category: "fixedWing" },
    TARO: { manufacturer: "Anahuac", model: "Tauro", variant: "", category: "fixedWing" },
    TARR: { manufacturer: "Pelegrin", model: "Tarragon", variant: "", category: "fixedWing" },
    TAYA: { manufacturer: "Taylor-Young", model: "A", variant: "", category: "fixedWing" },
    TAYB: { manufacturer: "Swick", model: "Swick-T", variant: "", category: "fixedWing" },
    TAYD: { manufacturer: "Taylorcraft (1)", model: "DC", variant: "", category: "fixedWing" },
    TB05: { manufacturer: "Amc", model: "Texas Bullet 205", variant: "", category: "fixedWing" },
    TB20: {
        manufacturer: "Shijiazhuang",
        model: "LE-500 Little Eagle",
        variant: "",
        category: "fixedWing",
    },
    TB21: {
        manufacturer: "Socata",
        model: "TB-21 Trinidad TC",
        variant: "",
        category: "fixedWing",
    },
    TB30: {
        manufacturer: "Aerospatiale",
        model: "TB-30 Epsilon",
        variant: "",
        category: "fixedWing",
    },
    TB31: { manufacturer: "Socata", model: "TB-31 Omega", variant: "", category: "fixedWing" },
    TBEE: {
        manufacturer: "United Consultant",
        model: "UC-1 Twin Bee",
        variant: "",
        category: "fixedWing",
    },
    TBM: {
        manufacturer: "General Motors",
        model: "TBM Avenger",
        variant: "",
        category: "fixedWing",
    },
    TBM7: { manufacturer: "Socata", model: "TBM-700A", variant: "", category: "fixedWing" },
    TBM8: { manufacturer: "Socata", model: "TBM-850", variant: "", category: "fixedWing" },
    TBM9: {
        manufacturer: "Daher",
        model: "TBM-700N (TBM-930)",
        variant: "",
        category: "fixedWing",
    },
    TBR3: {
        manufacturer: "Golden Circle",
        model: "T-Bird 2 Cargo",
        variant: "",
        category: "fixedWing",
    },
    TC2: { manufacturer: "Aero Mirage", model: "TC-2", variant: "", category: "fixedWing" },
    TCAT: { manufacturer: "Grumman", model: "F7F Tigercat", variant: "", category: "fixedWing" },
    TCOU: {
        manufacturer: "Helio",
        model: "H-500 Twin Courier",
        variant: "",
        category: "fixedWing",
    },
    TD1: { manufacturer: "Taiwan Dancer", model: "TD-1", variant: "", category: "fixedWing" },
    TD2: {
        manufacturer: "Turbine Design",
        model: "TD-2 Tempest",
        variant: "",
        category: "fixedWing",
    },
    TD3: {
        manufacturer: "Taiwan Dancer",
        model: "TD-3 Alluvion Legend",
        variant: "",
        category: "fixedWing",
    },
    TEAL: { manufacturer: "Schweizer", model: "TSC-1 Teal", variant: "", category: "fixedWing" },
    TERM: { manufacturer: "Smith (2)", model: "Termite", variant: "", category: "fixedWing" },
    TERR: { manufacturer: "Foxcon", model: "Terrier", variant: "", category: "fixedWing" },
    TEX2: { manufacturer: "Beechcraft", model: "T-6 Texan 2", variant: "", category: "fixedWing" },
    TEXA: { manufacturer: "Fly Synthesis", model: "Texan", variant: "", category: "fixedWing" },
    TF19: {
        manufacturer: "Taylorcraft (1)",
        model: "19 Sportsman",
        variant: "",
        category: "fixedWing",
    },
    TF21: { manufacturer: "Taylor Kits", model: "T-Kraft", variant: "", category: "fixedWing" },
    TF22: {
        manufacturer: "Taylorcraft (1)",
        model: "F-22 Classic",
        variant: "",
        category: "fixedWing",
    },
    TFK2: { manufacturer: "Ams-Flight", model: "Carat", variant: "", category: "fixedWing" },
    TFOC: {
        manufacturer: "Thunder Wings",
        model: "Focke-Wulf Fw-190",
        variant: "",
        category: "fixedWing",
    },
    TFUN: { manufacturer: "Twi", model: "Taifun", variant: "", category: "fixedWing" },
    TIAD: { manufacturer: "Trike Icaros", model: "Adventure", variant: "", category: "fixedWing" },
    TIGR: {
        manufacturer: "Airbus Helicopters",
        model: "EC-665 Tiger",
        variant: "",
        category: "rotorcraft",
    },
    TIJU: {
        manufacturer: "Avions Fairey",
        model: "Tipsy Junior",
        variant: "",
        category: "fixedWing",
    },
    TIPB: { manufacturer: "Avions Fairey", model: "Tipsy B", variant: "", category: "fixedWing" },
    TJET: { manufacturer: "Maverick", model: "TJ-1500 Leader", variant: "", category: "fixedWing" },
    TL20: {
        manufacturer: "Tl Ultralight",
        model: "TL-96 Star",
        variant: "",
        category: "fixedWing",
    },
    TL30: {
        manufacturer: "Tl Ultralight",
        model: "TL-3000 Sirius",
        variant: "",
        category: "fixedWing",
    },
    TLEG: { manufacturer: "Legend", model: "Turbine Legend", variant: "", category: "fixedWing" },
    TM5: { manufacturer: "Tm Aircraft", model: "TM-5", variant: "", category: "fixedWing" },
    TMOT: { manufacturer: "Fisher", model: "R-80 Tiger Moth", variant: "", category: "fixedWing" },
    TMUS: { manufacturer: "Papa 51", model: "Thunder Mustang", variant: "", category: "fixedWing" },
    TNAV: { manufacturer: "Camair", model: "480 Twin Navion", variant: "", category: "fixedWing" },
    TNDR: { manufacturer: "Dream", model: "Tundra", variant: "", category: "fixedWing" },
    TOBA: { manufacturer: "Socata", model: "TB-10 Tobago", variant: "", category: "fixedWing" },
    TOOT: { manufacturer: "Meyer", model: "Little Toot", variant: "", category: "fixedWing" },
    TOR: { manufacturer: "Aeritalia", model: "Tornado", variant: "", category: "fixedWing" },
    TOUR: { manufacturer: "Aesl", model: "Airtourer", variant: "", category: "fixedWing" },
    TOXO: { manufacturer: "Cag (1)", model: "Toxo", variant: "", category: "fixedWing" },
    TP40: {
        manufacturer: "Thunder Wings",
        model: "Curtiss P-40",
        variant: "",
        category: "fixedWing",
    },
    TPIL: {
        manufacturer: "Enaer",
        model: "T-35DT Turbo Pillán",
        variant: "",
        category: "fixedWing",
    },
    TPIN: {
        manufacturer: "Scottish Aviation",
        model: "Twin Pioneer",
        variant: "",
        category: "fixedWing",
    },
    TR1: { manufacturer: "Trident", model: "TR-1 Trigull", variant: "", category: "fixedWing" },
    TR20: { manufacturer: "Feugray", model: "TR-200", variant: "", category: "fixedWing" },
    TR26: { manufacturer: "Feugray", model: "TR-260 Sirius", variant: "", category: "fixedWing" },
    TR55: { manufacturer: "3xtrim", model: "550 Trener", variant: "", category: "fixedWing" },
    TRAL: { manufacturer: "Groppo", model: "Trail", variant: "", category: "fixedWing" },
    TRAP: { manufacturer: "Capella", model: "T-Raptor", variant: "", category: "fixedWing" },
    TRBA: { manufacturer: "Podesva", model: "Trener Baby", variant: "", category: "fixedWing" },
    TRDO: { manufacturer: "Titan", model: "Tornado SS", variant: "", category: "fixedWing" },
    TRF1: { manufacturer: "Team Rocket", model: "F-1", variant: "", category: "fixedWing" },
    TRIM: { manufacturer: "Ford", model: "4-AT Tri-Motor", variant: "", category: "fixedWing" },
    TRIS: {
        manufacturer: "Anglo Normandy",
        model: "BN-2A Mk3 Trislander",
        variant: "",
        category: "fixedWing",
    },
    TRMA: { manufacturer: "Stinson", model: "A Tri-Motor", variant: "", category: "fixedWing" },
    TRWN: {
        manufacturer: "Pacific Airmotive",
        model: "Tradewind",
        variant: "",
        category: "fixedWing",
    },
    TS11: { manufacturer: "Pzl-Mielec", model: "TS-11 Iskra", variant: "", category: "fixedWing" },
    TS14: { manufacturer: "Test", model: "TST-14 Bonus", variant: "", category: "fixedWing" },
    TS8: { manufacturer: "Pzl-Mielec", model: "TS-8 Bies", variant: "", category: "fixedWing" },
    TSPT: {
        manufacturer: "Thunder Wings",
        model: "Supermarine Spitfire",
        variant: "",
        category: "fixedWing",
    },
    TSTN: { manufacturer: "Terrafugia", model: "Transition", variant: "", category: "fixedWing" },
    TSTR: { manufacturer: "Air & Space", model: "Twinstar", variant: "", category: "rotorcraft" },
    TTRS: { manufacturer: "Humbert", model: "Tetras", variant: "", category: "fixedWing" },
    TTWO: { manufacturer: "Parker", model: "Teenie Two", variant: "", category: "fixedWing" },
    TU16: { manufacturer: "Tupolev", model: "Tu-16", variant: "", category: "fixedWing" },
    TU22: { manufacturer: "Tupolev", model: "Tu-22", variant: "", category: "fixedWing" },
    TU95: { manufacturer: "Tupolev", model: "Tu-20", variant: "", category: "fixedWing" },
    TUCA: { manufacturer: "Aoi", model: "EMB-312 Tucano", variant: "", category: "fixedWing" },
    TUCR: {
        manufacturer: "Flying Legend",
        model: "Tucano Replica",
        variant: "",
        category: "fixedWing",
    },
    TUCT: {
        manufacturer: "Flying Legend",
        model: "Turbine Tucano Replica",
        variant: "",
        category: "fixedWing",
    },
    TUL3: { manufacturer: "Podesva", model: "TUL-03 Amigo", variant: "", category: "fixedWing" },
    TUTR: { manufacturer: "Avro", model: "621 Tutor", variant: "", category: "fixedWing" },
    TVL4: { manufacturer: "Travel Air", model: "4", variant: "", category: "fixedWing" },
    TVLB: { manufacturer: "Bel-Aire", model: "2000", variant: "", category: "fixedWing" },
    TWEN: {
        manufacturer: "Tecnam",
        model: "P-2010 Twenty-Ten",
        variant: "",
        category: "fixedWing",
    },
    TWIR: { manufacturer: "Dyn'aero", model: "Twin-R", variant: "", category: "fixedWing" },
    TWSP: { manufacturer: "Time Warp", model: "Spitfire", variant: "", category: "fixedWing" },
    TWST: { manufacturer: "Silence", model: "Twister", variant: "", category: "fixedWing" },
    TZRV: { manufacturer: "Iriaf", model: "JT-2-2 Tazarve", variant: "", category: "fixedWing" },
    U15: { manufacturer: "Phoenix Air", model: "U-15 Phoenix", variant: "", category: "fixedWing" },
    U16: { manufacturer: "Grumman", model: "G-64 Albatross", variant: "", category: "fixedWing" },
    U2: { manufacturer: "Lockheed", model: "ER-2", variant: "", category: "fixedWing" },
    U21: { manufacturer: "Beech", model: "90 (A90-1) Ute", variant: "", category: "fixedWing" },
    U22: { manufacturer: "Beech", model: "1074", variant: "", category: "fixedWing" },
    UBAT: { manufacturer: "Australite", model: "Ultrabat", variant: "", category: "fixedWing" },
    UF10: { manufacturer: "Airo", model: "5", variant: "", category: "fixedWing" },
    UF13: { manufacturer: "Distar", model: "D-13 SunDancer", variant: "", category: "fixedWing" },
    UFHT: { manufacturer: "Ufo", model: "HeliThruster", variant: "", category: "rotorcraft" },
    UH1: { manufacturer: "Agusta", model: "AB-204", variant: "", category: "rotorcraft" },
    UH12: { manufacturer: "Fairchild Hiller", model: "E4", variant: "", category: "rotorcraft" },
    UH1Y: { manufacturer: "Bell", model: "UH-1Y", variant: "", category: "rotorcraft" },
    UHEL: {
        manufacturer: "Generic",
        model: "Ultralight Helicopter",
        variant: "",
        category: "rotorcraft",
    },
    UL10: {
        manufacturer: "Streamline Welding",
        model: "10 Ultimate Competitor",
        variant: "",
        category: "fixedWing",
    },
    UL20: {
        manufacturer: "Streamline Welding",
        model: "20 Ultimate Companion",
        variant: "",
        category: "fixedWing",
    },
    UL2F: { manufacturer: "Aeros", model: "UL-2000 Flamingo", variant: "", category: "fixedWing" },
    UL39: { manufacturer: "Cvut", model: "UL-39 Albi", variant: "", category: "fixedWing" },
    UL45: { manufacturer: "3xtrim", model: "450 Ultra", variant: "", category: "fixedWing" },
    ULAC: {
        manufacturer: "Generic",
        model: "Ultra Light Aircraft",
        variant: "",
        category: "fixedWing",
    },
    ULPA: { manufacturer: "Ullmann", model: "Panther", variant: "", category: "fixedWing" },
    ULTS: {
        manufacturer: "American Sportscopter",
        model: "Ultrasport 496",
        variant: "",
        category: "rotorcraft",
    },
    UM18: { manufacturer: "Air & Space", model: "18", variant: "", category: "rotorcraft" },
    UNIV: { manufacturer: "Neiva", model: "N-621 Universal", variant: "", category: "fixedWing" },
    URRA: { manufacturer: "Ibis (2)", model: "Urraco", variant: "", category: "fixedWing" },
    US2: { manufacturer: "Shinmaywa", model: "US-2", variant: "", category: "fixedWing" },
    UT60: { manufacturer: "Utva", model: "60", variant: "", category: "fixedWing" },
    UT65: { manufacturer: "Utva", model: "65 Privrednik", variant: "", category: "fixedWing" },
    UT66: { manufacturer: "Utva", model: "66", variant: "", category: "fixedWing" },
    UT75: { manufacturer: "Safat", model: "03", variant: "", category: "fixedWing" },
    UU12: {
        manufacturer: "Udet",
        model: "U-12 Flamingo Replica",
        variant: "",
        category: "fixedWing",
    },
    V1: { manufacturer: "Grumman", model: "AO-1 Mohawk", variant: "", category: "fixedWing" },
    V10: {
        manufacturer: "North American",
        model: "OV-10 Bronco",
        variant: "",
        category: "fixedWing",
    },
    V22: { manufacturer: "Bell-Boeing", model: "901 Osprey", variant: "", category: "other" },
    V221: { manufacturer: "Msw", model: "Votec 221", variant: "", category: "fixedWing" },
    V252: { manufacturer: "Msw", model: "Votec 252", variant: "", category: "fixedWing" },
    V280: { manufacturer: "Bell", model: "V-280 Valor", variant: "", category: "other" },
    V322: { manufacturer: "Msw", model: "Votec 322", variant: "", category: "fixedWing" },
    V351: { manufacturer: "Msw", model: "Votec 351", variant: "", category: "fixedWing" },
    V452: { manufacturer: "Msw", model: "Votec 452", variant: "", category: "fixedWing" },
    V500: {
        manufacturer: "Revolution (1)",
        model: "Voyager-500",
        variant: "",
        category: "rotorcraft",
    },
    VALI: { manufacturer: "Convair", model: "BT-13 Valiant", variant: "", category: "fixedWing" },
    VAMP: {
        manufacturer: "De Havilland",
        model: "DH-100 Vampire",
        variant: "",
        category: "fixedWing",
    },
    VANT: {
        manufacturer: "Visionaire",
        model: "VA-10 Vantage",
        variant: "",
        category: "fixedWing",
    },
    VAUT: { manufacturer: "Sud", model: "SO-4050 Vautour", variant: "", category: "fixedWing" },
    VELO: { manufacturer: "Velocity", model: "Velocity 173", variant: "", category: "fixedWing" },
    VELT: {
        manufacturer: "Velocity",
        model: "Velocity V-Twin",
        variant: "",
        category: "fixedWing",
    },
    VENT: { manufacturer: "Schempp-Hirth", model: "Ventus bT", variant: "", category: "fixedWing" },
    VEZE: { manufacturer: "Rutan", model: "33 VariEze", variant: "", category: "fixedWing" },
    VF2: { manufacturer: "Fry", model: "VF-2 Esprit", variant: "", category: "fixedWing" },
    VF35: {
        manufacturer: "Lockheed Martin",
        model: "F-35B Lightning 2",
        variant: "",
        category: "fixedWing",
    },
    VF60: {
        manufacturer: "Vulcanair",
        model: "VF-600 Mission",
        variant: "",
        category: "fixedWing",
    },
    VG3T: { manufacturer: "Aviakit", model: "Vega 3000", variant: "", category: "fixedWing" },
    VGUL: { manufacturer: "Percival", model: "K-1 Vega Gull", variant: "", category: "fixedWing" },
    VIMA: { manufacturer: "Valtion", model: "Viima", variant: "", category: "fixedWing" },
    VIPJ: { manufacturer: "Viper", model: "ViperJet", variant: "", category: "fixedWing" },
    VIPR: { manufacturer: "Paxman's", model: "Viper", variant: "", category: "fixedWing" },
    VISI: { manufacturer: "Pro-Composites", model: "Vision", variant: "", category: "fixedWing" },
    VIVA: { manufacturer: "Composit Airplanes", model: "Viva", variant: "", category: "fixedWing" },
    VIX: { manufacturer: "Skystar", model: "Kitfox Vixen", variant: "", category: "fixedWing" },
    VIXN: { manufacturer: "Amax", model: "Vixen", variant: "", category: "fixedWing" },
    VJ22: { manufacturer: "Volmer", model: "VJ-22 Sportsman", variant: "", category: "fixedWing" },
    VK3P: {
        manufacturer: "Cirrus",
        model: "VK-30 Cirrus (piston)",
        variant: "",
        category: "fixedWing",
    },
    VK3T: {
        manufacturer: "Cirrus",
        model: "VK-30 Cirrus (turbine)",
        variant: "",
        category: "fixedWing",
    },
    VL3: { manufacturer: "Aveko", model: "VL-3", variant: "", category: "fixedWing" },
    VL3T: {
        manufacturer: "Jmb Aircraft",
        model: "VL-3 Evolution Turbine",
        variant: "",
        category: "fixedWing",
    },
    VLOT: {
        manufacturer: "Velocity",
        model: "Velocity XL (turbine)",
        variant: "",
        category: "fixedWing",
    },
    VLTT: {
        manufacturer: "Velocity",
        model: "Velocity V-Twin (turbine)",
        variant: "",
        category: "fixedWing",
    },
    VM1: {
        manufacturer: "Vol Mediterrani",
        model: "VM-1 Esqual",
        variant: "",
        category: "fixedWing",
    },
    VNOM: {
        manufacturer: "De Havilland",
        model: "DH-112 Venom",
        variant: "",
        category: "fixedWing",
    },
    VNTE: { manufacturer: "Schempp-Hirth", model: "Ventus 3F", variant: "", category: "fixedWing" },
    VNTR: { manufacturer: "Icp", model: "Ventura", variant: "", category: "fixedWing" },
    VO10: {
        manufacturer: "Aero Commander",
        model: "100 Commander 100",
        variant: "",
        category: "fixedWing",
    },
    VOL2: { manufacturer: "Volante", model: "Volante Two", variant: "", category: "fixedWing" },
    VP2: { manufacturer: "Evans", model: "VP-2 Volksplane", variant: "", category: "fixedWing" },
    VR20: { manufacturer: "Egvoyager", model: "VR-202", variant: "", category: "fixedWing" },
    VR7: { manufacturer: "Vashon", model: "Ranger R7", variant: "", category: "fixedWing" },
    VSON: {
        manufacturer: "American Affordable",
        model: "Vision",
        variant: "",
        category: "fixedWing",
    },
    VTOR: {
        manufacturer: "Partenavia",
        model: "AP-68TP-600 Viator",
        variant: "",
        category: "fixedWing",
    },
    VTRA: { manufacturer: "Rans", model: "S-19 Venterra", variant: "", category: "fixedWing" },
    VTUR: { manufacturer: "Nuventure", model: "Venture", variant: "", category: "fixedWing" },
    VUT1: { manufacturer: "Evektor", model: "VUT-100 Cobra", variant: "", category: "fixedWing" },
    VVIG: { manufacturer: "Rutan", model: "27 VariViggen", variant: "", category: "fixedWing" },
    VW10: {
        manufacturer: "Airconcept",
        model: "VoWi-10 Airbuggy",
        variant: "",
        category: "fixedWing",
    },
    VWIT: { manufacturer: "Aircraft Spruce", model: "V-Witt", variant: "", category: "fixedWing" },
    W11: {
        manufacturer: "Wolf",
        model: "W-11 Boredom Fighter",
        variant: "",
        category: "fixedWing",
    },
    W135: { manufacturer: "Boeing", model: "WC-135R", variant: "", category: "fixedWing" },
    W201: { manufacturer: "Weatherly", model: "201", variant: "", category: "fixedWing" },
    W3: { manufacturer: "Pzl-Swidnik", model: "S-1 Procjon", variant: "", category: "rotorcraft" },
    W5BC: {
        manufacturer: "Luceair",
        model: "W-5 Buttercup Replica",
        variant: "",
        category: "fixedWing",
    },
    W62T: { manufacturer: "Weatherly", model: "620BTG", variant: "", category: "fixedWing" },
    WA40: { manufacturer: "Wassmer", model: "WA-40 Super 4", variant: "", category: "fixedWing" },
    WA41: { manufacturer: "Wassmer", model: "WA-41 Baladou", variant: "", category: "fixedWing" },
    WA42: { manufacturer: "Wassmer", model: "WA-421 Prestige", variant: "", category: "fixedWing" },
    WA50: { manufacturer: "Wassmer", model: "WA-51 Pacific", variant: "", category: "fixedWing" },
    WA80: { manufacturer: "Wassmer", model: "WA-80 Piranha", variant: "", category: "fixedWing" },
    WAC9: { manufacturer: "Waco", model: "9", variant: "", category: "fixedWing" },
    WACA: { manufacturer: "Waco", model: "IBA", variant: "", category: "fixedWing" },
    WACC: { manufacturer: "Waco", model: "AGC", variant: "", category: "fixedWing" },
    WACD: { manufacturer: "Waco", model: "D", variant: "", category: "fixedWing" },
    WACE: { manufacturer: "Waco", model: "ARE Aristocrat", variant: "", category: "fixedWing" },
    WACF: { manufacturer: "Classic", model: "Waco Classic", variant: "", category: "fixedWing" },
    WACG: { manufacturer: "Waco", model: "CRG", variant: "", category: "fixedWing" },
    WACM: { manufacturer: "Waco", model: "JWM", variant: "", category: "fixedWing" },
    WACN: { manufacturer: "Waco", model: "AVN", variant: "", category: "fixedWing" },
    WACO: { manufacturer: "Waco", model: "10", variant: "", category: "fixedWing" },
    WACT: { manufacturer: "Waco", model: "RPT", variant: "", category: "fixedWing" },
    WAIX: { manufacturer: "Sonex", model: "Waiex", variant: "", category: "fixedWing" },
    WASP: { manufacturer: "Westland", model: "Wasp", variant: "", category: "rotorcraft" },
    WB57: { manufacturer: "Martin", model: "272", variant: "", category: "fixedWing" },
    WBOO: {
        manufacturer: "Dean-Wilson",
        model: "DW-200 Whitney Boomerang",
        variant: "",
        category: "fixedWing",
    },
    WCAT: {
        manufacturer: "General Motors",
        model: "FM Wildcat",
        variant: "",
        category: "fixedWing",
    },
    WDEX: { manufacturer: "Radab", model: "Windex", variant: "", category: "fixedWing" },
    WESX: { manufacturer: "Westland", model: "WS-58 Wessex", variant: "", category: "rotorcraft" },
    WF4U: { manufacturer: "War", model: "Vought F4U Corsair", variant: "", category: "fixedWing" },
    WFOC: { manufacturer: "War", model: "Focke-Wulf 190", variant: "", category: "fixedWing" },
    WFUR: { manufacturer: "War", model: "Hawker Sea Fury", variant: "", category: "fixedWing" },
    WH1: { manufacturer: "Wendt", model: "WH-1 Traveler", variant: "", category: "fixedWing" },
    WH4: { manufacturer: "Bayles", model: "Lightning", variant: "", category: "fixedWing" },
    WHAT: { manufacturer: "Whatley", model: "Special", variant: "", category: "fixedWing" },
    WHIL: {
        manufacturer: "Reflex",
        model: "WLAC-1 White Lightning",
        variant: "",
        category: "fixedWing",
    },
    WHIS: { manufacturer: "Grove", model: "GR-2 Whisper", variant: "", category: "fixedWing" },
    WHIT: {
        manufacturer: "Miles",
        model: "M-11 Whitney Straight",
        variant: "",
        category: "fixedWing",
    },
    WHK2: {
        manufacturer: "Rutan",
        model: "348 White Knight Two",
        variant: "",
        category: "fixedWing",
    },
    WICH: { manufacturer: "Javelin", model: "Wichawk", variant: "", category: "fixedWing" },
    WILT: {
        manufacturer: "Aerostar (2)",
        model: "WT-01 Wild Thing",
        variant: "",
        category: "fixedWing",
    },
    WIND: { manufacturer: "Watson", model: "GW-1 Windwagon", variant: "", category: "fixedWing" },
    WINE: { manufacturer: "Windecker", model: "Eagle", variant: "", category: "fixedWing" },
    WIRR: {
        manufacturer: "Commonwealth (1)",
        model: "CA-16 Wirraway",
        variant: "",
        category: "fixedWing",
    },
    WISP: { manufacturer: "Whisper", model: "Whisper", variant: "", category: "fixedWing" },
    WLBY: { manufacturer: "Fly Synthesis", model: "Wallaby", variant: "", category: "fixedWing" },
    WM2: { manufacturer: "Miller (2)", model: "WM-2", variant: "", category: "fixedWing" },
    WOPU: { manufacturer: "Aerosport", model: "Woody Pusher", variant: "", category: "fixedWing" },
    WP40: {
        manufacturer: "War",
        model: "Curtiss P-40 Kittyhawk",
        variant: "",
        category: "fixedWing",
    },
    WP47: {
        manufacturer: "War",
        model: "Republic P-47 Thunderbolt",
        variant: "",
        category: "fixedWing",
    },
    WS22: { manufacturer: "Spring", model: "WS-202 Sprint", variant: "", category: "fixedWing" },
    WSP: { manufacturer: "Aak", model: "Wasp", variant: "", category: "fixedWing" },
    WT10: {
        manufacturer: "Aerospool",
        model: "WT-10 Advantic",
        variant: "",
        category: "fixedWing",
    },
    WT9: { manufacturer: "Aerospool", model: "WT-9 Dynamic", variant: "", category: "fixedWing" },
    WUSH: { manufacturer: "Wüst", model: "Seahawk", variant: "", category: "fixedWing" },
    WW1: { manufacturer: "White", model: "WW-1 Der Jäger D-9", variant: "", category: "fixedWing" },
    WW23: { manufacturer: "Iai", model: "1123 Westwind", variant: "", category: "fixedWing" },
    WW24: { manufacturer: "Iai", model: "1124 Sea Scan", variant: "", category: "fixedWing" },
    WZ10: { manufacturer: "Changhe", model: "WZ-10", variant: "", category: "rotorcraft" },
    WZER: { manufacturer: "War", model: "Mitsubishi Zero", variant: "", category: "fixedWing" },
    X2: { manufacturer: "Sikorsky", model: "X-2", variant: "", category: "rotorcraft" },
    X3: { manufacturer: "Eurocopter", model: "X-3", variant: "", category: "rotorcraft" },
    X47B: { manufacturer: "Northrop Grumman", model: "X-47B", variant: "", category: "fixedWing" },
    X49: { manufacturer: "Piasecki", model: "X-49 SpeedHawk", variant: "", category: "rotorcraft" },
    X59: {
        manufacturer: "Lockheed Martin",
        model: "X-59 QueSST",
        variant: "",
        category: "fixedWing",
    },
    XA41: { manufacturer: "Xtremeair", model: "Sbach 300", variant: "", category: "fixedWing" },
    XA42: { manufacturer: "Reinsdorf", model: "325", variant: "", category: "fixedWing" },
    XA85: {
        manufacturer: "Raj Hamsa",
        model: "X-AIR H Hanuman",
        variant: "",
        category: "fixedWing",
    },
    XAIR: { manufacturer: "Raj Hamsa", model: "X-AIR S", variant: "", category: "fixedWing" },
    XB1: { manufacturer: "Boom", model: "XB-1", variant: "", category: "fixedWing" },
    XL2: { manufacturer: "Discovery", model: "XL-2", variant: "", category: "fixedWing" },
    XNON: { manufacturer: "Abs Aerolight", model: "Xenon", variant: "", category: "rotorcraft" },
    XNOS: { manufacturer: "Sonex", model: "Xenos", variant: "", category: "fixedWing" },
    Y11: { manufacturer: "Harbin", model: "Y-11", variant: "", category: "fixedWing" },
    Y112: { manufacturer: "Yakovlev", model: "Yak-112", variant: "", category: "fixedWing" },
    Y12: { manufacturer: "Harbin", model: "Y-12-2", variant: "", category: "fixedWing" },
    Y12F: { manufacturer: "Harbin", model: "Y-12F Aircar", variant: "", category: "fixedWing" },
    Y130: { manufacturer: "Yakovlev", model: "Yak-130", variant: "", category: "fixedWing" },
    Y18T: { manufacturer: "Technoavia", model: "SM-94", variant: "", category: "fixedWing" },
    Y20: { manufacturer: "Xian", model: "Y-20 Kunpeng", variant: "", category: "fixedWing" },
    YA1: { manufacturer: "Yeoman", model: "YA-1 Cropmaster", variant: "", category: "fixedWing" },
    YAK3: { manufacturer: "Yakovlev", model: "Yak-3", variant: "", category: "fixedWing" },
    YAK9: { manufacturer: "Yakovlev", model: "Yak-9", variant: "", category: "fixedWing" },
    YALE: {
        manufacturer: "North American",
        model: "BT-9 Yale",
        variant: "",
        category: "fixedWing",
    },
    YARR: { manufacturer: "Arrow (2)", model: "Yarrow Arrow", variant: "", category: "fixedWing" },
    YAST: { manufacturer: "Sgau", model: "Yastreb", variant: "", category: "fixedWing" },
    YC12: { manufacturer: "Chasle", model: "YC-12 Tourbillon", variant: "", category: "fixedWing" },
    YK11: { manufacturer: "Let", model: "C-11", variant: "", category: "fixedWing" },
    YK12: { manufacturer: "Pzl-Okecie", model: "Yak-12", variant: "", category: "fixedWing" },
    YK18: { manufacturer: "Nanchang", model: "CJ-5", variant: "", category: "fixedWing" },
    YK28: { manufacturer: "Yakovlev", model: "Yak-28", variant: "", category: "fixedWing" },
    YK30: { manufacturer: "Yakovlev", model: "Yak-30", variant: "", category: "fixedWing" },
    YK40: { manufacturer: "Yakovlev", model: "Yak-40", variant: "", category: "fixedWing" },
    YK42: { manufacturer: "Yakovlev", model: "Yak-42", variant: "", category: "fixedWing" },
    YK50: { manufacturer: "Yakovlev", model: "Yak-50", variant: "", category: "fixedWing" },
    YK52: { manufacturer: "Aerostar (2)", model: "Iak-52", variant: "", category: "fixedWing" },
    YK53: { manufacturer: "Yakovlev", model: "Yak-53", variant: "", category: "fixedWing" },
    YK54: { manufacturer: "Yakovlev", model: "Yak-54", variant: "", category: "fixedWing" },
    YK55: { manufacturer: "Yakovlev", model: "Yak-55", variant: "", category: "fixedWing" },
    YK58: { manufacturer: "Yakovlev", model: "Yak-58", variant: "", category: "fixedWing" },
    YL15: { manufacturer: "Boeing", model: "451 Scout", variant: "", category: "fixedWing" },
    YNHL: {
        manufacturer: "Aviaimpex",
        model: "KT-112 Yanhol",
        variant: "",
        category: "rotorcraft",
    },
    YS11: { manufacturer: "Mitsubishi", model: "YS-11", variant: "", category: "fixedWing" },
    YUKN: { manufacturer: "Murphy", model: "Yukon", variant: "", category: "fixedWing" },
    YUNO: { manufacturer: "Shuya", model: "Yunona", variant: "", category: "fixedWing" },
    YURO: { manufacturer: "Soko-Cniar", model: "J-22 Orao", variant: "", category: "fixedWing" },
    Z22: { manufacturer: "Moravan", model: "Zlin Z-22 Junak", variant: "", category: "fixedWing" },
    Z26: { manufacturer: "Moravan", model: "Zlin C-5 Trener", variant: "", category: "fixedWing" },
    Z37P: { manufacturer: "Let", model: "Z-37 Cmelák", variant: "", category: "fixedWing" },
    Z37T: {
        manufacturer: "Moravan",
        model: "Z-37T Agro Turbo",
        variant: "",
        category: "fixedWing",
    },
    Z42: { manufacturer: "Moravan", model: "Firnas 142", variant: "", category: "fixedWing" },
    Z43: { manufacturer: "Moravan", model: "Safir 43", variant: "", category: "fixedWing" },
    Z50: { manufacturer: "Moravan", model: "Zlin Z-50", variant: "", category: "fixedWing" },
    ZA6: { manufacturer: "Aerokopter", model: "AK-1", variant: "", category: "rotorcraft" },
    ZEFR: { manufacturer: "Curti", model: "Zefhir", variant: "", category: "rotorcraft" },
    ZEP2: { manufacturer: "Aero Adventure", model: "Toucan", variant: "", category: "fixedWing" },
    ZEPH: { manufacturer: "Atec", model: "122 Zephyr", variant: "", category: "fixedWing" },
    ZERO: { manufacturer: "Mitsubishi", model: "A6M Zero", variant: "", category: "fixedWing" },
    ZIU: { manufacturer: "Tai", model: "ZIU", variant: "", category: "fixedWing" },
    ZULU: { manufacturer: "Bul", model: "Zùlù", variant: "", category: "fixedWing" },
}

export const resolveAircraftType = (typeDesignator: string | null): AircraftTypeInfo | null => {
    if (!typeDesignator) return null
    return AIRCRAFT_TYPE_DESIGNATORS[typeDesignator.toUpperCase()] ?? null
}
